import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useClickOutside } from '../utils/useClickOutside';
import { collection, onSnapshot, doc, serverTimestamp, getDoc, getDocs, query, where } from '../firebase.config';
import { enforcedAddDoc as addDoc, enforcedSetDoc as setDoc, enforcedDeleteDoc as deleteDoc } from '../utils/enforcedTracking';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import PurchaseBillTemplate from './BillTemplates/PurchaseBillTemplate';
import PurchaseBillNonGstTemplate from './BillTemplates/PurchaseBillNonGstTemplate';
import PurchaseOrderTemplate from './BillTemplates/PurchaseOrderTemplate';
import PurchaseReturnTemplate from './BillTemplates/PurchaseReturnTemplate';
import DebitNoteTemplate from './BillTemplates/DebitNoteTemplate';
import CreditNoteTemplate from './BillTemplates/CreditNoteTemplate';
import ReceiptTemplate from './BillTemplates/ReceiptTemplate';
import ActionButtons from './ActionButtons';
import imageManager from '../utils/imageManager';
import {
  BILL_PREVIEW_MODAL_MAX_WIDTH,
  BILL_PREVIEW_INNER_CLASS,
  BILL_PREVIEW_SCREEN_CSS,
  BILL_PREVIEW_SHELL_CLASS,
  BILL_PREVIEW_SHELL_STYLE,
  getBillPreviewInitialZoom,
} from './BillTemplates/billPreviewScreenCss';

import { convertImageToDataUrl, preloadBillImages, inlineImagesForPrint } from '../utils/imageUtils';
import { useTableSort, SortableHeader } from '../utils/tableSort';
import { useTablePagination } from '../utils/tablePagination';
import PaginationControls from '../utils/PaginationControls';
import CompactFilterBar from './CompactFilterBar';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';
import { roundTo2Decimals, calculateGSTAmount, calculateLineTotal } from '../utils/numberFormat';
import { formatDateToCompanyFormat } from '../utils/dateUtils';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { getCollectionForCompany, normalizeCompanyDocSnapshot } from '../utils/dataAdapter';
import { useCompanySwitchGuard } from '../hooks/useCompanySwitchGuard';
import {
  getBillLineItems,
  resolveBillLineItemName,
  enrichBillRowForSave,
  findItemInMaster,
  getItemDisplayName,
  matchItemBySearchText,
  filterItemsForPicker,
  resolveRowItemBeforeSave,
  getBillLineItemId,
} from '../utils/itemDisplayUtils';
import WorkOrderLinkSelect from './WorkOrders/WorkOrderLinkSelect';
import {
  appendWorkOrderFields,
  syncLedgerFromSource,
  removeLedgerEntry,
  LEDGER_SOURCE_TYPES,
  isPaymentOutflow,
} from '../utils/workOrderLedger';
import { useCompanyCurrency } from '../hooks/useCompanyCurrency';
import { formatCurrency, getCurrencySymbol } from '../utils/currencyUtils';
import { EmployeeCashManager } from '../utils/employeeCashManager';
import { useNotification } from '../contexts/NotificationContext';
import { Wallet, ChevronDown } from 'lucide-react';
import { 
  getPartyAdvance, 
  allocateAdvanceToBill, 
  markAdvanceUsed,
  applyAdvanceToBills as applyAdvanceToBillsUtil
} from '../utils/advanceUtils';
import { 
  PreviewModal, 
  FormModal,
  StandardModal,
  StandardButton,
  useModalManager,
  ConfirmationModal,
  MODAL_SECTIONS,
} from './Modal';
import ShareButton from './Reports/ShareButton';

// Custom ShareButton component for purchases (only PDF and Image)
const PurchaseShareButton = ({ onExportPDF, onExportImage, disabled = false }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Close dropdown on ESC key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showDropdown) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showDropdown]);

  const handleShareClick = () => {
    console.log('PurchaseShareButton: Share button clicked');
    setShowDropdown(!showDropdown);
  };

  const handleOptionClick = async (action) => {
    console.log('PurchaseShareButton: Option clicked, action:', action);
    if (!action || isProcessing) return;
    
    setShowDropdown(false);
    setIsProcessing(true);
    
    let tempContainer;
    try {
      if (typeof action === 'function') {
        console.log('PurchaseShareButton: Executing action function');
        await action();
      }
    } catch (error) {
      console.error('PurchaseShareButton: Action execution failed:', error);
      // Silent error handling to prevent console spam
    } finally {
      setIsProcessing(false);
    }
  };

  const shareOptions = [
    { 
      label: '📄 Share as PDF', 
      action: onExportPDF,
      disabled: !onExportPDF
    },
    { 
      label: '🖼️ Share as Image', 
      action: onExportImage,
      disabled: !onExportImage
    }
  ].filter(option => !option.disabled);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleShareClick}
        disabled={disabled || isProcessing}
        className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-md transition duration-300 text-sm relative"
      >
        {isProcessing ? '⏳' : '📱'} Share
      </button>
      
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200 sm:w-48 w-40"
          style={{ zIndex: 9999 }}
        >
          {shareOptions.length === 0 ? (
            <div className="p-2 text-gray-500 text-xs">No sharing options available</div>
          ) : (
            shareOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => handleOptionClick(option.action)}
                disabled={option.disabled || isProcessing}
                className="w-full text-left px-3 py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-100 first:rounded-t-md last:rounded-b-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Make imageManager globally available for PDF generation
if (typeof window !== 'undefined') {
  try {
    window.imageManager = imageManager;
    console.log('Purchases: imageManager assigned to window successfully');
  } catch (error) {
    console.error('Purchases: Failed to assign imageManager to window:', error);
    // Silent error handling
  }
}



const initialItemRow = {
  item: '',
  nos: 1,
  length: 0,
  height: 0,
  qty: 0,
  qtyExpr: '',
  qtyDisplay: '',
  lineDiscountType: 'amount',
  lineDiscountValue: 0,
  rate: 0,
  amount: 0,
  sgst: 0,
  cgst: 0,
  igst: 0,
  total: 0,
};



// Utility for rounding to 2 decimal places - now imported from numberFormat.js
// function round2(val) {
//   return Math.round((parseFloat(val) + Number.EPSILON) * 100) / 100;
// }

// Parse a quantity expression like "5x3x2" or "10*2" into a numeric product and a normalized display string
function parseQtyExpression(input) {
  const s = String(input || '').trim().replace(/\s+/g, '');
  if (!s) return { ok: false, value: 0, display: '' };
  const parts = s.split(/[xX\*]/).filter(Boolean);
  if (parts.length === 0) return { ok: false, value: 0, display: '' };
  let product = 1;
  for (const p of parts) {
    const n = Number(p);
    if (!isFinite(n) || n <= 0) return { ok: false, value: 0, display: '' };
    product *= n;
  }
  return { ok: true, value: product, display: parts.join('×') };
}

// Collapsible Purchase Card Component for Mobile View
const PurchaseCard = ({ bill, docType, docTypeOptions, companyCurrency, onView, onEdit, onDelete, onPrint, onPayment, onReceipts, receiptsCount, receiptNumbers, items = [], index = 0, isExpanded, onToggle, currentCompany, employees = [], onQuickEmployeeCashFlow }) => {
    const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
    const [selectedPaymentMode, setSelectedPaymentMode] = useState('cash'); // 'cash' or 'upi'
    const [customPaymentAmount, setCustomPaymentAmount] = useState('');
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const employeeDropdownRef = useRef(null);
    const walletButtonRef = useRef(null);
    
    // Close dropdown when clicking outside (excluding wallet button)
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                showEmployeeDropdown &&
                employeeDropdownRef.current &&
                !employeeDropdownRef.current.contains(event.target) &&
                walletButtonRef.current &&
                !walletButtonRef.current.contains(event.target)
            ) {
                setShowEmployeeDropdown(false);
                setSelectedPaymentMode('cash'); // Reset on close
                setCustomPaymentAmount(''); // Reset custom amount
                setSelectedEmployeeId(null); // Reset selected employee
            }
        };

        if (showEmployeeDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showEmployeeDropdown]);
    
    // Format date according to company settings
    const date = formatDateToCompanyFormat(bill.billDate || bill.date, currentCompany);
    
    // Format amount with currency
    const amount = typeof bill.amount === "number"
        ? formatCurrency(bill.amount, companyCurrency)
        : bill.amount;

    // Get bill items for this invoice - check both items and rows arrays
    const billItems = getBillLineItems(bill);

    const getLineItemDisplayName = (line) => resolveBillLineItemName(line, items);

    return (
        <div 
            className="bg-white border border-gray-200 rounded-lg shadow-sm mb-0 transform transition-all duration-300 ease-in-out hover:shadow-md hover:scale-[1.02] overflow-visible"
            style={{
                animationName: 'slideInUp',
                animationDuration: '0.4s',
                animationTimingFunction: 'ease-out',
                animationFillMode: 'forwards',
                animationDelay: `${index * 50}ms`
            }}
        >
            {/* Header Section */}
            <div 
                className="px-3 py-2 sm:p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => onToggle(index)}
            >
                <div className="flex justify-between items-center">
                    <div className="flex-1 min-w-0">
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{bill.number || bill.invoiceNumber}</h4>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">{bill.partyName || 'No Party'}</p>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3 ml-2">
                        <div className="text-right">
                            <div className="text-sm sm:text-lg font-semibold text-blue-600">{amount}</div>
                            <div className="text-xs text-gray-500">{date}</div>
                        </div>
                        <div className="flex flex-col items-center">
                            <span className="px-1 sm:px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 mb-2">
                                {docTypeOptions.find(opt => opt.value === docType)?.label || 'Purchase Bill'}
                            </span>
                    </div>
                </div>
            </div>

                {/* Payment Status Row - Single Row Layout */}
                <div className="mt-2 sm:mt-3 flex justify-between items-center">
                    <div 
                        className="text-xs sm:text-sm font-medium text-green-600 cursor-pointer hover:text-green-800 transition-colors px-1 sm:px-2 py-1 rounded hover:bg-green-50"
                                onClick={(e) => {
                                    e.stopPropagation();
                            onReceipts(bill);
                        }}
                        title="Click to view receipt details"
                    >
                        <span className="hidden sm:inline">Paid: </span>
                        <span className="sm:hidden">💰 </span>
                        {formatCurrency(bill.paid || 0, companyCurrency)}
                    </div>
                    <div className="flex items-center gap-1">
                        <div 
                            className="text-xs sm:text-sm font-medium text-red-600 cursor-pointer hover:text-red-800 transition-colors px-1 sm:px-2 py-1 rounded hover:bg-red-50"
                            onClick={(e) => {
                                e.stopPropagation();
                                onPayment(bill);
                            }}
                            title="Click to add payment"
                        >
                            <span className="hidden sm:inline">Outstanding: </span>
                            <span className="sm:hidden">💳 </span>
                            {formatCurrency(bill.outstanding || 0, companyCurrency)}
                        </div>
                        {bill.outstanding > 0 && employees && employees.length > 0 && (
                            <div className="relative employee-wallet-dropdown-container">
                                <button
                                    ref={walletButtonRef}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (walletButtonRef.current) {
                                            const rect = walletButtonRef.current.getBoundingClientRect();
                                            setDropdownPosition({
                                                top: rect.bottom + window.scrollY + 4,
                                                left: rect.right + window.scrollX - 256 // 256 = w-64 (256px)
                                            });
                                        }
                                        setShowEmployeeDropdown(!showEmployeeDropdown);
                                    }}
                                    className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded transition-colors"
                                    title="Quick record in employee cash flow"
                                >
                                    <Wallet size={14} />
                                </button>
                                {showEmployeeDropdown && createPortal(
                                    <div 
                                        ref={employeeDropdownRef}
                                        className="employee-wallet-dropdown fixed w-72 bg-white border border-gray-200 rounded-lg shadow-2xl max-h-96 overflow-y-auto z-[99999]"
                                        style={{
                                            top: `${dropdownPosition.top}px`,
                                            left: `${dropdownPosition.left}px`
                                        }}
                                    >
                                        <div className="p-3 border-b border-gray-200 bg-blue-50">
                                            <div className="text-xs font-semibold text-gray-700 mb-2">Quick Record Cash Flow</div>
                                            <div className="text-xs text-gray-500 mb-2">Outstanding: {formatCurrency(bill.outstanding || 0, companyCurrency)}</div>
                                            
                                            {/* Custom Amount Input */}
                                            <div className="mb-2">
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Amount</label>
                                                <input
                                                    type="number"
                                                    value={customPaymentAmount}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        setCustomPaymentAmount(e.target.value);
                                                    }}
                                                    placeholder={formatCurrency(bill.outstanding || 0, companyCurrency)}
                                                    min="0"
                                                    step="0.01"
                                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <div className="text-xs text-gray-500 mt-0.5">
                                                    Leave empty to use full outstanding amount
                                                </div>
                                            </div>
                                            
                                            <div className="flex gap-2 mt-2">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedPaymentMode('cash');
                                                    }}
                                                    className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
                                                        selectedPaymentMode === 'cash'
                                                            ? 'bg-blue-600 text-white font-medium'
                                                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    Cash
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedPaymentMode('upi');
                                                    }}
                                                    className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
                                                        selectedPaymentMode === 'upi'
                                                            ? 'bg-blue-600 text-white font-medium'
                                                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    UPI
                                                </button>
                                            </div>
                                        </div>
                                        <div className="py-1 max-h-48 overflow-y-auto">
                                            {employees.filter(emp => emp.status === 'active').map((employee) => {
                                                const balance = employee.cashBalance || { cash: 0, upi: 0, total: 0 };
                                                const availableBalance = selectedPaymentMode === 'cash' ? balance.cash : balance.upi;
                                                const outstandingNum = parseFloat(bill.outstanding || 0);
                                                const customAmountNum = customPaymentAmount.trim() !== '' ? parseFloat(customPaymentAmount) : outstandingNum;
                                                const isSelected = selectedEmployeeId === employee.id;
                                                return (
                                                    <div
                                                        key={employee.id}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedEmployeeId(isSelected ? null : employee.id);
                                                        }}
                                                        className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors text-sm border-b border-gray-100 last:border-b-0 cursor-pointer flex items-center gap-2"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedEmployeeId(e.target.checked ? employee.id : null);
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        />
                                                        <div className="flex-1">
                                                            <div className="font-medium text-gray-900">{employee.name}</div>
                                                            <div className="text-xs text-gray-600">
                                                                {employee.employeeId || 'N/A'} • Cash: ₹{balance.cash?.toLocaleString('en-IN') || 0} | UPI: ₹{balance.upi?.toLocaleString('en-IN') || 0}
                                                            </div>
                                                            {selectedPaymentMode === 'cash' && availableBalance < customAmountNum && (
                                                                <div className="text-xs text-red-600 mt-1">⚠ Insufficient Cash Balance</div>
                                                            )}
                                                            {selectedPaymentMode === 'upi' && availableBalance < customAmountNum && (
                                                                <div className="text-xs text-red-600 mt-1">⚠ Insufficient UPI Balance</div>
                                                            )}
                                                            {selectedPaymentMode === 'cash' && availableBalance >= customAmountNum && (
                                                                <div className="text-xs text-blue-600 mt-1">✓ Will pay from cash (Outflow)</div>
                                                            )}
                                                            {selectedPaymentMode === 'upi' && availableBalance >= customAmountNum && (
                                                                <div className="text-xs text-blue-600 mt-1">✓ Will pay from UPI (Outflow)</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div className="p-3 border-t border-gray-200 bg-gray-50">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (selectedEmployeeId && onQuickEmployeeCashFlow) {
                                                        const selectedEmployee = employees.find(emp => emp.id === selectedEmployeeId);
                                                        if (selectedEmployee) {
                                                            const amountToUse = customPaymentAmount.trim() !== '' ? customPaymentAmount : null;
                                                            onQuickEmployeeCashFlow(bill, selectedEmployee, selectedPaymentMode, amountToUse);
                                                            setShowEmployeeDropdown(false);
                                                            setSelectedPaymentMode('cash'); // Reset
                                                            setCustomPaymentAmount(''); // Reset custom amount
                                                            setSelectedEmployeeId(null); // Reset selected employee
                                                        }
                                                    }
                                                }}
                                                disabled={!selectedEmployeeId}
                                                className={`w-full px-4 py-2 text-sm font-medium rounded transition-colors ${
                                                    selectedEmployeeId
                                                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                }`}
                                            >
                                                Record Transaction
                                            </button>
                                        </div>
                                    </div>,
                                    document.body
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Action Buttons with Dropdown */}
                <div className="mt-2 sm:mt-4 pt-2 sm:pt-3 border-t border-gray-100 relative">
                    <div className="flex justify-between items-center">
                    <ActionButtons
                        enableDropdown={true}
                        billData={{
                            ...bill,
                            partyName: bill.partyName
                        }}
                        items={items}
                        actions={[
                            { type: 'edit', onClick: () => onEdit(bill) },
                            { type: 'delete', onClick: () => onDelete(bill.id) },
                            {
                                type: docType === 'purchaseBill' ? 'purchase' : 'purchaseOrder',
                                onClick: () => onPrint(bill)
                            }
                        ]}
                            className="justify-start"
                        size="md"
                    />
                            <button
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                onToggle(index);
                            }}
                        >
                            <svg 
                                className={`w-5 h-5 transform transition-transform duration-200 ${isExpanded ? '-rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                            </button>
                        </div>
            </div>
            </div>

            {/* Dropdown Content - Items List */}
            {isExpanded && (
                <div className="border-t border-gray-200 bg-blue-50 px-3 py-2 sm:p-4">
                    <div className="mb-3">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Items in this {docType}:</h4>
                        {billItems.length > 0 ? (
                            <div className="space-y-1 max-h-80 overflow-y-auto custom-scrollbar">
                                {billItems.map((item, itemIdx) => (
                                    <div key={itemIdx} className="bg-white p-2 rounded border border-gray-200 hover:bg-gray-50 transition-colors">
                                        {/* Item Name - Prominent on Mobile */}
                                        <div className="mb-1">
                                            <span className="text-sm sm:text-base font-medium text-gray-900 block" title={getLineItemDisplayName(item)}>
                                                {getLineItemDisplayName(item)}
                                            </span>
                                        </div>
                                        
                                        {/* Item Details - Responsive Layout */}
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-600">
                                            <span className="whitespace-nowrap">Qty: {item.qty || item.quantity || item.nos || 0}</span>
                                            <span className="whitespace-nowrap">Rate: {formatCurrency(item.rate || 0, companyCurrency)}</span>
                                            <span className="whitespace-nowrap font-medium text-gray-900">Total: {formatCurrency(item.amount || item.total || 0, companyCurrency)}</span>
                                        </div>
                                        {(item.description || item.desc) && (
                                            <div className="text-xs text-gray-500 mt-1 truncate" title={item.description || item.desc}>
                                                {item.description || item.desc}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500 italic text-center py-4">No items found for this {docType}</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Dropdown Form Component for Desktop Table Rows
const DropdownFormRow = ({ bill, docType, docTypeOptions, companyCurrency, onView, onEdit, onDelete, onPrint, onPayment, onReceipts, receiptsCount, receiptNumbers, items = [], index = 0, isExpanded, onToggle, employees = [], onQuickEmployeeCashFlow }) => {
    const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
    const [selectedPaymentMode, setSelectedPaymentMode] = useState('cash'); // 'cash' or 'upi'
    const [customPaymentAmount, setCustomPaymentAmount] = useState('');
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const employeeDropdownRef = useRef(null);
    const walletButtonRef = useRef(null);
    
    // Close dropdown when clicking outside (excluding wallet button)
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                showEmployeeDropdown &&
                employeeDropdownRef.current &&
                !employeeDropdownRef.current.contains(event.target) &&
                walletButtonRef.current &&
                !walletButtonRef.current.contains(event.target)
            ) {
                setShowEmployeeDropdown(false);
                setSelectedPaymentMode('cash'); // Reset on close
                setCustomPaymentAmount(''); // Reset custom amount
                setSelectedEmployeeId(null); // Reset selected employee
            }
        };

        if (showEmployeeDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showEmployeeDropdown]);
    // Format date as DD/MM/YYYY
    let date = bill.billDate || bill.date || "";
    if (date && date.includes("-")) {
        const [yyyy, mm, dd] = date.split("-");
        date = `${dd}/${mm}/${yyyy}`;
    }
    
    // Format amount with currency
    const amount = typeof bill.amount === "number"
        ? formatCurrency(bill.amount, companyCurrency)
        : bill.amount;

    const paidAmount = formatCurrency(bill.paid || 0, companyCurrency);
    const outstandingAmount = formatCurrency(bill.outstanding || 0, companyCurrency);

    // Get bill items for this bill - check both items and rows arrays
    const billItems = getBillLineItems(bill);

    const getLineItemDisplayName = (line) => resolveBillLineItemName(line, items);

    return (
        <div className="bg-white border-b border-gray-200 rounded-none shadow-none mb-0 transform transition-all duration-150 ease-out overflow-visible">
            {/* Main Row - Always Visible */}
            <div 
                className="py-2.5 px-3 lg:py-3 lg:px-4 cursor-pointer hover:bg-slate-50 transition-colors overflow-visible"
                onClick={() => onToggle(index)}
            >
                <div className="flex items-center justify-between overflow-visible">
                    <div className="flex-1 grid grid-cols-6 gap-3 lg:gap-5 items-center justify-items-center text-center overflow-visible leading-relaxed">
                        <div className="text-sm font-semibold text-slate-800 truncate" title={bill.number || bill.billNumber}>
                            {bill.number || bill.billNumber}
                        </div>
                        <div className="text-sm text-slate-600" title={date}>{date}</div>
                        <div className="text-sm text-slate-700 truncate font-medium" title={bill.partyName}>
                            {bill.partyName}
                        </div>
                        <div className="text-sm font-semibold text-blue-700 bg-blue-50 rounded-md px-2 py-1 w-fit mx-auto" title={amount}>{amount}</div>
                        <div 
                            className="text-sm font-semibold text-green-700 cursor-pointer hover:text-green-800 transition-colors hover:bg-green-50 px-2 py-1 rounded-md"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onReceipts(bill);
                                }}
                            title="Click to view receipt details"
                        >
                            {paidAmount}
                        </div>
                        <div className="flex items-center gap-1 overflow-visible">
                            <div 
                                className="text-sm font-semibold text-red-700 cursor-pointer hover:text-red-800 transition-colors hover:bg-red-50 px-2 py-1 rounded-md"
                                onClick={(e) => {
                                    e.stopPropagation();
                                onPayment(bill);
                            }}
                            title="Click to add payment"
                        >
                            {outstandingAmount}
                        </div>
                            {bill.outstanding > 0 && employees && employees.length > 0 && (
                                <div className="relative employee-wallet-dropdown-container">
                                    <button
                                        ref={walletButtonRef}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (walletButtonRef.current) {
                                                const rect = walletButtonRef.current.getBoundingClientRect();
                                                setDropdownPosition({
                                                    top: rect.bottom + window.scrollY + 4,
                                                    left: rect.right + window.scrollX - 256 // 256 = w-64 (256px)
                                                });
                                            }
                                            setShowEmployeeDropdown(!showEmployeeDropdown);
                                        }}
                                        className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded transition-colors"
                                        title="Quick record in employee cash flow"
                                    >
                                        <Wallet size={14} />
                                    </button>
                                    {showEmployeeDropdown && createPortal(
                                        <div 
                                            ref={employeeDropdownRef}
                                            className="employee-wallet-dropdown fixed w-72 bg-white border border-gray-200 rounded-lg shadow-2xl max-h-96 overflow-y-auto z-[99999]"
                                            style={{
                                                top: `${dropdownPosition.top}px`,
                                                left: `${dropdownPosition.left}px`
                                            }}
                                        >
                                            <div className="p-3 border-b border-gray-200 bg-blue-50">
                                                <div className="text-xs font-semibold text-gray-700 mb-2">Quick Record Cash Flow</div>
                                                <div className="text-xs text-gray-500 mb-2">Outstanding: {outstandingAmount}</div>
                                                
                                                {/* Custom Amount Input */}
                                                <div className="mb-2">
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Payment Amount</label>
                                                    <input
                                                        type="number"
                                                        value={customPaymentAmount}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            setCustomPaymentAmount(e.target.value);
                                                        }}
                                                        placeholder={outstandingAmount}
                                                        min="0"
                                                        step="0.01"
                                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <div className="text-xs text-gray-500 mt-0.5">
                                                        Leave empty to use full outstanding amount
                                                    </div>
                                                </div>
                                                
                                                <div className="flex gap-2 mt-2">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedPaymentMode('cash');
                                                        }}
                                                        className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
                                                            selectedPaymentMode === 'cash'
                                                                ? 'bg-blue-600 text-white font-medium'
                                                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        Cash
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedPaymentMode('upi');
                                                        }}
                                                        className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
                                                            selectedPaymentMode === 'upi'
                                                                ? 'bg-blue-600 text-white font-medium'
                                                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        UPI
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="py-1 max-h-48 overflow-y-auto">
                                                {employees.filter(emp => emp.status === 'active').map((employee) => {
                                                    const balance = employee.cashBalance || { cash: 0, upi: 0, total: 0 };
                                                    const availableBalance = selectedPaymentMode === 'cash' ? balance.cash : balance.upi;
                                                    const outstandingNum = parseFloat(bill.outstanding || 0);
                                                    const customAmountNum = customPaymentAmount.trim() !== '' ? parseFloat(customPaymentAmount) : outstandingNum;
                                                    const isSelected = selectedEmployeeId === employee.id;
                                                    return (
                                                        <div
                                                            key={employee.id}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedEmployeeId(isSelected ? null : employee.id);
                                                            }}
                                                            className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors text-sm border-b border-gray-100 last:border-b-0 cursor-pointer flex items-center gap-2"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedEmployeeId(e.target.checked ? employee.id : null);
                                                                }}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                            />
                                                            <div className="flex-1">
                                                                <div className="font-medium text-gray-900">{employee.name}</div>
                                                                <div className="text-xs text-gray-600">
                                                                    {employee.employeeId || 'N/A'} • Cash: ₹{balance.cash?.toLocaleString('en-IN') || 0} | UPI: ₹{balance.upi?.toLocaleString('en-IN') || 0}
                                                                </div>
                                                                {selectedPaymentMode === 'cash' && availableBalance < customAmountNum && (
                                                                    <div className="text-xs text-red-600 mt-1">⚠ Insufficient Cash Balance</div>
                                                                )}
                                                                {selectedPaymentMode === 'upi' && availableBalance < customAmountNum && (
                                                                    <div className="text-xs text-red-600 mt-1">⚠ Insufficient UPI Balance</div>
                                                                )}
                                                                {selectedPaymentMode === 'cash' && availableBalance >= customAmountNum && (
                                                                    <div className="text-xs text-blue-600 mt-1">✓ Will pay from cash (Outflow)</div>
                                                                )}
                                                                {selectedPaymentMode === 'upi' && availableBalance >= customAmountNum && (
                                                                    <div className="text-xs text-blue-600 mt-1">✓ Will pay from UPI (Outflow)</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className="p-3 border-t border-gray-200 bg-gray-50">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (selectedEmployeeId && onQuickEmployeeCashFlow) {
                                                            const selectedEmployee = employees.find(emp => emp.id === selectedEmployeeId);
                                                            if (selectedEmployee) {
                                                                const amountToUse = customPaymentAmount.trim() !== '' ? customPaymentAmount : null;
                                                                onQuickEmployeeCashFlow(bill, selectedEmployee, selectedPaymentMode, amountToUse);
                                                                setShowEmployeeDropdown(false);
                                                                setSelectedPaymentMode('cash'); // Reset
                                                                setCustomPaymentAmount(''); // Reset custom amount
                                                                setSelectedEmployeeId(null); // Reset selected employee
                                                            }
                                                        }
                                                    }}
                                                    disabled={!selectedEmployeeId}
                                                    className={`w-full px-4 py-2 text-sm font-medium rounded transition-colors ${
                                                        selectedEmployeeId
                                                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    }`}
                                                >
                                                    Record Transaction
                                                </button>
                                            </div>
                                        </div>,
                                        document.body
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-2 lg:ml-4">
                        <ActionButtons
                            enableDropdown={true}
                            billData={{
                                ...bill,
                                partyName: bill.partyName
                            }}
                            items={items}
                            actions={[
                                { type: 'edit', onClick: () => onEdit(bill) },
                                { type: 'delete', onClick: () => onDelete(bill.id) },
                                {
                                    type: docType === 'purchaseBill' ? 'purchaseBill' : 'purchaseOrder',
                                    onClick: () => onPrint(bill)
                                }
                            ]}
                            size="sm"
                        />
                            <button
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                onToggle(index);
                            }}
                        >
                            <svg 
                                className={`w-5 h-5 transform transition-transform duration-200 ${isExpanded ? '-rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                            </button>
                        </div>
                </div>
            </div>

            {/* Dropdown Content - Items List */}
            {isExpanded && (
                <div className="border-t border-gray-200 bg-blue-50 p-4">
                    <div className="mb-3">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Items in this {docType}:</h4>
                        {billItems.length > 0 ? (
                            <div className="space-y-1 max-h-64 overflow-y-auto custom-scrollbar">
                                {billItems.map((item, itemIdx) => (
                                    <div key={itemIdx} className="bg-white p-2 rounded border border-gray-200 hover:bg-gray-50 transition-colors">
                                        <div className="flex items-center justify-between text-sm">
                                            <div className="flex-1 min-w-0">
                                                <span className="font-medium text-gray-900 truncate" title={getLineItemDisplayName(item)}>
                                                    {getLineItemDisplayName(item)}
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-4 text-gray-600">
                                                <span className="text-xs">Qty: {item.qty || item.quantity || item.nos || 0}</span>
                                                <span className="text-xs">Rate: {formatCurrency(item.rate || 0, companyCurrency)}</span>
                                                <span className="text-xs font-medium text-gray-900">Total: {formatCurrency(item.amount || item.total || 0, companyCurrency)}</span>
                                            </div>
                                        </div>
                                        {(item.description || item.desc) && (
                                            <div className="text-xs text-gray-500 mt-1 truncate" title={item.description || item.desc}>
                                                {item.description || item.desc}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500 italic text-center py-4">No items found for this {docType}</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

  // ESC Key Functionality: Press ESC key to close modals in LIFO order (Last In, First Out)
  // Order: Receipt Modal → Payment Details Modal → Payment Modal → Invoice Modal → View Modal → Success Modal → Image Manager
  function PurchasesDev({ db, userId, isAuthReady, appId }) {
    const { currentCompany, employees } = useEnhancedCompany();
    const companyId = currentCompany?.companyId || currentCompany?.id;
    const { currency: companyCurrency } = useCompanyCurrency();
    const { t } = useLanguage();
    const { paymentSuccess, success, error, warning } = useNotification();
    // Modal manager hook
    const { } = useModalManager();
    
    // URL parameter handling for document type selection
    const location = useLocation();
    

  
  // Document type: purchaseBill, purchaseOrder, purchaseReturn, debitNote, creditNote
  const [docType, setDocType] = useState(() => {
    const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const tabParam = urlParams.get('tab');
    const typeParam = urlParams.get('type');
    if (typeParam && ['purchaseReturn', 'debitNote', 'creditNote'].includes(typeParam)) return typeParam;
    if (tabParam === 'orders') return 'purchaseOrder';
    return 'purchaseBill';
  });
  const docTypeOptions = [
    { value: 'purchaseBill', label: t('purchaseBill'), collection: 'purchaseBills', numberLabel: 'Purchase Bill Number' },
    { value: 'purchaseOrder', label: t('purchaseOrder'), collection: 'purchaseOrders', numberLabel: 'Purchase Order Number' },
    { value: 'purchaseReturn', label: 'PURCHASE RETURN', collection: 'purchaseReturns', numberLabel: 'Return Number' },
    { value: 'debitNote', label: 'DEBIT NOTE', collection: 'debitNotes', numberLabel: 'Debit Note Number' },
    { value: 'creditNote', label: 'CREDIT NOTE', collection: 'creditNotes', numberLabel: 'Credit Note Number' },
  ];

  // Bill state
  const [billNumber, setBillNumber] = useState('');
  const [billDate, setBillDate] = useState(new Date().toISOString().split('T')[0]);
  const [party, setParty] = useState('');
  const [notes, setNotes] = useState('');
  // Fields for returns and notes
  const [originalBillId, setOriginalBillId] = useState('');
  const [originalBillNumber, setOriginalBillNumber] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [rows, setRows] = useState(Array.from({ length: 5 }, () => ({ ...initialItemRow })));
  const [itemSearchTextByRow, setItemSearchTextByRow] = useState({});
  const [customFields, setCustomFields] = useState({ ewayBillNo: '', ewayQr: '', ewayDate: '' });
  const [workOrderLink, setWorkOrderLink] = useState({ workOrderId: '', phaseId: '', costCategory: '' });
  const companyCtx = { companyId, appId, userId };
  const [showEwayBill, setShowEwayBill] = useState(false);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const woId = params.get('workOrderId') || '';
      const phaseId = params.get('phaseId') || '';
      if (woId) setWorkOrderLink((prev) => ({ ...prev, workOrderId: woId, phaseId }));
    } catch (_) { /* ignore */ }
  }, []);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showEwayBillModal, setShowEwayBillModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showDocTypeDropdown, setShowDocTypeDropdown] = useState(false);
  const [showGstInfoDropdown, setShowGstInfoDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDocTypeDropdown && !event.target.closest('.relative')) {
        setShowDocTypeDropdown(false);
      }
      if (showGstInfoDropdown && !event.target.closest('.relative')) {
        setShowGstInfoDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDocTypeDropdown, showGstInfoDropdown]);

  // Data
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [bills, setBills] = useState([]);
  const [company, setCompany] = useState({});
  
  // Ref to track bills for bill number generation without causing re-renders
  const billsRef = useRef([]);
  
  // Quick items functionality
  const [mostUsedItems, setMostUsedItems] = useState([]);
  const [partyWiseItems, setPartyWiseItems] = useState([]);
  const [selectedPartyForQuickItems, setSelectedPartyForQuickItems] = useState('');
  const [showQuickItemsModal, setShowQuickItemsModal] = useState(false);
  const [quickItemModalPosition, setQuickItemModalPosition] = useState({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
  const quickItemButtonRef = useRef(null);
  
  // Mobile edit modal states
  const [showMobileEditModal, setShowMobileEditModal] = useState(false);
  const [partySearchText, setPartySearchText] = useState('');
  const [showPartyDropdown, setShowPartyDropdown] = useState(false);
  
  // Filtered parties for mobile search
  const filteredParties = parties.filter(p => 
    p.firmName.toLowerCase().includes(partySearchText.toLowerCase())
  );
  
  // Close party dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPartyDropdown && !event.target.closest('.party-dropdown-container')) {
        setShowPartyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPartyDropdown]);
  
  // Mobile drawer functionality
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  // Dropdown form state for expanded rows
  const [expandedRows, setExpandedRows] = useState({});

  // Toggle row expansion
  const toggleRowExpansion = (index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Filter states
  const [showFilters, setShowFilters] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('purchases_showFilters');
      return saved !== null ? JSON.parse(saved) : true;
    }
    return true;
  });
  const [filterSearch, setFilterSearch] = useState(''); // Combined search for bill number and party name
  const [filterDate, setFilterDate] = useState('');
  const [filterAmount, setFilterAmount] = useState('');
  const [filterBillNo, setFilterBillNo] = useState('');
  const [filterParty, setFilterParty] = useState('');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');

  // UI
    const [editingBillId, setEditingBillId] = useState(null);
  const [isSavingBill, setIsSavingBill] = useState(false);
  const isSavingBillRef = useRef(false);
  const [message, setMessage] = useState('');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceBill, setInvoiceBill] = useState(null);
  const [invoiceZoom, setInvoiceZoom] = useState(1);
  const invoiceRef = useRef();
  const tableRef = useRef();
  const [viewBill, setViewBill] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [savedBillId, setSavedBillId] = useState(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  
  // ADD ITEM Modal states
  const [newItemName, setNewItemName] = useState('');
  const [newItemMeasurement, setNewItemMeasurement] = useState('');
  const [newItemRate, setNewItemRate] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemHsnCode, setNewItemHsnCode] = useState('');
  const [newItemGstPercentage, setNewItemGstPercentage] = useState('0');
  const [newItemPurchasePrice, setNewItemPurchasePrice] = useState('0');
  const [newItemSalePrice, setNewItemSalePrice] = useState('0');
  const [newItemCurrentStock, setNewItemCurrentStock] = useState('0');
  const [newItemIsActive, setNewItemIsActive] = useState(true);
  const [newItemCustomUnit, setNewItemCustomUnit] = useState('');
  const [addItemMessage, setAddItemMessage] = useState('');
  
  // ADD PARTY Modal states
  const [showAddPartyModal, setShowAddPartyModal] = useState(false);
  const [newPartyName, setNewPartyName] = useState('');
  const [newPartyGstin, setNewPartyGstin] = useState('');
  const [newPartyAddress, setNewPartyAddress] = useState('');
  const [newPartyPhone, setNewPartyPhone] = useState('');
  const [newPartyEmail, setNewPartyEmail] = useState('');
  const [newPartyGstType, setNewPartyGstType] = useState('Unregistered');
  const [addPartyMessage, setAddPartyMessage] = useState('');
  
  // Additional party fields
  const [newPartyOpeningBalance, setNewPartyOpeningBalance] = useState('');
  const [newPartyCreditLimit, setNewPartyCreditLimit] = useState('');
  const [newPartyCreditPeriod, setNewPartyCreditPeriod] = useState('');
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);
  
  // Mobile view carousel state
  const [currentMobileItemIndex, setCurrentMobileItemIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [mobileItemPickerOpen, setMobileItemPickerOpen] = useState(false);
  const mobileItemPickerRef = useRef(null);

  // Keep docType and edit/view ids in URL for deep links
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const desiredTab = docType === 'purchaseOrder' ? 'orders' : 'bills';
    if (params.get('tab') !== desiredTab) params.set('tab', desiredTab);
    if (editingBillId) params.set('edit', editingBillId); else params.delete('edit');
    const base = window.location.hash.split('?')[0] || '#/purchases';
    const next = `${base}?${params.toString()}`;
    if (window.location.hash !== next) window.location.hash = next;
  }, [docType, editingBillId]);

  // On first load, if view/edit present open it
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const viewId = params.get('view');
    const editId = params.get('edit');
    if (viewId) {
      const collectionName = docType === 'purchaseOrder' ? 'purchaseOrders' : 'purchaseBills';
      getDoc(doc(db, 'companies', companyId, collectionName, viewId)).then(snap => {
        if (snap.exists()) { setViewBill({ id: snap.id, ...snap.data() }); setShowViewModal(true); }
      }).catch(() => {});
    }
    if (editId) setEditingBillId(editId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Payment Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [payments, setPayments] = useState([]);
  const [selectedBillForPayment, setSelectedBillForPayment] = useState(null);
  
  // Employee cash flow integration state
  const [recordInEmployeeCashFlow, setRecordInEmployeeCashFlow] = useState(false);
  const [selectedEmployeeForCashFlow, setSelectedEmployeeForCashFlow] = useState('');
  
  // FIFO Payment Employee Cash Flow state
  const [fifoRecordInEmployeeCashFlow, setFifoRecordInEmployeeCashFlow] = useState(false);
  const [fifoSelectedEmployeeForCashFlow, setFifoSelectedEmployeeForCashFlow] = useState('');
  
  // FIFO Payment Modal states
  const [showFIFOPaymentModal, setShowFIFOPaymentModal] = useState(false);
  const [selectedPartyForFIFO, setSelectedPartyForFIFO] = useState('');
  const [fifoPaymentAmount, setFifoPaymentAmount] = useState('');
  const [fifoPaymentDate, setFifoPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [fifoPaymentMode, setFifoPaymentMode] = useState('Cash');
  const [fifoPaymentReference, setFifoPaymentReference] = useState('');
  const [fifoPaymentNotes, setFifoPaymentNotes] = useState('');
  const [fifoReceiptNumber, setFifoReceiptNumber] = useState('');

  // Payment Receipt Preview states
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedPaymentForReceipt, setSelectedPaymentForReceipt] = useState(null);
  const receiptRef = useRef();
  
  // Payment Details Modal states (for multiple receipts)
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [selectedBillForReceipts, setSelectedBillForReceipts] = useState(null);
  
  // Click outside refs for modals
  const successModalRef = useClickOutside(() => setShowSuccessModal(false), showSuccessModal);
  const paymentModalRef = useClickOutside(() => setShowPaymentModal(false), showPaymentModal);
  const addItemModalRef = useClickOutside(() => setShowAddItemModal(false), showAddItemModal);

  // Table sorting hook — key must match row field `number` and SortableHeader columnKey (not `billNumber`)
  const { sortConfig, handleSort, getSortedData } = useTableSort([], { key: 'number', direction: 'desc' });
  

  


  // ESC Key Functionality: Press ESC key to close modals in LIFO order (Last In, First Out)
  // Order: Add Party Modal → Add Item Modal → Receipt Modal → Payment Details Modal → Payment Modal → Invoice Modal → View Modal → Success Modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        if (showAddPartyModal) {
          setShowAddPartyModal(false);
          clearAddPartyForm();
        } else if (showAddItemModal) {
          setShowAddItemModal(false);
        } else if (showReceiptModal) {
          setShowReceiptModal(false);
        } else if (showPaymentDetailsModal) {
          setShowPaymentDetailsModal(false);
        } else if (showPaymentModal) {
          setShowPaymentModal(false);
          setSelectedBillForPayment(null);
        } else if (showFIFOPaymentModal) {
          setShowFIFOPaymentModal(false);
          setSelectedPartyForFIFO('');
          setFifoPaymentAmount('');
        } else if (showInvoiceModal) {
          setShowInvoiceModal(false);
        } else if (showViewModal) {
          setShowViewModal(false);
        } else if (showSuccessModal) {
          setShowSuccessModal(false);
        }
      }
    };
    
    // Add event listener if any modal is open
    if (showAddPartyModal || showAddItemModal || showReceiptModal || showPaymentDetailsModal || showPaymentModal || showInvoiceModal || showViewModal || showSuccessModal) {
      document.addEventListener('keydown', handleEscKey);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showAddPartyModal, showAddItemModal, showReceiptModal, showPaymentDetailsModal, showPaymentModal, showFIFOPaymentModal, showInvoiceModal, showViewModal, showSuccessModal]);

  // Define handleViewBill
  const handleViewBill = (bill) => {
    setViewBill(bill);
    setShowViewModal(true);
  };

  // Fetch parties (sellers)
    useEffect(() => {
        if (db && userId && isAuthReady && companyId) {
      // Path updated to use getCollectionForCompany
      const partiesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
      const unsubscribe = onSnapshot(
        partiesCollectionRef,
        (snapshot) => {
          const allParties = [];
                snapshot.forEach((doc) => {
            allParties.push({ id: doc.id, ...doc.data() });
          });
          allParties.sort((a, b) => (a.firmName || '').localeCompare(b.firmName || ''));
          setParties(allParties);
        }, (error) => {
          console.error('Error fetching parties:', error);
          setParties([]);
        }
      );
      return () => unsubscribe();
        }
    }, [db, userId, isAuthReady, appId, companyId]);

  // Fetch items
    useEffect(() => {
        if (db && userId && isAuthReady && companyId) {
      const itemsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'items');
      const unsubscribe = onSnapshot(itemsCollectionRef, (snapshot) => {
        const itemsArr = [];
                snapshot.forEach((doc) => {
          itemsArr.push({ id: doc.id, ...doc.data() });
        });
        itemsArr.sort((a, b) => (a.itemName || '').localeCompare(b.itemName || ''));
        setItems(itemsArr);
      }, (error) => {
        console.error('Error fetching items:', error);
        // Keep last known items; wiping to [] causes silent Unnamed Item saves
      });
            return () => unsubscribe();
        }
    }, [db, userId, isAuthReady, appId, companyId]);

  // Fetch company details
  useEffect(() => {
    if (db && isAuthReady && companyId) {
      const unsubscribe = onSnapshot(doc(db, 'companies', companyId), (docSnap) => {
        if (docSnap.exists()) {
          setCompany(normalizeCompanyDocSnapshot(docSnap.data() || {}, companyId));
        }
      }, (error) => {
        console.error('Error fetching company details:', error);
        setCompany({});
      });
      return () => unsubscribe();
    }
  }, [db, isAuthReady, companyId]);

  // Fetch payments
  useEffect(() => {
    if (db && userId && isAuthReady && companyId) {
      const paymentsQuery = query(getCollectionForCompany(db, { companyId, appId, userId }, 'payments'));
      const unsubscribe = onSnapshot(paymentsQuery, (snapshot) => {
        const paymentsData = [];
        snapshot.forEach((doc) => {
          paymentsData.push({ id: doc.id, ...doc.data() });
        });
        setPayments(paymentsData);
      });
      return () => unsubscribe();
    }
  }, [db, userId, isAuthReady, appId, companyId]);
  


  // Generate receipt number for purchase payments
  const generateReceiptNumber = async () => {
    const currentYear = new Date().getFullYear();
    const financialYear = `${currentYear.toString().slice(-2)}-${(currentYear + 1).toString().slice(-2)}`;
    const paymentType = 'P'; // Purchase payments
    
    try {
      const paymentsQuery = query(getCollectionForCompany(db, { companyId, appId, userId }, 'payments'));
      const paymentsSnapshot = await getDocs(paymentsQuery);
      
      let highestSequence = 0;
      
      paymentsSnapshot.forEach((doc) => {
        const payment = doc.data();
        if (payment.receiptNumber) {
          const regexPattern = new RegExp(`PR${paymentType}${financialYear.replace('-', '\\-')}\\/(\\d+)`);
          const match = payment.receiptNumber.match(regexPattern);
          
          if (match) {
            const sequence = parseInt(match[1]);
            if (sequence > highestSequence) {
              highestSequence = sequence;
            }
          }
        }
      });
      
      const nextSequence = highestSequence + 1;
      return `PR${paymentType}${financialYear}/${nextSequence.toString().padStart(3, '0')}`;
    } catch (error) {
      console.error('Error generating receipt number:', error);
      return `PR${paymentType}${financialYear}/001`;
    }
  };

  // Get party name by ID
  const getPartyName = (partyId) => {
    const party = parties.find(p => p.id === partyId);
    return party ? (party.firmName || party.name) : 'Unknown Party';
  };

  // Get party GST type by ID
  const getPartyGstType = (partyId) => {
    const party = parties.find(p => p.id === partyId);
    return party ? (party.gstType || 'Unregistered') : 'Unregistered';
  };

  // Calculate outstanding amount for a bill
  const getBillOutstanding = (bill) => {
    const billPayments = payments.filter(p => 
      p.allocations && p.allocations.some(a => a.billId === bill.id && a.billType === 'purchase')
    );
    
    const totalPaid = billPayments.reduce((sum, payment) => {
      const allocation = payment.allocations.find(a => a.billId === bill.id && a.billType === 'purchase');
      return sum + (allocation ? allocation.allocatedAmount : 0);
    }, 0);
    
    const totalAmount = parseFloat(bill.totalAmount || bill.amount) || 0;
    return totalAmount - totalPaid;
  };

  // Filter functions
  const clearFilters = () => {
    setFilterSearch('');
    setFilterDate('');
    setFilterAmount('');
    setFilterBillNo('');
    setFilterParty('');
    setFilterFromDate('');
    setFilterToDate('');
  };

  // Handle filter changes from CompactFilterBar
  const handleFilterChange = (filters) => {
    setFilterSearch(filters.search || '');
    setFilterDate(filters.date || '');
    setFilterAmount(filters.amount || '');
    setFilterBillNo(filters.billNo || '');
    setFilterParty(filters.party || '');
    setFilterFromDate(filters.fromDate || '');
    setFilterToDate(filters.toDate || '');
  };

  const getFilteredBills = (billsList) => {
    const parseBillDate = (value) => {
      if (!value) return null;
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    return billsList.filter(bill => {
      const billNumber = String(bill.number || bill.billNumber || '').trim();
      const partyName = getPartyName(bill.party);
      const billDateObj = parseBillDate(bill.billDate || bill.invoiceDate || bill.date);

      // Combined search for bill number and party name
      if (filterSearch) {
        const searchTerms = filterSearch.toLowerCase().trim().split(/\s+/);
        const billNumberLower = billNumber.toLowerCase();
        const partyNameLower = partyName.toLowerCase();
        
        // Check if all search terms match either bill number or party name
        if (!searchTerms.every(term => 
          billNumberLower.includes(term) || partyNameLower.includes(term)
        )) {
          return false;
        }
      }

      if (filterBillNo && !billNumber.toLowerCase().includes(filterBillNo.toLowerCase())) {
        return false;
      }

      if (filterParty && !partyName.toLowerCase().includes(filterParty.toLowerCase())) {
        return false;
      }
      
      // Filter by date
      if (filterDate) {
        const billDate = bill.billDate || bill.invoiceDate || '';
        if (!billDate.toLowerCase().includes(filterDate.toLowerCase())) {
          return false;
        }
      }
      
      // Filter by amount
      if (filterAmount) {
        const totalAmount = parseFloat(bill.totalAmount || bill.amount) || 0;
        const amountStr = totalAmount.toString();
        if (!amountStr.includes(filterAmount)) {
          return false;
        }
      }

      if (filterFromDate && !(billDateObj && billDateObj >= new Date(`${filterFromDate}T00:00:00`))) {
        return false;
      }

      if (filterToDate && !(billDateObj && billDateObj <= new Date(`${filterToDate}T23:59:59`))) {
        return false;
      }
      
      return true;
    });
  };

  // Table pagination hook - placed after function definitions
  const filteredBills = getFilteredBills(bills);
  const sortedBills = getSortedData(filteredBills.map((bill) => {
    const partyName = getPartyName(bill.party);
    const outstanding = getBillOutstanding(bill);
    const totalAmount = parseFloat(bill.totalAmount || bill.amount) || 0;
    const paid = totalAmount - outstanding;
    return {
      ...bill,
      number: bill.number || bill.billNumber || '',
      partyName,
      paid: Math.max(0, paid),
      outstanding: Math.max(0, outstanding)
    };
  }));
  const pagination = useTablePagination(sortedBills, 10);

  // Fetch most used items for quick access
  useEffect(() => {
    if (db && userId && isAuthReady && bills.length > 0) {
      // Aggregate item usage from purchase bills
      const itemUsage = {};
      
      bills.forEach(bill => {
        // Check both bill.items and bill.rows for item data
        const billItems = getBillLineItems(bill);
        if (Array.isArray(billItems)) {
          billItems.forEach(item => {
            if (item.item && (item.qty || item.nos) && item.rate) {
              if (!itemUsage[item.item]) {
                itemUsage[item.item] = {
                  itemId: item.item, // Use itemId for consistency
                  id: item.item,
                  count: 0,
                  lastUsed: null,
                  lastRate: 0,
                  lastQty: 0
                };
              }
              
              itemUsage[item.item].count += 1;
              const billDate = bill.billDate || bill.date ? new Date(bill.billDate || bill.date) : new Date();
              
              if (!itemUsage[item.item].lastUsed || billDate > itemUsage[item.item].lastUsed) {
                itemUsage[item.item].lastUsed = billDate;
                itemUsage[item.item].lastRate = parseFloat(item.rate) || 0;
                itemUsage[item.item].lastQty = parseFloat(item.qty || item.nos) || 0;
              }
            }
          });
        }
      });
      
      // Convert to array and sort by usage count, then by last used date
      const sortedItems = Object.values(itemUsage)
        .sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return new Date(b.lastUsed) - new Date(a.lastUsed);
        })
        .slice(0, 20); // Top 20 most used items
      
      // Add item names to the quick items data
      const itemsWithNames = sortedItems.map(item => {
        const itemDetails = items.find(it => it.id === item.itemId);
        return {
          ...item,
          itemName: itemDetails ? (itemDetails.itemName || itemDetails.name || 'Unknown Item') : 'Unknown Item',
          name: itemDetails ? (itemDetails.itemName || itemDetails.name || 'Unknown Item') : 'Unknown Item'
        };
      });
      
      setMostUsedItems(itemsWithNames);
    } else {
      setMostUsedItems([]);
    }
  }, [db, userId, isAuthReady, bills, items]);

  // Fetch party-wise items for quick access
  useEffect(() => {
    if (db && userId && isAuthReady && selectedPartyForQuickItems && bills.length > 0) {
      // Filter bills by selected party
      const partyBills = bills.filter(bill => bill.party === selectedPartyForQuickItems);
      
      // Aggregate item usage from party-specific bills
      const itemUsage = {};
      
      partyBills.forEach(bill => {
        // Check both bill.items and bill.rows for item data
        const billItems = getBillLineItems(bill);
        if (Array.isArray(billItems)) {
          billItems.forEach(item => {
            if (item.item && (item.qty || item.nos) && item.rate) {
              if (!itemUsage[item.item]) {
                itemUsage[item.item] = {
                  itemId: item.item, // Use itemId for consistency
                  id: item.item,
                  count: 0,
                  lastUsed: null,
                  lastRate: 0,
                  lastQty: 0
                };
              }
              
              itemUsage[item.item].count += 1;
              const billDate = bill.billDate || bill.date ? new Date(bill.billDate || bill.date) : new Date();
              
              if (!itemUsage[item.item].lastUsed || billDate > itemUsage[item.item].lastUsed) {
                itemUsage[item.item].lastUsed = billDate;
                itemUsage[item.item].lastRate = parseFloat(item.rate) || 0;
                itemUsage[item.item].lastQty = parseFloat(item.qty || item.nos) || 0;
              }
            }
          });
        }
      });
      
      // Convert to array and sort by usage count, then by last used date
      const sortedItems = Object.values(itemUsage)
        .sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return new Date(b.lastUsed) - new Date(a.lastUsed);
        })
        .slice(0, 20); // Top 20 party-specific items
      
      // Add item names to the quick items data
      const itemsWithNames = sortedItems.map(item => {
        const itemDetails = items.find(it => it.id === item.itemId);
        return {
          ...item,
          itemName: itemDetails ? (itemDetails.itemName || itemDetails.name || 'Unknown Item') : 'Unknown Item',
          name: itemDetails ? (itemDetails.itemName || itemDetails.name || 'Unknown Item') : 'Unknown Item'
        };
      });
      
      setPartyWiseItems(itemsWithNames);
    } else {
      setPartyWiseItems([]);
    }
  }, [db, userId, isAuthReady, selectedPartyForQuickItems, items]);

  // Update party search text when party changes
  useEffect(() => {
    if (party) {
      const selectedParty = parties.find(p => p.id === party);
      if (selectedParty) {
        setPartySearchText(selectedParty.firmName);
      }
    } else {
      setPartySearchText('');
    }
  }, [party, parties]);

  // Fetch bills for the selected type with auto-refresh
  // This useEffect automatically refreshes data when bills are saved, edited, or deleted
  // No manual refresh button needed - data updates in real-time
  useEffect(() => {
    if (!db || !userId || !isAuthReady || !appId) return;
    const selected = docTypeOptions.find(opt => opt.value === docType);
    if (!selected) return;
    if (!companyId) return;
    
    try {
      const collectionRef = getCollectionForCompany(db, { companyId, appId, userId }, docType === 'purchaseBill' ? 'purchaseBills' : 'purchaseOrders');
      const unsubscribe = onSnapshot(collectionRef, (snapshot) => {
        const arr = [];
        snapshot.forEach((doc) => {
          arr.push({ id: doc.id, ...doc.data() });
        });
        // LIFO sorting is now handled by the pagination utility
        setBills(arr);
        billsRef.current = arr; // Update ref to avoid infinite loops
      }, (error) => {
        console.error('Error fetching bills:', error);
        setBills([]);
      });
      return () => unsubscribe();
    } catch (err) {
      console.error('Firestore listener setup failed:', err);
      setBills([]);
    }
  }, [db, userId, isAuthReady, appId, companyId, docType]);

  // Save showFilters state to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('purchases_showFilters', JSON.stringify(showFilters));
    }
  }, [showFilters]);

  // Helper function to generate bill number (extracted for reuse in useEffect and handleSaveBill)
  const generateBillNumber = useCallback((currentBills = bills) => {
    if (editingBillId) return billNumber; // Don't regenerate for editing
    const selected = docTypeOptions.find(opt => opt.value === docType);
    if (!selected) return '';
    const prefixMap = { 
      purchaseBill: 'PRB', 
      purchaseOrder: 'PRO',
      purchaseReturn: 'PR',
      debitNote: 'DN',
      creditNote: 'CN'
    };
    const prefix = prefixMap[docType] || 'PRB';
    const fy = getFinancialYear(billDate);
    const fyShort = fy.split('-').map(y => y.slice(-2)).join('-');
    
    // Use provided bills or current bills state to generate number
    const serials = currentBills
      .filter(bill => (bill.number || '').startsWith(prefix + fyShort))
      .map(bill => parseInt((bill.number || '').split('/')[1], 10))
      .filter(n => !isNaN(n));
    const nextSerial = (serials.length ? Math.max(...serials) : 0) + 1;
    return `${prefix}${fyShort}/${nextSerial.toString().padStart(3, '0')}`;
  }, [billDate, docType, editingBillId, bills, billNumber]);

  // Auto-generate number for the selected type
  useEffect(() => {
    if (editingBillId) return;
    const generatedNumber = generateBillNumber();
    if (generatedNumber && generatedNumber !== billNumber) {
      setBillNumber(generatedNumber);
    }
  }, [billDate, docType, editingBillId, bills, generateBillNumber, billNumber]);

  // Helper
  function getFinancialYear(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    if (month >= 4) {
      return `${year}-${(year + 1).toString().slice(-2)}`;
    } else {
      return `${year - 1}-${year.toString().slice(-2)}`;
    }
  }

  // Calculate modal position based on button click
  const calculateModalPosition = (buttonElement) => {
    if (!buttonElement) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
    
    const rect = buttonElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const modalHeight = Math.min(viewportHeight * 0.85, 600); // Estimated modal height
    const modalWidth = Math.min(viewportWidth - 32, 672); // max-w-2xl = 672px, with 16px margin on each side
    
    let top = rect.bottom + 8; // 8px below the button
    let left = rect.left;
    
    // Adjust if modal would go below viewport
    if (top + modalHeight > viewportHeight - 16) {
      top = Math.max(16, rect.top - modalHeight - 8); // 8px above button, min 16px from top
    }
    
    // Adjust if modal would go beyond right edge
    if (left + modalWidth > viewportWidth - 16) {
      left = viewportWidth - modalWidth - 16;
    }
    
    // Adjust if modal would go beyond left edge
    if (left < 16) {
      left = 16;
    }
    
    return {
      top: `${top}px`,
      left: `${left}px`,
      transform: 'none'
    };
  };

  // Quick items helper functions
  const handleQuickItemClick = (itemData, isPartySpecific = false) => {
    // Use itemId consistently - check both itemId and id fields
    const targetItemId = itemData.itemId || itemData.id;
    if (!targetItemId) {
      return;
    }
    
    // Find the item details from items array
    const itemDetails = items.find(item => item.id === targetItemId);
    if (!itemDetails) {
      return;
    }
    
    // Find an empty row or add a new one
    let targetRowIndex = rows.findIndex(row => !row.item && !row.itemId);
    if (targetRowIndex === -1) {
      // Add new row if all rows are filled
      setRows(prev => [...prev, { ...initialItemRow }]);
      targetRowIndex = rows.length;
    }
    
    // Calculate the amount
    const rate = itemData.lastRate || itemDetails.purchasePrice || itemDetails.rate || 0;
    const qty = itemData.lastQty || 1;
    const amount = rate * qty;
    
    // Update the row with item details using handleRowChange to ensure proper calculations
    // First set the item to trigger GST calculations
    handleRowChange(targetRowIndex, 'item', targetItemId);
    
    // Then set the rate and quantity in sequence to ensure proper calculations
    setTimeout(() => {
      handleRowChange(targetRowIndex, 'rate', rate.toString());
    }, 50);
    
    setTimeout(() => {
      handleRowChange(targetRowIndex, 'qty', qty.toString());
    }, 100);
    
    // Update item search text for this row
    setItemSearchTextByRow(prev => ({
      ...prev,
      [targetRowIndex]: itemDetails.itemName || itemDetails.name || 'Unknown Item'
    }));
  };

  const getResponsiveColumns = () => {
    if (typeof window !== 'undefined') {
      const screenWidth = window.innerWidth;
      if (screenWidth >= 1920) return 3; // Large desktop
      if (screenWidth >= 1366) return 2; // Medium desktop
      return 1; // Small desktop/mobile
    }
    return 1; // Default fallback
  };

  // Mobile helper functions
  const toggleMobileDrawer = () => {
    setIsMobileDrawerOpen(!isMobileDrawerOpen);
  };

  const isMobileView = () => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  };

  // Mobile-specific invoice modal handler
  const handleMobileInvoiceView = (bill) => {
    setInvoiceBill(bill);
    setShowInvoiceModal(true);
    setInvoiceZoom(getBillPreviewInitialZoom());
  };

  // Add/Edit/Delete item rows
  const handleRowChange = (idx, field, value) => {
    setRows(currentRows => {
      const updatedRows = currentRows.map((row, i) => {
        if (i !== idx) return row;
        let updated = { ...row, [field]: value };

        // Always keep gstPercent from item
        if (field === 'item') {
          let itemObj = findItemInMaster(value, items);
          updated.gstPercent = itemObj ? (itemObj.gstPercentage || 0) : 0;
          if (itemObj) {
            updated.itemName = getItemDisplayName(itemObj);
          } else if (!value) {
            updated.itemName = '';
          }
          if (!updated.nos) updated.nos = 1;
          // Prefer last party-wise rate from purchase bills; fallback to item.purchasePrice
          const partyId = party;
          let defaultRate = 0;
          try {
            for (let bIndex = bills.length - 1; bIndex >= 0; bIndex -= 1) {
              const bill = bills[bIndex];
              if ((bill.partyId || bill.party) !== partyId) continue;
              const foundRow = (bill.rows || []).find(r => r.item === value);
              if (foundRow && foundRow.rate) { defaultRate = parseFloat(foundRow.rate) || 0; break; }
            }
          } catch (e) { /* no-op */ }
          if (!defaultRate && itemObj) {
            defaultRate = parseFloat(itemObj.purchasePrice || itemObj.rate || 0) || 0;
          }
          if (!updated.rate || updated.rate === 0) {
            updated.rate = defaultRate;
          }
        }
        if (updated.gstPercent === undefined) {
          let itemObj = findItemInMaster(field === 'item' ? value : row.item, items);
          updated.gstPercent = itemObj ? (itemObj.gstPercentage || 0) : 0;
        }

        // Get GSTINs
        const sellerGstin = company.gstin || '';
        const partyObj = parties.find(p => p.id === (field === 'party' ? value : party));
        const buyerGstin = partyObj ? (partyObj.gstin || '') : '';
        const partyGstType = partyObj ? (partyObj.gstType || 'Unregistered') : 'Unregistered';

        // Always auto-calculate GST split if item or party is changed
        if (field === 'item' || field === 'party') {
          // Only calculate GST if party is Regular
          if (partyGstType === 'Regular') {
            if (sellerGstin && buyerGstin && sellerGstin.length >= 2 && buyerGstin.length >= 2) {
              if (sellerGstin.substring(0, 2) === buyerGstin.substring(0, 2)) {
                // Same state: split GST
                updated.sgst = updated.gstPercent / 2;
                updated.cgst = updated.gstPercent / 2;
                updated.igst = 0;
              } else {
                // Different state: IGST only
                updated.sgst = 0;
                updated.cgst = 0;
                updated.igst = updated.gstPercent;
              }
            } else {
              // Fallback: split GST
              updated.sgst = updated.gstPercent / 2;
              updated.cgst = updated.gstPercent / 2;
              updated.igst = 0;
            }
          } else {
            // For Composition/Unregistered parties, set GST to 0
            updated.sgst = 0;
            updated.cgst = 0;
            updated.igst = 0;
          }
        }

        // Quantity calculation: prefer qty expression; fallback to Nos×Length×Height
        let itemObj = findItemInMaster(field === 'item' ? value : row.item, items);
        const unit = itemObj ? itemObj.quantityMeasurement : '';
        
        // Set unit and quantityMeasurement in the row whenever item is present
        if (itemObj && (field === 'item' || !updated.unit)) {
          updated.unit = unit;
          updated.quantityMeasurement = unit;
        }
        
        const parsed = parseQtyExpression(updated.qtyExpr);
        let computedQty;
        if (parsed.ok) {
          computedQty = parsed.value;
          updated.qtyDisplay = parsed.display;
        } else {
          computedQty = (parseFloat(updated.nos) || 0) * (parseFloat(updated.length) || 1) * (parseFloat(updated.height) || 1);
          if (!updated.qtyExpr) {
            const a = parseFloat(updated.nos) || 0; const b = parseFloat(updated.length) || 1; const c = parseFloat(updated.height) || 1;
            updated.qtyDisplay = `${a}×${b}×${c}`;
          }
        }
        updated.qty = computedQty;
        // Keep nos in sync for legacy logic
        updated.nos = computedQty;
        // Apply per-line discount to amount
        const rawAmount = calculateLineTotal(updated.qty, updated.rate);
        let lineDiscAmt = 0;
        if ((updated.lineDiscountType || 'amount') === 'percent') {
          lineDiscAmt = calculateGSTAmount(rawAmount, updated.lineDiscountValue);
        } else {
          lineDiscAmt = roundTo2Decimals(parseFloat(updated.lineDiscountValue) || 0);
        }
        lineDiscAmt = Math.min(Math.max(lineDiscAmt, 0), rawAmount);
        updated.amount = roundTo2Decimals(rawAmount - lineDiscAmt);
        
        // GST and total calculation - only for Regular parties
        const currentPartyForGst = parties.find(p => p.id === party);
        const currentPartyGstType = currentPartyForGst ? (currentPartyForGst.gstType || 'Unregistered') : 'Unregistered';
        
        if (currentPartyGstType === 'Regular') {
          const sgstAmt = calculateGSTAmount(updated.amount, updated.sgst);
          const cgstAmt = calculateGSTAmount(updated.amount, updated.cgst);
          const igstAmt = calculateGSTAmount(updated.amount, updated.igst);
          updated.total = roundTo2Decimals((parseFloat(updated.amount) || 0) + sgstAmt + cgstAmt + igstAmt);
        } else {
          // For Composition/Unregistered parties, total equals amount (no GST)
          updated.total = roundTo2Decimals(parseFloat(updated.amount) || 0);
        }
        return updated;
      });

      // Auto-add trailing row logic
      const isRowNonEmpty = (r) => !!(r.item || (parseFloat(r.rate) || 0) || (r.qtyExpr && String(r.qtyExpr).trim()) || (parseFloat(r.qty) || 0) > 0 || (parseFloat(r.lineDiscountValue) || 0));
      const last = updatedRows[updatedRows.length - 1];
      if (isRowNonEmpty(last)) {
        updatedRows.push({ ...initialItemRow });
      }
      while (updatedRows.length < 5) updatedRows.push({ ...initialItemRow });
      return updatedRows;
    });
  };
  const addRow = () => {
    setRows([...rows, { ...initialItemRow }]);
    // Move to the new item in mobile view
    setCurrentMobileItemIndex(rows.length);
  };
  const removeRow = idx => {
    setRows(rows.filter((_, i) => i !== idx));
    // Adjust mobile carousel index
    if (currentMobileItemIndex >= rows.length - 1) {
      setCurrentMobileItemIndex(Math.max(0, currentMobileItemIndex - 1));
    }
  };

  // Mobile carousel navigation functions
  const goToPreviousItem = () => {
    setCurrentMobileItemIndex(Math.max(0, currentMobileItemIndex - 1));
  };

  const goToNextItem = () => {
    setCurrentMobileItemIndex(Math.min(rows.length - 1, currentMobileItemIndex + 1));
  };

  const goToItem = (index) => {
    setCurrentMobileItemIndex(index);
  };

  // Touch event handlers for swipe gestures
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentMobileItemIndex < rows.length - 1) {
      goToNextItem();
    }
    if (isRightSwipe && currentMobileItemIndex > 0) {
      goToPreviousItem();
    }
  };

  useEffect(() => {
    setMobileItemPickerOpen(false);
  }, [currentMobileItemIndex]);

  useEffect(() => {
    if (!mobileItemPickerOpen) return;
    const handlePointerDown = (e) => {
      if (mobileItemPickerRef.current && !mobileItemPickerRef.current.contains(e.target)) {
        setMobileItemPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [mobileItemPickerOpen]);

  // ADD PARTY Modal functions
  const clearAddPartyForm = () => {
    setNewPartyName('');
    setNewPartyGstin('');
    setNewPartyAddress('');
    setNewPartyPhone('');
    setNewPartyEmail('');
    setNewPartyGstType('Unregistered');
    setNewPartyOpeningBalance('');
    setNewPartyCreditLimit('');
    setNewPartyCreditPeriod('');
    setShowAdvancedFields(false);
    setAddPartyMessage('');
  };

  const handleAddParty = async () => {
    if (!db || !userId) {
      setAddPartyMessage("Firebase not initialized or user not authenticated.");
      return;
    }

    if (!newPartyName.trim()) {
      setAddPartyMessage("Party name is required.");
      return;
    }

    // Validate GSTIN for Regular parties
    if (newPartyGstType === 'Regular' && !newPartyGstin.trim()) {
      setAddPartyMessage("GSTIN is mandatory for Regular parties.");
      return;
    }

    try {
      const partiesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
      const partyData = {
        firmName: newPartyName.trim(),
        personName: '',
        address: newPartyAddress.trim(),
        contact: newPartyPhone.trim(),
        email: newPartyEmail.trim(),
        whatsapp: newPartyPhone.trim(),
        gstin: newPartyGstin.trim(),
        gstType: newPartyGstType,
        pan: '',
        city: '',
        stateName: '',
        pincode: '',
        creditPeriod: parseFloat(newPartyCreditPeriod) || 0,
        creditLimit: parseFloat(newPartyCreditLimit) || 0,
        openingBalance: parseFloat(newPartyOpeningBalance) || 0,
        partyType: 'Seller',
        useManualBalance: true,
        manualBalance: 0,
        manualBalanceDate: new Date().toISOString().split('T')[0],
        manualBalanceRemarks: '',
        createdAt: serverTimestamp(),
        createdBy: userId,
        companyId: companyId
      };

      const docRef = await addDoc(partiesCollectionRef, partyData);
      setAddPartyMessage("Party added successfully!");
      
      // Auto-select the newly added party
      setParty(docRef.id);
      setSelectedPartyForQuickItems(docRef.id);
      setPartySearchText(newPartyName.trim());
      
      clearAddPartyForm();
      setShowAddPartyModal(false);
    } catch (error) {
      console.error("Error adding party:", error);
      setAddPartyMessage("Error adding party. Please try again.");
    }
  };
  
  // ADD ITEM Modal functions
  const clearAddItemForm = () => {
    setNewItemName('');
    setNewItemMeasurement('');
    setNewItemRate('');
    setNewItemDescription('');
    setNewItemHsnCode('');
    setNewItemGstPercentage('0');
    setNewItemPurchasePrice('0');
    setNewItemSalePrice('0');
    setNewItemCurrentStock('0');
    setNewItemIsActive(true);
    setNewItemCustomUnit('');
    setAddItemMessage('');
  };

  const handleAddItem = async () => {
    if (!db || !userId) {
      setAddItemMessage("Firebase not initialized or user not authenticated.");
      return;
    }

    if (!newItemName.trim()) {
      setAddItemMessage("Please enter item name.");
      return;
    }

    try {
      const itemsCollectionRef = collection(db, 'companies', companyId, 'items');
      const itemData = {
        itemName: newItemName.trim(),
        quantityMeasurement: newItemMeasurement.trim() || 'Nos',
        rate: parseFloat(newItemRate) || 0,
        description: newItemDescription.trim(),
        hsn: newItemHsnCode.trim(),
        gstPercentage: parseFloat(newItemGstPercentage) || 0,
        purchasePrice: parseFloat(newItemPurchasePrice) || 0,
        salePrice: parseFloat(newItemSalePrice) || 0,
        currentStock: parseFloat(newItemCurrentStock) || 0,
        isActive: newItemIsActive,
        customUnit: newItemCustomUnit.trim(),
        createdAt: serverTimestamp(),
        createdBy: userId,
        companyId: companyId
      };

      await addDoc(itemsCollectionRef, itemData);
      setAddItemMessage("Item added successfully!");
      clearAddItemForm();
      setShowAddItemModal(false);
    } catch (error) {
      console.error("Error adding item:", error);
      setAddItemMessage("Error adding item. Please try again.");
    }
  };




  // Save/Update bill
  const handleSaveBill = async () => {
    if (!db || !userId || !appId) return;
    if (isSavingBillRef.current) return;
    isSavingBillRef.current = true;
    setIsSavingBill(true);
    try {
    
    // Resolve typed names → item ids; keep only rows with a linked item
    const filledRows = [];
    const unresolvedLabels = [];
    rows.forEach((row, idx) => {
      const hasNumbers =
        (parseFloat(row.qty) || 0) > 0 ||
        (parseFloat(row.rate) || 0) > 0 ||
        (parseFloat(row.amount) || 0) > 0 ||
        (parseFloat(row.total) || 0) > 0;
      const hasItemHint =
        (row.item && String(row.item).trim()) ||
        (row.itemId && String(row.itemId).trim()) ||
        itemSearchTextByRow[idx];
      if (!hasItemHint && !hasNumbers) return;
      const resolved = resolveRowItemBeforeSave(row, items, itemSearchTextByRow[idx] || '');
      if (resolved.ok && getBillLineItemId(resolved.row)) {
        filledRows.push(enrichBillRowForSave(resolved.row, items));
      } else if (hasItemHint || hasNumbers) {
        unresolvedLabels.push(
          itemSearchTextByRow[idx] || row.itemName || row.name || `Line ${idx + 1}`
        );
      }
    });
    
    if (!party || filledRows.length === 0) {
      warning("Please select a party and add at least one item.", { title: 'Validation Error' });
      return;
    }
    if (unresolvedLabels.length > 0) {
      warning(
        `Please select a valid item for: ${unresolvedLabels.slice(0, 3).join(', ')}${unresolvedLabels.length > 3 ? '…' : ''}.`,
        { title: 'Item not linked' }
      );
      return;
    }
    const selected = docTypeOptions.find(opt => opt.value === docType);
    if (!selected) return;
    const collectionRef = getCollectionForCompany(db, { companyId, appId, userId }, selected.collection);
    
    // CRITICAL FIX: Ensure bill number is generated if empty (handles race condition)
    let finalBillNumber = billNumber;
    if (!finalBillNumber || finalBillNumber.trim() === '') {
      // Generate bill number synchronously if it's empty
      finalBillNumber = generateBillNumber();
      if (!finalBillNumber || finalBillNumber.trim() === '') {
        error("Error: Could not generate bill number. Please try again.", { title: 'Bill Number Error' });
        return;
      }
      setBillNumber(finalBillNumber); // Update state for next time
    }
  const subtotal = roundTo2Decimals(filledRows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0));
  
  // Get party GST type to determine GST calculation
  const partyObj = parties.find(p => p.id === party);
  const partyGstType = partyObj ? (partyObj.gstType || 'Unregistered') : 'Unregistered';
  
  let totalSGST = 0, totalCGST = 0, totalIGST = 0, grandTotal = 0;
  
  if (partyGstType === 'Regular') {
    // Calculate GST only for Regular parties
    totalSGST = filledRows.reduce((sum, row) => sum + calculateGSTAmount(row.amount, row.sgst), 0);
    totalCGST = filledRows.reduce((sum, row) => sum + calculateGSTAmount(row.amount, row.cgst), 0);
    totalIGST = filledRows.reduce((sum, row) => sum + calculateGSTAmount(row.amount, row.igst), 0);
    grandTotal = roundTo2Decimals(subtotal + totalSGST + totalCGST + totalIGST);
  } else {
    // For Composition/Unregistered parties, no GST calculation
    totalSGST = 0;
    totalCGST = 0;
    totalIGST = 0;
    grandTotal = roundTo2Decimals(subtotal); // Total equals subtotal (no GST added)
  }
    const billData = {
      number: finalBillNumber,
      billDate,
      party,
      notes,
      rows: filledRows,
      customFields,
      amount: grandTotal,
      createdAt: serverTimestamp(),
      docType, // Store document type
    };
    if (docType === 'purchaseBill') {
      appendWorkOrderFields(billData, workOrderLink);
    }
    // Add fields for returns and notes
    if (docType === 'purchaseReturn' || docType === 'debitNote' || docType === 'creditNote') {
      billData.originalBillId = originalBillId;
      billData.originalBillNumber = originalBillNumber;
      if (docType === 'purchaseReturn') {
        billData.returnReason = returnReason;
        billData.isReturn = true;
      }
    }

    const allBillCollections = ['salesBills', 'challans', 'quotations', 'purchaseBills', 'purchaseOrders'];
    let allBills = [];
    for (const coll of allBillCollections) {
      const billsSnap = await getDocs(getCollectionForCompany(db, { companyId, appId, userId }, coll));
      billsSnap.forEach(doc => allBills.push({ ...doc.data(), id: doc.id, collection: coll }));
    }
    
    // CRITICAL FIX: If bill number is still empty after initial check, generate using fresh bills data
    if (!finalBillNumber || finalBillNumber.trim() === '') {
      // Filter bills for the current document type to generate number
      const currentTypeBills = allBills.filter(b => {
        const billCollection = b.collection || '';
        if (docType === 'purchaseBill') return billCollection === 'purchaseBills';
        if (docType === 'purchaseOrder') return billCollection === 'purchaseOrders';
        return false;
      });
      finalBillNumber = generateBillNumber(currentTypeBills);
      if (!finalBillNumber || finalBillNumber.trim() === '') {
        error("Error: Could not generate bill number. Please try again.", { title: 'Bill Number Error' });
        return;
      }
      setBillNumber(finalBillNumber); // Update state
    }
    
    // 1. Check for duplicate number
    if (allBills.some(b => b.number === finalBillNumber && (editingBillId ? b.id !== editingBillId : true))) {
      // If duplicate found, regenerate number using fresh bills data and check again
      const currentTypeBills = allBills.filter(b => {
        const billCollection = b.collection || '';
        if (docType === 'purchaseBill') return billCollection === 'purchaseBills';
        if (docType === 'purchaseOrder') return billCollection === 'purchaseOrders';
        return false;
      });
      finalBillNumber = generateBillNumber(currentTypeBills);
      if (allBills.some(b => b.number === finalBillNumber && (editingBillId ? b.id !== editingBillId : true))) {
        setErrorMessage('Bill number already exists in another document. Please use a unique number.');
        error('Bill number already exists in another document. Please use a unique number.', { title: 'Duplicate Bill Number' });
        return;
      }
      setBillNumber(finalBillNumber); // Update state with regenerated number
    }
    // 2. Gap/date check: allow if any bill in FY has the same date
    const fy = getFinancialYear(billDate);
    const fyBills = allBills.filter(b => (b.number || '').startsWith(fy));
    const thisSerial = parseInt((finalBillNumber || '').split('/')[1], 10);
    const thisDate = new Date(billDate);
    const sameDateExists = fyBills.some(b => {
      if (!b.number || (!b.invoiceDate && !b.billDate)) return false;
      if (b.id === editingBillId) return false;
      const date = new Date(b.invoiceDate || b.billDate);
      return date.getTime() === thisDate.getTime();
    });
    if (!sameDateExists) {
      // Find previous and next serials
      const sortedBills = fyBills
        .filter(b => b.id !== editingBillId && b.number)
        .map(b => ({
          serial: parseInt((b.number || '').split('/')[1], 10),
          date: new Date(b.invoiceDate || b.billDate),
        }))
        .sort((a, b) => a.serial - b.serial);
      let prev = null, next = null;
      for (let i = 0; i < sortedBills.length; i++) {
        if (sortedBills[i].serial < thisSerial) prev = sortedBills[i];
        if (sortedBills[i].serial > thisSerial) { next = sortedBills[i]; break; }
      }
      if (prev && thisDate.getTime() < prev.date.getTime()) {
        setErrorMessage(`Date must be on or after previous bill (${prev.serial}) date: ${prev.date.toLocaleDateString()}`);
        warning(`Date must be on or after previous bill (${prev.serial}) date: ${prev.date.toLocaleDateString()}`, { title: 'Date Validation Error' });
        return;
      }
      if (next && thisDate.getTime() > next.date.getTime()) {
        setErrorMessage(`Date must be on or before next bill (${next.serial}) date: ${next.date.toLocaleDateString()}`);
        warning(`Date must be on or before next bill (${next.serial}) date: ${next.date.toLocaleDateString()}`, { title: 'Date Validation Error' });
        return;
      }
    }

      let savedBill;
      if (editingBillId) {
        const billRef = doc(getCollectionForCompany(db, { companyId, appId, userId }, selected.collection), editingBillId);
        await setDoc(billRef, billData, { merge: true });
        savedBill = { id: editingBillId, ...billData };
      } else {
        const docRef = await addDoc(collectionRef, billData);
        savedBill = { id: docRef.id, ...billData };
      }

      if (docType === 'purchaseBill' && savedBill?.id) {
        await syncLedgerFromSource(db, companyCtx, {
          sourceType: LEDGER_SOURCE_TYPES.purchase,
          sourceCollection: 'purchaseBills',
          sourceId: savedBill.id,
          doc: savedBill,
        });
      }

      // --- ADVANCE ALLOCATION LOGIC ---
      // Only allocate advance for purchase bills (not purchase orders)
      if (docType === 'purchaseBill') {
        const availableAdvance = getPartyAdvance(party, payments);
        if (availableAdvance > 0) {
          const { allocatedAdvance, advanceAllocations, remainingBillAmount } = allocateAdvanceToBill(party, grandTotal, payments);
          if (allocatedAdvance > 0) {
            // Mark advances as used
            await markAdvanceUsed(advanceAllocations, db, appId, userId, payments, companyId);
            // Optionally, create a payment record for advance allocation
            const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
            await addDoc(paymentsRef, {
              receiptNumber: `ADV-${finalBillNumber}`,
              paymentDate: new Date().toISOString().split('T')[0],
              partyId: party,
              partyName: parties.find(p => p.id === party)?.firmName || '',
              totalAmount: allocatedAdvance,
              paymentMode: 'Advance Allocation',
              reference: 'Auto-advance',
              notes: 'Advance automatically allocated to new purchase bill',
              type: 'purchase',
              paymentType: 'advance-allocation',
              billId: savedBill.id,
              billNumber: finalBillNumber,
              allocations: [{
                billType: 'purchase',
                billId: savedBill.id,
                billNumber: finalBillNumber,
                allocatedAmount: allocatedAdvance,
                billOutstanding: grandTotal,
                isFullPayment: allocatedAdvance >= grandTotal
              }],
              advanceAllocations,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          }
        }
      }

      // Show success notification
      const docTypeLabel = docTypeOptions.find(opt => opt.value === docType)?.label || 'Document';
      const partyName = parties.find(p => p.id === party)?.firmName || 'Unknown Party';
      const action = editingBillId ? 'updated' : 'created';
      
      success(
        `${docTypeLabel} ${finalBillNumber} ${action} successfully!`,
        {
          title: `${docTypeLabel} ${action.charAt(0).toUpperCase() + action.slice(1)}`,
          details: `${finalBillNumber} • ${partyName} • ${formatCurrency(grandTotal, companyCurrency)}`,
          duration: 6000
        }
      );

      setBillNumber('');
      setBillDate(new Date().toISOString().split('T')[0]);
      setParty('');
      setNotes('');
      setRows(Array.from({ length: 5 }, () => ({ ...initialItemRow })));
      setCustomFields({ ewayBillNo: '', ewayQr: '', ewayDate: '' });
      setCurrentMobileItemIndex(0);
      setEditingBillId(null);
      setSavedBillId(savedBill.id);
      setShowSuccessModal(true);
      // Reset return/note fields
      setOriginalBillId('');
      setOriginalBillNumber('');
      setReturnReason('');
    } catch (err) {
      // 🚫 Handle subscription/limit errors
      if (err.code === 'SUBSCRIPTION_EXPIRED' || err.code === 'FEATURE_LIMIT_EXCEEDED') {
        console.log('🚫 Subscription expired or limit exceeded:', err);
        // Error notification is already shown by ErrorNotificationService
        return;
      }
      
      error("Error saving bill: " + err.message, { title: 'Save Failed', duration: 7000 });
    } finally {
      isSavingBillRef.current = false;
      setIsSavingBill(false);
    }
  };

  // Function to clear bill form fields
  const clearBillForm = () => {
    setBillNumber('');
    setBillDate(new Date().toISOString().split('T')[0]);
    setParty('');
    setNotes('');
    setRows([{ ...initialItemRow }]);
    setCustomFields({ ewayBillNo: '', ewayQr: '', ewayDate: '' });
    setEditingBillId(null);
    setMessage('');
    setItemSearchTextByRow({});
    setCurrentMobileItemIndex(0);
    // Reset return/note fields
    setOriginalBillId('');
    setOriginalBillNumber('');
    setReturnReason('');
  };

  // Edit bill
  const handleEditBill = (bill) => {
    setEditingBillId(bill.id);
    setBillNumber(bill.number || '');
    setBillDate(bill.billDate || '');
    setParty(bill.party || '');
    setNotes(bill.notes || '');
    setRows(bill.rows || [{ ...initialItemRow }]);
    setCustomFields(bill.customFields || { ewayBillNo: '', ewayQr: '', ewayDate: '' });
    
    // Initialize itemSearchTextByRow for mobile view
    const newItemSearchText = {};
    if (bill.rows && bill.rows.length > 0) {
      bill.rows.forEach((row, index) => {
        if (row.item) {
          const item = items.find(it => it.id === row.item);
          if (item) {
            newItemSearchText[index] = item.itemName || item.name || '';
          }
        }
      });
    }
    setItemSearchTextByRow(newItemSearchText);
    
    // Reset mobile carousel to first item
    setCurrentMobileItemIndex(0);
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Load return/note fields if applicable
    if (bill.docType === 'purchaseReturn' || bill.docType === 'debitNote' || bill.docType === 'creditNote') {
      setOriginalBillId(bill.originalBillId || '');
      setOriginalBillNumber(bill.originalBillNumber || '');
      if (bill.docType === 'purchaseReturn') {
        setReturnReason(bill.returnReason || '');
      }
    }
  };

  // Function to fetch original bill by number and auto-populate form
  const fetchOriginalBill = async (billNumber) => {
    if (!billNumber || !billNumber.trim() || !db || !userId || !appId || !companyId) {
      return;
    }

    try {
      // Search in purchaseBills collection
      const purchaseBillsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseBills');
      if (!purchaseBillsRef) return;

      const purchaseBillsQuery = query(purchaseBillsRef, where('number', '==', billNumber.trim()));
      const purchaseBillsSnapshot = await getDocs(purchaseBillsQuery);
      
      let originalBill = null;
      
      if (!purchaseBillsSnapshot.empty) {
        const doc = purchaseBillsSnapshot.docs[0];
        originalBill = { id: doc.id, ...doc.data() };
      } else {
        // Also check purchaseOrders
        const purchaseOrdersRef = getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseOrders');
        if (purchaseOrdersRef) {
          const purchaseOrdersQuery = query(purchaseOrdersRef, where('number', '==', billNumber.trim()));
          const purchaseOrdersSnapshot = await getDocs(purchaseOrdersQuery);
          if (!purchaseOrdersSnapshot.empty) {
            const doc = purchaseOrdersSnapshot.docs[0];
            originalBill = { id: doc.id, ...doc.data() };
          }
        }
      }

      if (originalBill) {
        // Set original bill ID and number
        setOriginalBillId(originalBill.id);
        setOriginalBillNumber(billNumber.trim());
        
        // Auto-populate party
        if (originalBill.party || originalBill.partyId) {
          setParty(originalBill.party || originalBill.partyId);
        }

        // Auto-populate items/rows
        const billRows = originalBill.rows || originalBill.items || [];
        if (billRows.length > 0) {
          const newRows = billRows.map(row => {
            // Get item details to get default unit/measurement
            const itemObj = items.find(it => it.id === row.item);
            const defaultUnit = itemObj ? (itemObj.quantityMeasurement || itemObj.unit || '') : '';
            
            return {
              item: row.item || '',
              // Preserve all measurement fields
              nos: row.nos || 1,
              length: row.length || 0,
              height: row.height || 0,
              qty: row.qty || row.quantity || 0,
              qtyExpr: row.qtyExpr || '',
              qtyDisplay: row.qtyDisplay || '',
              unit: row.unit || row.measurement || defaultUnit || '',
              quantityMeasurement: row.quantityMeasurement || row.unit || row.measurement || defaultUnit || '',
              rate: row.rate || row.price || 0,
              discount: row.discount || '',
              discountType: row.discountType || 'percentage',
              lineDiscountType: row.lineDiscountType || 'amount',
              lineDiscountValue: row.lineDiscountValue || 0,
              amount: row.amount || 0,
              gstRate: row.gstRate || row.gst || 0,
              sgst: row.sgst || 0,
              cgst: row.cgst || 0,
              igst: row.igst || 0,
              total: row.total || 0,
              hsn: row.hsn || row.hsnCode || '',
              description: row.description || '',
              // Store original quantity for partial return reference
              originalQty: row.qty || row.quantity || 0,
              originalQtyExpr: row.qtyExpr || '',
              originalQtyDisplay: row.qtyDisplay || ''
            };
          });

          // Fill remaining rows with empty data
          const totalRows = Math.max(newRows.length, 5);
          const filledRows = Array.from({ length: totalRows }, (_, i) => 
            newRows[i] || { ...initialItemRow }
          );
          setRows(filledRows);

          // Update item search text for each row
          const newItemSearchText = {};
          filledRows.forEach((row, index) => {
            if (row.item) {
              const item = items.find(it => it.id === row.item);
              if (item) {
                newItemSearchText[index] = item.itemName || item.name || '';
              }
            }
          });
          setItemSearchTextByRow(newItemSearchText);
        }

        // Auto-populate date
        if (originalBill.billDate || originalBill.date) {
          setBillDate(originalBill.billDate || originalBill.date);
        }

        // Auto-populate notes if any
        if (originalBill.notes) {
          setNotes(originalBill.notes);
        }

        success(`Original bill "${billNumber}" loaded successfully!`);
      } else {
        warning(`Bill number "${billNumber}" not found. Please check the number and try again.`);
      }
    } catch (err) {
      console.error('Error fetching original bill:', err);
      error('Error fetching original bill: ' + err.message);
    }
  };

  // Handle original bill number change with debounce
  const originalBillSearchTimeoutRef = useRef(null);
  const handleOriginalBillNumberChange = (value) => {
    setOriginalBillNumber(value);
    
    // Clear previous timeout
    if (originalBillSearchTimeoutRef.current) {
      clearTimeout(originalBillSearchTimeoutRef.current);
    }

    // Only search if value is not empty and has at least 3 characters
    if (value && value.trim().length >= 3) {
      originalBillSearchTimeoutRef.current = setTimeout(() => {
        fetchOriginalBill(value.trim());
      }, 1000); // Wait 1 second after user stops typing
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (originalBillSearchTimeoutRef.current) {
        clearTimeout(originalBillSearchTimeoutRef.current);
      }
    };
  }, []);

  // Delete bill
  const handleDeleteBill = async (billId) => {
    if (!db || !userId || !appId) return;
    const selected = docTypeOptions.find(opt => opt.value === docType);
    if (!selected) return;
    
    // Get bill details before deleting for notification
    const billToDelete = bills.find(b => b.id === billId);
    const billNumber = billToDelete?.number || 'Unknown';
    const partyName = billToDelete?.party ? (parties.find(p => p.id === billToDelete.party)?.firmName || 'Unknown Party') : 'Unknown Party';
    const docTypeLabel = selected.label || 'Document';
    
    const billRef = doc(getCollectionForCompany(db, { companyId, appId, userId }, selected.collection), billId);
    if (docType === 'purchaseBill') {
      await removeLedgerEntry(db, companyCtx, {
        sourceType: LEDGER_SOURCE_TYPES.purchase,
        sourceId: billId,
      });
    }
    await deleteDoc(billRef);
    
    success(
      `${docTypeLabel} ${billNumber} deleted successfully!`,
      {
        title: `${docTypeLabel} Deleted`,
        details: `${billNumber} • ${partyName}`,
        duration: 6000
      }
    );
  };

  // Payment handling functions
  // Get bill payments for receipt preview
  const getBillPayments = (bill) => {
    if (!bill || !bill.party) {
      return [];
    }
    
    return payments.filter(payment => {
      // Check if payment is for the same party
      const paymentPartyId = payment.partyId || payment.party;
      if (paymentPartyId !== bill.party) {
        return false;
      }
      
      // Special handling for advance receipts
      if (payment.receiptNumber && payment.receiptNumber.startsWith('ADV-')) {
        // Extract invoice number from advance receipt (e.g., ADV-PUR25-26/12 -> PUR25-26/12)
        const targetInvoiceNo = payment.receiptNumber.replace('ADV-', '');
        const currentInvoiceNo = bill.number || bill.invoiceNumber || bill.id;
        return targetInvoiceNo === currentInvoiceNo;
      }
      
      // Check if payment has allocations for this invoice
      if (payment.allocations && Array.isArray(payment.allocations)) {
        return payment.allocations.some(allocation => 
          allocation.billId === bill.id && allocation.billType === 'purchase'
        );
      }
      
      // Check if payment has advance allocations for this invoice
      if (payment.advanceAllocations && Array.isArray(payment.advanceAllocations)) {
        return payment.advanceAllocations.some(allocation => 
          allocation.billId === bill.id
        );
      }
      
      // Check direct billId match
      if (payment.billId === bill.id) {
        return true;
      }
      
      return false;
    });
  };

  // Handle payment receipt preview
  const handlePreviewReceipt = (payment) => {
    setSelectedPaymentForReceipt(payment);
    setShowReceiptModal(true);
  };

  // Handle payment receipt preview from bill
  const handleViewPaymentReceipts = (bill) => {
    const billPayments = getBillPayments(bill);
    if (billPayments.length > 0) {
      setSelectedBillForReceipts(bill);
      setShowPaymentDetailsModal(true);
    }
  };

  // Print receipt
  const printReceipt = () => {
    if (receiptRef.current) {
      const printContents = receiptRef.current.innerHTML;
      const printWindow = window.open('', '', 'height=800,width=1000');
      printWindow.document.write('<html><head><title>Payment Receipt</title>');
      printWindow.document.write('<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/tailwindcss/dist/tailwind.min.css">');
      printWindow.document.write('<style>');
      printWindow.document.write('.print\\:hidden { display: none !important; }');
      printWindow.document.write('@media print { .print\\:hidden { display: none !important; } }');
      printWindow.document.write('.print\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }');
      printWindow.document.write('.print\\:bg-white { background-color: white !important; }');
      printWindow.document.write('.print\\:border { border-width: 1px !important; }');
      printWindow.document.write('.print\\:border-gray-300 { border-color: #d1d5db !important; }');
      printWindow.document.write('.print\\:flex-row { flex-direction: row !important; }');
      printWindow.document.write('.print\\:text-sm { font-size: 0.875rem !important; }');
      printWindow.document.write('.print\\:text-xs { font-size: 0.75rem !important; }');
      printWindow.document.write('.print\\:text-lg { font-size: 1.125rem !important; }');
      printWindow.document.write('.print\\:text-xl { font-size: 1.25rem !important; }');
      printWindow.document.write('.print\\:text-2xl { font-size: 1.5rem !important; }');
      printWindow.document.write('.print\\:font-bold { font-weight: 700 !important; }');
      printWindow.document.write('.print\\:font-semibold { font-weight: 600 !important; }');
      printWindow.document.write('.print\\:text-center { text-align: center !important; }');
      printWindow.document.write('.print\\:text-right { text-align: right !important; }');
      printWindow.document.write('.print\\:text-left { text-align: left !important; }');
      printWindow.document.write('.print\\:p-4 { padding: 1rem !important; }');
      printWindow.document.write('.print\\:p-2 { padding: 0.5rem !important; }');
      printWindow.document.write('.print\\:p-1 { padding: 0.25rem !important; }');
      printWindow.document.write('.print\\:m-4 { margin: 1rem !important; }');
      printWindow.document.write('.print\\:m-2 { margin: 0.5rem !important; }');
      printWindow.document.write('.print\\:m-1 { margin: 0.25rem !important; }');
      printWindow.document.write('.print\\:border-b { border-bottom-width: 1px !important; }');
      printWindow.document.write('.print\\:border-gray-300 { border-color: #d1d5db !important; }');
      printWindow.document.write('.print\\:w-full { width: 100% !important; }');
      printWindow.document.write('.print\\:h-auto { height: auto !important; }');
      printWindow.document.write('.print\\:max-w-none { max-width: none !important; }');
      printWindow.document.write('.print\\:shadow-none { box-shadow: none !important; }');
      printWindow.document.write('.print\\:bg-white { background-color: white !important; }');
      printWindow.document.write('.print\\:text-black { color: black !important; }');
      printWindow.document.write('.print\\:text-gray-800 { color: #1f2937 !important; }');
      printWindow.document.write('.print\\:text-gray-600 { color: #4b5563 !important; }');
      printWindow.document.write('.print\\:text-gray-500 { color: #6b7280 !important; }');
      printWindow.document.write('.print\\:text-blue-600 { color: #2563eb !important; }');
      printWindow.document.write('.print\\:text-green-600 { color: #16a34a !important; }');
      printWindow.document.write('.print\\:text-red-600 { color: #dc2626 !important; }');
      printWindow.document.write('.print\\:text-yellow-600 { color: #ca8a04 !important; }');
      printWindow.document.write('.print\\:text-purple-600 { color: #9333ea !important; }');
      printWindow.document.write('.print\\:text-pink-600 { color: #db2777 !important; }');
      printWindow.document.write('.print\\:text-indigo-600 { color: #4f46e5 !important; }');
      printWindow.document.write('.print\\:text-teal-600 { color: #0d9488 !important; }');
      printWindow.document.write('.print\\:text-orange-600 { color: #ea580c !important; }');
      printWindow.document.write('.print\\:text-cyan-600 { color: #0891b2 !important; }');
      printWindow.document.write('.print\\:text-lime-600 { color: #65a30d !important; }');
      printWindow.document.write('.print\\:text-emerald-600 { color: #059669 !important; }');
      printWindow.document.write('.print\\:text-rose-600 { color: #e11d48 !important; }');
      printWindow.document.write('.print\\:text-violet-600 { color: #7c3aed !important; }');
      printWindow.document.write('.print\\:text-fuchsia-600 { color: #c026d3 !important; }');
      printWindow.document.write('.print\\:text-sky-600 { color: #0284c7 !important; }');
      printWindow.document.write('.print\\:text-slate-600 { color: #475569 !important; }');
      printWindow.document.write('.print\\:text-zinc-600 { color: #52525b !important; }');
      printWindow.document.write('.print\\:text-neutral-600 { color: #525252 !important; }');
      printWindow.document.write('.print\\:text-stone-600 { color: #57534e !important; }');
      printWindow.document.write('.print\\:text-red-500 { color: #ef4444 !important; }');
      printWindow.document.write('.print\\:text-green-500 { color: #22c55e !important; }');
      printWindow.document.write('.print\\:text-blue-500 { color: #3b82f6 !important; }');
      printWindow.document.write('.print\\:text-yellow-500 { color: #eab308 !important; }');
      printWindow.document.write('.print\\:text-purple-500 { color: #a855f7 !important; }');
      printWindow.document.write('.print\\:text-pink-500 { color: #ec4899 !important; }');
      printWindow.document.write('.print\\:text-indigo-500 { color: #6366f1 !important; }');
      printWindow.document.write('.print\\:text-teal-500 { color: #14b8a6 !important; }');
      printWindow.document.write('.print\\:text-orange-500 { color: #f97316 !important; }');
      printWindow.document.write('.print\\:text-cyan-500 { color: #06b6d4 !important; }');
      printWindow.document.write('.print\\:text-lime-500 { color: #84cc16 !important; }');
      printWindow.document.write('.print\\:text-emerald-500 { color: #10b981 !important; }');
      printWindow.document.write('.print\\:text-rose-500 { color: #f43f5e !important; }');
      printWindow.document.write('.print\\:text-violet-500 { color: #8b5cf6 !important; }');
      printWindow.document.write('.print\\:text-fuchsia-500 { color: #d946ef !important; }');
      printWindow.document.write('.print\\:text-sky-500 { color: #0ea5e9 !important; }');
      printWindow.document.write('.print\\:text-slate-500 { color: #64748b !important; }');
      printWindow.document.write('.print\\:text-zinc-500 { color: #71717a !important; }');
      printWindow.document.write('.print\\:text-neutral-500 { color: #737373 !important; }');
      printWindow.document.write('.print\\:text-stone-500 { color: #78716c !important; }');
      printWindow.document.write('@media print { img { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }');
      printWindow.document.write('</style>');
      printWindow.document.write('</head><body>');
      printWindow.document.write(printContents);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 100);
    }
  };

  // Generate PDF receipt
  const generatePDFReceipt = async () => {
    if (!selectedPaymentForReceipt) return;
    
    let jsPDF;
    if (window.jspdf && window.jspdf.jsPDF) {
      jsPDF = window.jspdf.jsPDF;
    } else if (window.jsPDF) {
      jsPDF = window.jsPDF;
    } else {
      try {
        const mod = await import('jspdf');
        jsPDF = (mod && (mod.jsPDF || mod.default)) || undefined;
      } catch (e) {
        console.error('Failed to load jsPDF:', e);
      }
    }
    if (!jsPDF) {
      error('PDF generation library not available', { title: 'PDF Error' });
      return;
    }
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (2 * margin);
      let yPosition = margin;
      
      // Helper function to safely get text
      const safeText = (text) => text || '';
      

      
      // Company details
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text(safeText(company.firmName) || 'Company Name', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      if (safeText(company.address)) {
        doc.text(safeText(company.address), pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 5;
      }
      if (safeText(company.city)) {
        doc.text(safeText(company.city), pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 5;
      }
      if (safeText(company.gstin)) {
        doc.text(`GSTIN: ${safeText(company.gstin)}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 5;
      }
      
      yPosition += 10;
      
      // Receipt title
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('PAYMENT RECEIPT', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;
      
      // Receipt details
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      
      const receiptData = [
        ['Receipt Number:', safeText(selectedPaymentForReceipt.receiptNumber)],
        ['Date:', safeText(selectedPaymentForReceipt.paymentDate)],
        ['Party Name:', safeText(selectedPaymentForReceipt.partyName)],
        ['Payment Mode:', safeText(selectedPaymentForReceipt.paymentMode)],
        ['Amount:', formatCurrency(selectedPaymentForReceipt.totalAmount || 0, companyCurrency)],
      ];
      
      if (safeText(selectedPaymentForReceipt.reference)) {
        receiptData.push(['Reference:', safeText(selectedPaymentForReceipt.reference)]);
      }
      if (safeText(selectedPaymentForReceipt.notes)) {
        receiptData.push(['Notes:', safeText(selectedPaymentForReceipt.notes)]);
      }
      
      receiptData.forEach(([label, value]) => {
        doc.setFont(undefined, 'bold');
        doc.text(label, margin, yPosition);
        doc.setFont(undefined, 'normal');
        doc.text(value, margin + 50, yPosition);
        yPosition += 8;
      });
      
      // Allocations
      if (selectedPaymentForReceipt.allocations && selectedPaymentForReceipt.allocations.length > 0) {
        yPosition += 10;
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('Payment Allocations:', margin, yPosition);
        yPosition += 10;
        
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        selectedPaymentForReceipt.allocations.forEach((allocation, index) => {
          const billType = safeText(allocation.billType).toUpperCase();
          const billNumber = safeText(allocation.billNumber);
          const allocatedAmount = allocation.allocatedAmount || 0;
          doc.text(`${index + 1}. ${billType} ${billNumber}: ${formatCurrency(allocatedAmount, companyCurrency)}`, margin + 5, yPosition);
          yPosition += 6;
        });
      }
      
      // Advance allocations
      if (selectedPaymentForReceipt.advanceAllocations && selectedPaymentForReceipt.advanceAllocations.length > 0) {
        yPosition += 10;
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('Advance Allocations:', margin, yPosition);
        yPosition += 10;
        
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        selectedPaymentForReceipt.advanceAllocations.forEach((advance, index) => {
          const amountUsed = advance.amountUsed || 0;
          doc.text(`${index + 1}. Advance Payment: ${formatCurrency(amountUsed, companyCurrency)}`, margin + 5, yPosition);
          yPosition += 6;
        });
      }
      
      // Footer
      yPosition = pageHeight - 30;
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text('Thank you for your payment!', pageWidth / 2, yPosition, { align: 'center' });
      
      // Save PDF with initials and compact date
      const fmt = (d) => {
        try { const x = new Date(d); const day = String(x.getDate()); const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][x.getMonth()]; const yr = x.getFullYear(); return `${day}${mon}${yr}`; } catch { return d; }
      };
      const fileName = `REC_${safeText(selectedPaymentForReceipt.receiptNumber)}_${fmt(selectedPaymentForReceipt.paymentDate)}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      error('Error generating PDF. Please try again.', { title: 'PDF Generation Error' });
    }
  };

  const handleAddPayment = async (bill) => {
    setPaymentAmount('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentMode('Cash');
    setPaymentReference('');
    setPaymentNotes('');
    setReceiptNumber('');
    
    // Generate receipt number
    const receiptNum = await generateReceiptNumber();
    setReceiptNumber(receiptNum);
    
    // Pre-fill with bill details
    setPaymentAmount(getBillOutstanding(bill).toString());
    
    // Store the bill information for payment
    setSelectedBillForPayment(bill);
    setShowPaymentModal(true);
  };

  // Quick employee cash flow recording
  const handleQuickEmployeeCashFlow = async (bill, employee, paymentMode = 'cash', customAmount = null) => {
    if (!bill || !employee || !currentCompany) {
      warning('Missing information for cash flow recording');
      return;
    }

    const outstandingAmount = getBillOutstanding(bill);
    // Parse outstanding amount - handle both string (formatted) and number
    const outstandingAmountNum = typeof outstandingAmount === 'string' 
      ? parseFloat(outstandingAmount.replace(/[₹,]/g, '')) 
      : parseFloat(outstandingAmount);
    
    // Use custom amount if provided, otherwise use outstanding amount
    let paymentAmountNum = customAmount !== null && customAmount !== undefined && customAmount !== ''
      ? parseFloat(String(customAmount).replace(/[₹,]/g, ''))
      : outstandingAmountNum;
    
    if (isNaN(paymentAmountNum) || paymentAmountNum <= 0) {
      warning('Invalid payment amount. Please enter a valid amount.');
      return;
    }
    
    // Validate that custom amount doesn't exceed outstanding
    if (paymentAmountNum > outstandingAmountNum) {
      error(`Payment amount (${formatCurrency(paymentAmountNum, companyCurrency)}) cannot exceed the bill's outstanding amount (${formatCurrency(outstandingAmountNum, companyCurrency)}).`);
      return;
    }

    try {
      const companyData = {
        companyId: currentCompany.companyId,
        userId: currentCompany.userId,
        appId: currentCompany.appId
      };

      // Use the selected payment mode from dropdown (cash or upi)
      const paymentModeValue = paymentMode === 'upi' ? 'UPI' : 'Cash';
      
      // IMPORTANT: Validate employee balance BEFORE creating payment record
      // For purchase bills (outflow), check if employee has sufficient balance
      const employeeBalance = await EmployeeCashManager.getEmployeeBalance(companyData, employee.id);
      const availableBalance = paymentMode === 'cash' ? employeeBalance.cash : paymentMode === 'upi' ? employeeBalance.upi : employeeBalance.total;
      
      if (availableBalance < paymentAmountNum) {
        error(`Insufficient balance! ${employee.name} has ₹${availableBalance.toLocaleString('en-IN')} ${paymentMode === 'cash' ? 'cash' : 'UPI'} balance, but needs ₹${paymentAmountNum.toLocaleString('en-IN')}`, {
          title: 'Insufficient Balance',
          duration: 6000
        });
        return; // Stop here - don't create payment
      }
      
      // Generate receipt number
      const receiptNum = await generateReceiptNumber();
      const paymentDateValue = new Date().toISOString().split('T')[0];
      
      // First, try to allocate available advance
      // Note: Advance reduces party's debt, but employee still pays full amount from their balance
      const availableAdvance = getPartyAdvance(bill.party, payments);
      let advanceAllocations = [];
      let advanceUsed = 0;
      
      if (availableAdvance > 0) {
        // Allocate advance to this bill
        const advanceResult = allocateAdvanceToBill(bill.party, outstandingAmountNum, payments);
        if (advanceResult.allocatedAdvance > 0) {
          advanceAllocations = advanceResult.advanceAllocations;
          advanceUsed = advanceResult.allocatedAdvance;
          
          // Mark advances as used
          if (Array.isArray(advanceResult.advanceAllocations) && advanceResult.advanceAllocations.length > 0) {
            await markAdvanceUsed(advanceResult.advanceAllocations, db, appId, userId, payments, companyId);
          }
        }
      }
      
      // Employee pays the full outstanding amount from their balance
      // (Advance is applied to party's account, not employee's)
      
      // Create payment record
      const paymentData = {
        receiptNumber: receiptNum,
        paymentDate: paymentDateValue,
        partyId: bill.party,
        partyName: getPartyName(bill.party),
        totalAmount: paymentAmountNum,
        paymentMode: paymentModeValue,
        reference: `Employee: ${employee.name}`,
        notes: '',
        type: 'purchase',
        paymentType: 'bill',
        billId: bill.id,
        billNumber: bill.number || bill.billNumber,
        allocations: [{
          billType: 'purchase',
          billId: bill.id,
          billNumber: bill.number || bill.billNumber,
          allocatedAmount: paymentAmountNum,
          billOutstanding: outstandingAmountNum,
          isFullPayment: paymentAmountNum >= outstandingAmountNum
        }],
        remainingAmount: 0,
        fifoAllocationUsed: 0,
        advanceAllocations: advanceAllocations,
        advanceUsed: advanceUsed,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      if (bill.workOrderId) {
        appendWorkOrderFields(paymentData, bill);
      }

      // Add to payments collection
      const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
      let paymentDocRef = null;
      
      try {
        paymentDocRef = await addDoc(paymentsRef, paymentData);
        if (isPaymentOutflow(paymentData) && paymentDocRef?.id) {
          await syncLedgerFromSource(db, companyCtx, {
            sourceType: LEDGER_SOURCE_TYPES.payment_out,
            sourceCollection: 'payments',
            sourceId: paymentDocRef.id,
            doc: { ...paymentData, id: paymentDocRef.id },
          });
        }
      } catch (paymentError) {
        console.error('Error creating payment record:', paymentError);
        error('Failed to create payment record. Please try again.');
        return;
      }
      
      // For purchase bills, this is an outflow (money paid)
      const transactionData = {
        employeeId: employee.id,
        employeeName: employee.name || '',
        transactionType: 'outflow',
        amount: paymentAmountNum, // Use the actual payment amount
        paymentMode: paymentMode,
        description: `Payment made for ${bill.number || bill.billNumber || 'Bill'} - ${bill.partyName || 'Vendor'}`,
        referenceNumber: bill.number || bill.billNumber || '',
        date: paymentDateValue,
        category: 'Expense',
        createdBy: {
          userId: userId || currentCompany?.userId || '',
          name: currentCompany?.userName || 'User'
        },
        linkedPaymentId: paymentDocRef.id,
        linkedReceiptNumber: receiptNum,
        ...(paymentMode === 'cash' && {
          cashGivenTo: bill.partyName || 'Vendor',
          cashVerified: true
        }),
        ...(paymentMode === 'upi' && {
          upiId: employee.paymentPreferences?.upiId || '',
          transactionId: `QUICK-${Date.now()}`,
          upiApp: employee.paymentPreferences?.upiApp || 'Other'
        })
      };

      const result = await EmployeeCashManager.createCashTransaction(companyData, transactionData);

      if (result.success) {
        // Verify balance was actually updated
        if (result.balanceUpdateSuccess === false) {
          warning('Payment recorded but balance update had issues. Please verify employee balance.', {
            title: 'Balance Update Warning',
            duration: 6000
          });
        }
        
        success(`₹${paymentAmountNum.toLocaleString('en-IN')} payment recorded and added to ${employee.name}'s cash flow`, {
          title: 'Payment & Cash Flow Recorded',
          details: `Receipt #${receiptNum} • ${employee.name} • Balance: ₹${result.newBalance?.total?.toLocaleString('en-IN') || 'N/A'}`,
          duration: 5000
        });
      } else {
        // Rollback: Delete payment record if employee transaction failed
        if (paymentDocRef) {
          try {
            await deleteDoc(paymentDocRef);
            console.log('Payment record rolled back due to employee transaction failure');
          } catch (rollbackError) {
            console.error('Error rolling back payment record:', rollbackError);
          }
        }
        error(result.error || 'Failed to record cash flow. Payment was not processed.', {
          title: 'Transaction Failed',
          duration: 6000
        });
      }
    } catch (err) {
      console.error('Error recording quick cash flow:', err);
      error('Failed to record cash flow: ' + err.message);
    }
  };

  const handleSavePayment = async () => {
    if (!selectedBillForPayment || !paymentAmount) {
      warning('Please fill in amount field.', { title: 'Validation Error' });
      return;
    }

    try {
      // Ensure every payment has a receipt number - auto-generate if empty
      let effectiveReceiptNumber = (receiptNumber || '').trim();
      if (!effectiveReceiptNumber) {
        effectiveReceiptNumber = await generateReceiptNumber();
        setReceiptNumber(effectiveReceiptNumber);
      }

      const paymentAmountNum = parseFloat(paymentAmount);
      const billOutstanding = getBillOutstanding(selectedBillForPayment);
      
      // Check if payment amount exceeds bill outstanding
      if (paymentAmountNum > billOutstanding) {
        error(`Payment amount (${formatCurrency(paymentAmountNum, companyCurrency)}) cannot exceed the bill's outstanding amount (${formatCurrency(billOutstanding, companyCurrency)}). Please use the Payments page for amounts greater than the bill outstanding.`);
        return;
      }
      
      // First, try to allocate available advance
      const availableAdvance = getPartyAdvance(selectedBillForPayment.party, payments);
      let advanceAllocations = [];
      let advanceUsed = 0;
      let remainingPaymentAmount = paymentAmountNum;
      
      if (availableAdvance > 0) {
        // Allocate advance to this bill
        const advanceResult = allocateAdvanceToBill(selectedBillForPayment.party, billOutstanding, payments);
        if (advanceResult.allocatedAdvance > 0) {
          advanceAllocations = advanceResult.advanceAllocations;
          advanceUsed = advanceResult.allocatedAdvance;
          remainingPaymentAmount = paymentAmountNum - advanceUsed;
          
          // Mark advances as used
          if (Array.isArray(advanceResult.advanceAllocations) && advanceResult.advanceAllocations.length > 0) {
            await markAdvanceUsed(advanceResult.advanceAllocations, db, appId, userId, payments, companyId);
          }
        }
      }
      
      // Simple bill payment - no excess amount handling since it's restricted above
      const paymentData = {
        receiptNumber: effectiveReceiptNumber,
        paymentDate: paymentDate,
        partyId: selectedBillForPayment.party,
        partyName: getPartyName(selectedBillForPayment.party),
        totalAmount: paymentAmountNum,
        paymentMode: paymentMode,
        reference: paymentReference,
        notes: paymentNotes,
        type: 'purchase',
        paymentType: 'bill',
        billId: selectedBillForPayment.id,
        billNumber: selectedBillForPayment.number,
        allocations: [{
          billType: 'purchase',
          billId: selectedBillForPayment.id,
          billNumber: selectedBillForPayment.number,
          allocatedAmount: paymentAmountNum,
          billOutstanding: billOutstanding,
          isFullPayment: paymentAmountNum >= billOutstanding
        }],
        remainingAmount: 0, // No remaining amount since payment cannot exceed bill outstanding
        fifoAllocationUsed: 0,
        advanceAllocations: advanceAllocations,
        advanceUsed: advanceUsed,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      if (selectedBillForPayment?.workOrderId) {
        appendWorkOrderFields(paymentData, selectedBillForPayment);
      }

      // Add to payments collection
      const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
      const docRef = await addDoc(paymentsRef, paymentData);
      if (isPaymentOutflow(paymentData) && docRef?.id) {
        await syncLedgerFromSource(db, companyCtx, {
          sourceType: LEDGER_SOURCE_TYPES.payment_out,
          sourceCollection: 'payments',
          sourceId: docRef.id,
          doc: { ...paymentData, id: docRef.id },
        });
      }
      
      // Record in employee cash flow if enabled
      if (recordInEmployeeCashFlow && selectedEmployeeForCashFlow) {
        try {
          const selectedEmployee = employees?.find(emp => emp.id === selectedEmployeeForCashFlow);
          if (selectedEmployee) {
            if ((selectedEmployee.status || 'active') !== 'active') {
              warning('Selected employee is inactive. Please choose an active employee.', {
                title: 'Inactive Employee',
                duration: 5000
              });
              return;
            }
            // For purchases, making payment is outflow (employee pays money)
            const transactionType = 'outflow';
            
            // Map paymentMode to cash/upi
            let mappedPaymentMode = 'cash';
            const paymentModeStr = String(paymentMode || '').toLowerCase().trim();
            if (paymentModeStr === 'upi' || paymentModeStr.includes('upi') || paymentModeStr === 'unified payments interface') {
              mappedPaymentMode = 'upi';
            } else if (paymentModeStr === 'cash' || paymentModeStr.includes('cash')) {
              mappedPaymentMode = 'cash';
            }
            
            const companyData = {
              companyId: currentCompany?.companyId,
              userId: currentCompany?.userId,
              appId: currentCompany?.appId
            };
            
            const employeeTransactionData = {
              employeeId: selectedEmployeeForCashFlow,
              employeeName: selectedEmployee.name || '',
              transactionType,
              amount: paymentAmountNum, // Use the actual payment amount
              paymentMode: mappedPaymentMode,
              description: `Payment ${paymentData.receiptNumber} - ${paymentData.partyName}`,
              referenceNumber: paymentData.receiptNumber,
              date: paymentData.paymentDate,
              category: 'Payment',
              createdBy: {
                userId: userId || currentCompany?.userId || '',
                name: currentCompany?.userName || 'User'
              },
              linkedPaymentId: docRef.id, // Link to payment record
              linkedReceiptNumber: paymentData.receiptNumber, // Link to receipt number
              ...(mappedPaymentMode === 'cash' && {
                cashGivenTo: paymentData.partyName
              }),
              ...(mappedPaymentMode === 'upi' && {
                upiId: selectedEmployee.paymentPreferences?.upiId || '',
                transactionId: paymentData.reference || '',
                upiApp: selectedEmployee.paymentPreferences?.upiApp || 'Other'
              })
            };
            
            const employeeResult = await EmployeeCashManager.createCashTransaction(
              companyData, 
              employeeTransactionData
            );
            
            if (employeeResult.success) {
              success('Payment recorded in employee cash flow', {
                title: 'Employee Transaction Created',
                duration: 4000
              });
            } else {
              warning(employeeResult.error || 'Failed to record in employee cash flow', {
                title: 'Employee Transaction Warning',
                duration: 5000
              });
            }
          }
        } catch (employeeError) {
          console.error('Error recording employee cash flow transaction:', employeeError);
          warning('Payment saved but failed to record in employee cash flow: ' + (employeeError.message || 'Unknown error'), {
            title: 'Employee Transaction Warning',
            duration: 5000
          });
        }
      }
      
      // Reset payment form
      setPaymentAmount('');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setPaymentMode('Cash');
      setPaymentReference('');
      setPaymentNotes('');
      setReceiptNumber('');
      setSelectedBillForPayment(null);
      setRecordInEmployeeCashFlow(false);
      setSelectedEmployeeForCashFlow('');
      setShowPaymentModal(false);
      
      paymentSuccess(
        `Payment of ${formatCurrency(paymentAmountNum, companyCurrency)} has been added successfully!`,
        {
          title: 'Payment Added Successfully',
          details: `Receipt #${effectiveReceiptNumber} • ${getPartyName(selectedBillForPayment.party)} • ${paymentMode}`,
          duration: 6000
        }
      );
    } catch (err) {
      console.error('Error saving payment:', err);
      error('Error saving payment. Please try again.');
    }
  };

  // FIFO Payment Allocation Logic
  const allocatePaymentFIFO = (partyId, paymentAmount) => {
    try {
      if (!partyId || !paymentAmount) {
        return { allocations: [], remainingAmount: paymentAmount };
      }
      
      // Filter bills for this party with outstanding > 0
      const partyBills = bills.filter(bill => 
        (bill.partyId === partyId || bill.party === partyId) && 
        (bill.outstanding || 0) > 0
      );
      
      // Sort by date (FIFO - oldest first)
      partyBills.sort((a, b) => {
        const dateA = new Date(a.billDate || a.date || 0);
        const dateB = new Date(b.billDate || b.date || 0);
        return dateA - dateB;
      });
      
      // Check if there's available advance
      const availableAdvance = getPartyAdvance(partyId, payments);
      let remainingAmount = paymentAmount;
      const allocations = [];
      
      // Apply advance first if available
      if (availableAdvance > 0 && partyBills.length > 0) {
        const advanceResult = applyAdvanceToBillsUtil(partyId, partyBills, payments);
        
        if (advanceResult.totalApplied > 0) {
          advanceResult.allocations.forEach(advanceAlloc => {
            const bill = partyBills.find(b => b.id === advanceAlloc.billId);
            if (bill) {
              allocations.push({
                billType: docType,
                billId: bill.id,
                billNumber: bill.number || bill.billNumber,
                allocatedAmount: advanceAlloc.amountApplied,
                billOutstanding: advanceAlloc.billOutstanding,
                isFullPayment: advanceAlloc.amountApplied >= advanceAlloc.billOutstanding,
                isAdvanceAllocation: true,
                advancePaymentId: advanceAlloc.advancePaymentId
              });
              bill.outstanding = Math.max(0, bill.outstanding - advanceAlloc.amountApplied);
            }
          });
        }
      }
      
      // Now allocate the current payment amount
      for (const bill of partyBills) {
        if (remainingAmount <= 0) break;
        
        const originalBillAmount = bill.totalAmount || bill.amount || 0;
        const existingAdvanceAllocation = allocations.find(alloc => alloc.billId === bill.id && alloc.isAdvanceAllocation);
        const remainingBillOutstanding = bill.outstanding || 0;
        
        if (remainingBillOutstanding <= 0) continue;
        
        const allocatedAmount = Math.min(remainingAmount, remainingBillOutstanding);
        
        if (allocatedAmount > 0) {
          if (existingAdvanceAllocation) {
            existingAdvanceAllocation.allocatedAmount += allocatedAmount;
            existingAdvanceAllocation.isFullPayment = existingAdvanceAllocation.allocatedAmount >= originalBillAmount;
            existingAdvanceAllocation.isAdvanceAllocation = false;
          } else {
            allocations.push({
              billType: docType,
              billId: bill.id,
              billNumber: bill.number || bill.billNumber,
              allocatedAmount: allocatedAmount,
              billOutstanding: originalBillAmount,
              isFullPayment: allocatedAmount >= originalBillAmount,
              isAdvanceAllocation: false
            });
          }
          
          bill.outstanding = Math.max(0, remainingBillOutstanding - allocatedAmount);
          remainingAmount -= allocatedAmount;
        }
      }
      
      return { allocations, remainingAmount };
    } catch (error) {
      console.error('Error in allocatePaymentFIFO:', error);
      return { allocations: [], remainingAmount: paymentAmount };
    }
  };

  // Handle FIFO Payment
  const handleFIFOPayment = async () => {
    if (!selectedPartyForFIFO || !fifoPaymentAmount) {
      warning('Please select a party and enter payment amount.');
      return;
    }

    try {
      const paymentAmountNum = parseFloat(fifoPaymentAmount);
      if (isNaN(paymentAmountNum) || paymentAmountNum <= 0) {
        warning('Please enter a valid payment amount.');
        return;
      }

      if (!fifoReceiptNumber.trim()) {
        warning('Please enter a receipt number.');
        return;
      }

      // Get available advance
      const availableAdvance = getPartyAdvance(selectedPartyForFIFO, payments);
      let advanceAllocations = [];
      let advanceUsed = 0;
      let totalAmountToAllocate = paymentAmountNum;

      // Apply advance if available
      if (availableAdvance > 0) {
        const outstandingBills = bills.filter(bill => 
          (bill.partyId === selectedPartyForFIFO || bill.party === selectedPartyForFIFO) && 
          (bill.outstanding || 0) > 0
        );
        
        if (outstandingBills.length > 0) {
          const advanceResult = applyAdvanceToBillsUtil(selectedPartyForFIFO, outstandingBills, payments);
          if (advanceResult.totalApplied > 0) {
            advanceAllocations = advanceResult.advanceAllocations || [];
            advanceUsed = advanceResult.totalApplied;
            await markAdvanceUsed(advanceAllocations, db, appId, userId, payments, companyId);
          }
        }
      }

      // Allocate payment using FIFO
      const fifoResult = allocatePaymentFIFO(selectedPartyForFIFO, totalAmountToAllocate);
      
      if (!fifoResult || !Array.isArray(fifoResult.allocations)) {
        throw new Error('Failed to allocate payment. Please try again.');
      }

      const { allocations, remainingAmount } = fifoResult;

      const paymentData = {
        receiptNumber: fifoReceiptNumber,
        paymentDate: fifoPaymentDate,
        partyId: selectedPartyForFIFO,
        partyName: getPartyName(selectedPartyForFIFO),
        totalAmount: totalAmountToAllocate,
        paymentMode: fifoPaymentMode,
        reference: fifoPaymentReference,
        notes: fifoPaymentNotes,
        type: docType,
        paymentType: 'khata',
        allocations: allocations,
        remainingAmount: remainingAmount,
        advanceAllocations: advanceAllocations,
        advanceUsed: advanceUsed,
        originalPaymentAmount: paymentAmountNum,
        advanceAmount: availableAdvance,
        fifoAllocationUsed: remainingAmount > 0 ? 0 : totalAmountToAllocate,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
      const docRef = await addDoc(paymentsRef, paymentData);
      
      // Record in employee cash flow if enabled
      if (fifoRecordInEmployeeCashFlow && fifoSelectedEmployeeForCashFlow) {
        try {
          const selectedEmployee = employees?.find(emp => emp.id === fifoSelectedEmployeeForCashFlow);
          if (selectedEmployee) {
            if ((selectedEmployee.status || 'active') !== 'active') {
              warning('Selected employee is inactive. Please choose an active employee.', {
                title: 'Inactive Employee',
                duration: 5000
              });
              return;
            }
            // For purchases, making payment is outflow (employee pays money)
            const transactionType = 'outflow';
            
            // Map paymentMode to cash/upi
            let mappedPaymentMode = 'cash';
            const paymentModeStr = String(fifoPaymentMode || '').toLowerCase().trim();
            if (paymentModeStr === 'upi' || paymentModeStr.includes('upi') || paymentModeStr === 'unified payments interface') {
              mappedPaymentMode = 'upi';
            } else if (paymentModeStr === 'cash' || paymentModeStr.includes('cash')) {
              mappedPaymentMode = 'cash';
            }
            
            const companyData = {
              companyId: currentCompany?.companyId,
              userId: currentCompany?.userId,
              appId: currentCompany?.appId
            };
            
            const employeeTransactionData = {
              employeeId: fifoSelectedEmployeeForCashFlow,
              employeeName: selectedEmployee.name || '',
              transactionType,
              amount: paymentAmountNum, // Use the actual payment amount
              paymentMode: mappedPaymentMode,
              description: `FIFO Payment ${paymentData.receiptNumber} - ${paymentData.partyName}`,
              referenceNumber: paymentData.receiptNumber,
              date: paymentData.paymentDate,
              category: 'Payment',
              createdBy: {
                userId: userId || currentCompany?.userId || '',
                name: currentCompany?.userName || 'User'
              },
              linkedPaymentId: docRef.id, // Link to payment record
              linkedReceiptNumber: paymentData.receiptNumber, // Link to receipt number
              ...(mappedPaymentMode === 'cash' && {
                cashGivenTo: paymentData.partyName
              }),
              ...(mappedPaymentMode === 'upi' && {
                upiId: selectedEmployee.paymentPreferences?.upiId || '',
                transactionId: paymentData.reference || '',
                upiApp: selectedEmployee.paymentPreferences?.upiApp || 'Other'
              })
            };
            
            const employeeResult = await EmployeeCashManager.createCashTransaction(
              companyData, 
              employeeTransactionData
            );
            
            if (employeeResult.success) {
              success('Payment recorded in employee cash flow', {
                title: 'Employee Transaction Created',
                duration: 4000
              });
            } else {
              warning(employeeResult.error || 'Failed to record in employee cash flow', {
                title: 'Employee Transaction Warning',
                duration: 5000
              });
            }
          }
        } catch (employeeError) {
          console.error('Error recording employee cash flow transaction:', employeeError);
          warning('Payment saved but failed to record in employee cash flow: ' + (employeeError.message || 'Unknown error'), {
            title: 'Employee Transaction Warning',
            duration: 5000
          });
        }
      }

      // Reset form
      setFifoPaymentAmount('');
      setFifoPaymentDate(new Date().toISOString().split('T')[0]);
      setFifoPaymentMode('Cash');
      setFifoPaymentReference('');
      setFifoPaymentNotes('');
      setFifoReceiptNumber('');
      setSelectedPartyForFIFO('');
      setFifoRecordInEmployeeCashFlow(false);
      setFifoSelectedEmployeeForCashFlow('');
      setShowFIFOPaymentModal(false);

      success(
        `FIFO Payment of ${formatCurrency(paymentAmountNum, companyCurrency)} added successfully!`,
        {
          title: 'FIFO Payment Added',
          details: `Receipt #${fifoReceiptNumber} • ${getPartyName(selectedPartyForFIFO)} • ${fifoPaymentMode}`,
          duration: 6000
        }
      );
    } catch (err) {
      console.error('Error saving FIFO payment:', err);
      error('Error saving FIFO payment. Please try again.', { title: 'Payment Failed' });
    }
  };

  // Open FIFO Payment Modal
  const handleOpenFIFOPayment = async () => {
    const receiptNum = await generateReceiptNumber();
    setFifoReceiptNumber(receiptNum);
    setFifoPaymentDate(new Date().toISOString().split('T')[0]);
    setShowFIFOPaymentModal(true);
  };

  // Totals - Calculate based on party GST type
  // Filter out empty rows for display calculations
  const filledRowsForDisplay = rows.filter(row => row.item && row.item.trim() !== '');
  const subtotal = roundTo2Decimals(filledRowsForDisplay.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0));
  
  // Get party GST type to determine GST calculation
  const partyObj = parties.find(p => p.id === party);
  const partyGstType = partyObj ? (partyObj.gstType || 'Unregistered') : 'Unregistered';
  
  let totalSGST = 0, totalCGST = 0, totalIGST = 0, grandTotal = 0;
  
  if (partyGstType === 'Regular') {
    // Calculate GST only for Regular parties
    totalSGST = filledRowsForDisplay.reduce((sum, row) => sum + calculateGSTAmount(row.amount, row.sgst), 0);
    totalCGST = filledRowsForDisplay.reduce((sum, row) => sum + calculateGSTAmount(row.amount, row.cgst), 0);
    totalIGST = filledRowsForDisplay.reduce((sum, row) => sum + calculateGSTAmount(row.amount, row.igst), 0);
    grandTotal = roundTo2Decimals(subtotal + totalSGST + totalCGST + totalIGST);
  } else {
    // For Composition/Unregistered parties, no GST calculation
    totalSGST = 0;
    totalCGST = 0;
    totalIGST = 0;
    grandTotal = roundTo2Decimals(subtotal); // Total equals subtotal (no GST added)
  }

  // Add print and PDF download handlers (copied from Sales.js, adapted):
  const handleInvoicePrint = async () => {
    if (!invoiceRef.current || !invoiceBill) {
      return;
    }
    
    try {
      const temp = document.createElement('div');
      temp.innerHTML = invoiceRef.current.innerHTML;
      await inlineImagesForPrint(temp);
      
      const printContents = temp.innerHTML;
      const printWindow = window.open('', '', 'height=800,width=1000');
      
      if (!printWindow) {
        console.error('Print: Failed to open print window');
        warning('Please allow popups to print this document', { title: 'Print Error' });
        return;
      }
      
      printWindow.document.write('<html><head><title>Purchase Bill</title>');
      printWindow.document.write('<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/tailwindcss/dist/tailwind.min.css">');
      printWindow.document.write('<style>');
      printWindow.document.write('html,body{margin:0;padding:0;background:#fff;}');
      printWindow.document.write('@media print{html,body{width:210mm!important;max-width:210mm!important;box-sizing:border-box!important;}}');
      printWindow.document.write('@page{size:A4;margin:8mm;}');
      printWindow.document.write('.print\\:hidden { display: none !important; }');
      printWindow.document.write('@media print { .print\\:hidden { display: none !important; } }');
      printWindow.document.write('.print\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }');
      printWindow.document.write('.print\\:bg-white { background-color: white !important; }');
      printWindow.document.write('.print\\:border { border-width: 1px !important; }');
      printWindow.document.write('.print\\:border-gray-300 { border-color: #d1d5db !important; }');
      printWindow.document.write('.print\\:flex-row { flex-direction: row !important; }');
      printWindow.document.write('.print\\:bg-yellow-100 { background-color: #fef3c7 !important; }');
      printWindow.document.write('.print\\:bg-green-100 { background-color: #dcfce7 !important; }');
      printWindow.document.write('.print\\:transform-none { transform: none !important; }');
      printWindow.document.write('@media print { .grid { display: grid !important; } }');
      printWindow.document.write('@media print { .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; } }');
      printWindow.document.write('@media print { .flex { display: flex !important; } }');
      printWindow.document.write('@media print { .flex-row { flex-direction: row !important; } }');
      printWindow.document.write('@media print { [class*="print:hidden"] { display: none !important; } }');
      printWindow.document.write('@media print { button { display: none !important; } }');
      printWindow.document.write('@media print { .sticky { position: static !important; } }');
      printWindow.document.write('</style>');
      printWindow.document.write('</head><body style="background:white;">');
      printWindow.document.write(printContents);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.focus();
      
      const ensureImages = () => {
        try {
          const imgs = Array.from(printWindow.document.images || []);
          if (imgs.length === 0) return Promise.resolve();
          return Promise.all(imgs.map(img => new Promise(resolve => {
            if (img.complete && img.naturalWidth > 0) return resolve();
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          })));
        } catch (_) {
          return Promise.resolve();
        }
      };
      
      printWindow.onload = async () => {
        try {
          await ensureImages();
          setTimeout(() => {
            printWindow.print();
            printWindow.close();
          }, 300);
        } catch (error) {
          console.error('Print: Error in print window:', error);
          printWindow.close();
        }
      };
      
    } catch (error) {
      error('Error preparing print. Please try again.', { title: 'Print Error' });
    }
  };

  // Print function for success modal
  const handleSuccessModalPrint = () => {
    const bill = bills.find(b => b.id === savedBillId);
    if (!bill) return;
    
    // Enhance bill data with proper measurement units
    const enhancedBill = {
      ...bill,
      rows: (bill.rows || []).map(row => {
        if (row.item) {
          const itemObj = items.find(it => it.id === row.item);
          if (itemObj) {
            return {
              ...row,
              description: itemObj.itemName || row.description || '',
              unit: itemObj.quantityMeasurement || row.unit || '',
            };
          }
        }
        return row;
      }),
    };
    
    setInvoiceBill(enhancedBill);
    setShowInvoiceModal(true);
    setInvoiceZoom(getBillPreviewInitialZoom());
    setShowSuccessModal(false);
  };

  // Reset form for creating new bill
  const resetFormToInitialState = () => {
    setBillNumber('');
    setBillDate(new Date().toISOString().split('T')[0]);
    setParty('');
    setNotes('');
    setRows(Array.from({ length: 5 }, () => ({ ...initialItemRow })));
    setCustomFields({ ewayBillNo: '', ewayQr: '', ewayDate: '' });
    setCurrentMobileItemIndex(0);
    setEditingBillId(null);
    setItemSearchTextByRow({});
  };

  useCompanySwitchGuard({
    isDirty: () =>
      !!editingBillId ||
      !!party ||
      rows.some(
        (r) =>
          !!(r.item && String(r.item).trim()) ||
          !!(r.itemId && String(r.itemId).trim()) ||
          (parseFloat(r.qty) || 0) > 0
      ),
    onSwitch: () => {
      clearBillForm();
      resetFormToInitialState();
    },
    notify: (message) => warning(message, { title: 'Company switched' }),
  });

  // useEffect to trigger form reset when success modal is shown
  useEffect(() => {
    if (showSuccessModal) {
      // Small delay to ensure modal is fully rendered before resetting
      const timer = setTimeout(() => {
        resetFormToInitialState();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal]);

  // PDF download handler
  const handleInvoiceDownload = async () => {
    if (!invoiceRef.current || !invoiceBill) {
      warning('Document not ready. Please wait for the document to load.', { title: 'Document Not Ready' });
      return;
    }
    
    // Check if we have the required content
    if (!invoiceRef.current.innerHTML || invoiceRef.current.innerHTML.length < 100) {
      warning('Document content is not ready. Please wait for the document to load completely.', { title: 'Document Not Ready' });
      return;
    }
    
    // Show loading state
    const button = document.querySelector('button[onClick="handleInvoiceDownload"]') || 
                  document.querySelector('button[onclick="handleInvoiceDownload"]') ||
                  document.querySelector('[onClick="handleInvoiceDownload"]') ||
                  document.querySelector('[onclick="handleInvoiceDownload"]');
    
    let originalText = 'Save as PDF';
    if (button) {
      originalText = button.textContent || 'Save as PDF';
      button.textContent = 'Generating PDF...';
      button.disabled = true;
    }
    
    try {
      // Preload images before PDF generation
      const companyDetails = {
        logoUrl: currentCompany?.logoUrl || currentCompany?.details?.logoUrl,
        upiQrUrl: currentCompany?.upiQrUrl || currentCompany?.details?.upiQrUrl,
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signUrl || currentCompany?.details?.signUrl
      };
      
      const bankDetails = {
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signUrl || currentCompany?.details?.signUrl
      };
      
      console.log('Preloading images for PDF generation...');
      await preloadBillImages(companyDetails, bankDetails);
      
      // Check if html2pdf is available
      let html2pdf = window.html2pdf;
      
      if (!html2pdf) {
        try {
          // Try to load html2pdf
          const module = await import('html2pdf.js');
          html2pdf = module.default || module;
          window.html2pdf = html2pdf; // Store for future use
        } catch (importError) {
          // Try to load from CDN as fallback
          try {
            await new Promise((resolve, reject) => {
              const script = document.createElement('script');
              script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
              script.onload = () => {
                html2pdf = window.html2pdf;
                resolve();
              };
              script.onerror = reject;
              document.head.appendChild(script);
            });
          } catch (cdnError) {
            html2pdf = null; // Will trigger jsPDF fallback
          }
        }
      }
      
      // Test if html2pdf is working
      if (html2pdf && typeof html2pdf !== 'function') {
        html2pdf = null; // Will trigger jsPDF fallback
      }
      
      // Wait a bit for any dynamic content to render
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Create a temporary container and clone the content
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = invoiceRef.current.innerHTML;
      
      if (tempContainer.innerHTML.length < 100) {
        warning('Document content is empty. Please try again.', { title: 'Document Error' });
        return;
      }
      
      // Hide print:hidden elements
      const hiddenElements = tempContainer.querySelectorAll('.print\\:hidden, [class*="print:hidden"]');
      hiddenElements.forEach(el => el.style.display = 'none');

      // Fast image processing for PDF generation
      const imgs = Array.from(tempContainer.querySelectorAll('img'));
      
      const imagePromises = imgs.map(async (img) => {
        const src = img.getAttribute('src');
        if (!src) return;
        
        try {
          const imageData = await convertImageToDataUrl(src);
          if (imageData) {
            img.setAttribute('src', imageData);
            img.removeAttribute('crossorigin');
          } else if (window.imageManager) {
            const imageRecord = await window.imageManager.getImage(src);
            if (imageRecord?.data) {
              img.setAttribute('src', imageRecord.data);
              img.removeAttribute('crossorigin');
            }
          }
        } catch (error) {
          img.style.display = 'none';
        }
      });
      
      // Process all images in parallel
      await Promise.all(imagePromises);
      
      // Add delay to ensure images are fully loaded and DOM is ready for PDF generation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Generate filename
      const fmt = (d) => {
        try {
          const x = new Date(d);
          const day = String(x.getDate());
          const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][x.getMonth()];
          const yr = x.getFullYear();
          return `${day}${mon}${yr}`;
        } catch {
          return d;
        }
      };
      
      const filename = `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document').toUpperCase()}_${invoiceBill?.number || 'Bill'}_${fmt(invoiceBill?.billDate || invoiceBill?.invoiceDate || invoiceBill?.date)}.pdf`;
      
      // Generate PDF with optimized settings
      if (html2pdf) {
        const worker = html2pdf().from(tempContainer).set({
          margin: 0.5,
          filename: filename,
          html2canvas: { 
            scale: 2, 
            useCORS: true, 
            allowTaint: true,
            backgroundColor: '#ffffff',
            imageTimeout: 8000,
            removeContainer: true
          },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
        });
        
        await worker.save();
      } else {
        // Fallback to jsPDF
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF('p', 'in', 'a4');
        
        // Simple text-based PDF as fallback
        doc.setFontSize(16);
        doc.text(`${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill?.number || 'Bill'}`, 1, 1);
        
        doc.setFontSize(12);
        doc.text(`Date: ${invoiceBill?.billDate || invoiceBill?.invoiceDate || invoiceBill?.date || 'N/A'}`, 1, 1.5);
        doc.text(`Party: ${parties.find(p => p.id === invoiceBill?.party)?.firmName || 'N/A'}`, 1, 2);
        doc.text(`Total Amount: ${formatCurrency(invoiceBill?.amount || 0, companyCurrency)}`, 1, 2.5);
        
        // Add items
        let yPos = 3;
        (invoiceBill?.rows || invoiceBill?.items || []).forEach((item, index) => {
          const itemName = resolveBillLineItemName(item, items, item.item || 'Unknown Item');
          doc.text(`${index + 1}. ${itemName} - Qty: ${item.qty}, Rate: ${formatCurrency(item.rate, companyCurrency)}, Amount: ${formatCurrency(item.amount, companyCurrency)}`, 1, yPos);
          yPos += 0.3;
        });
        
        doc.save(filename);
      }
          } catch (err) {
        console.error('PDF Download: Error generating PDF:', err);
        const errorMessage = `Error generating PDF: ${err.message || err.toString()}. Please try again.`;
        console.error('PDF Download: Full error details:', errorMessage);
        error(errorMessage, { title: 'PDF Generation Failed', duration: 7000 });
      } finally {
        // Restore button state
        if (button) {
          button.textContent = originalText;
          button.disabled = false;
        }
      }
  };

  // Share: link to open this purchase bill directly in preview

  const handleInvoiceWheel = (e) => {
    // Only zoom when Ctrl key is held down, otherwise allow normal scrolling
    if (e.ctrlKey && showInvoiceModal) {
      e.preventDefault();
      setInvoiceZoom(z => {
        let next = z + (e.deltaY < 0 ? 0.1 : -0.1);
        next = Math.max(0.5, Math.min(2, next));
        return Math.round(next * 100) / 100;
      });
    }
    // If Ctrl is not held, let the browser handle normal scrolling
  };

  // Optimized PDF Share Handler - generates PDF as blob and shares via system
  const handleInvoiceSharePdf = async () => {
    console.log('PDF Share: Starting PDF generation...');
    
    if (!invoiceRef.current || !invoiceBill) {
      console.error('PDF Share: Missing invoiceRef or invoiceBill');
      warning('Document not ready. Please wait for the document to load.', { title: 'Document Not Ready' });
      return;
    }
    
    // Check if we have the required content
    if (!invoiceRef.current.innerHTML || invoiceRef.current.innerHTML.length < 100) {
      console.error('PDF Share: Document content not ready');
      warning('Document content is not ready. Please wait for the document to load completely.', { title: 'Document Not Ready' });
      return;
    }
    
    console.log('PDF Share: Document content ready, proceeding...');
    
    // Show loading state
    const shareButton = document.querySelector('button[onClick="handleInvoiceSharePdf"]') ||
                       document.querySelector('button[onclick="handleInvoiceSharePdf"]') ||
                       document.querySelector('[onClick="handleInvoiceSharePdf"]') ||
                       document.querySelector('[onclick="handleInvoiceSharePdf"]') ||
                       document.querySelector('.PurchaseShareButton button') ||
                       Array.from(document.querySelectorAll('button')).find(btn => btn.textContent.includes('Share'));
    
    let originalText = 'Share as PDF';
    if (shareButton) {
      originalText = shareButton.textContent || 'Share as PDF';
      shareButton.textContent = 'Generating PDF...';
      shareButton.disabled = true;
      console.log('PDF Share: Button state updated');
    } else {
      console.warn('PDF Share: Could not find share button to update');
    }
    
    let tempContainer;
    try {
      // Preload images before PDF generation
      const companyDetails = {
        logoUrl: currentCompany?.logoUrl || currentCompany?.details?.logoUrl,
        upiQrUrl: currentCompany?.upiQrUrl || currentCompany?.details?.upiQrUrl,
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signUrl || currentCompany?.details?.signUrl
      };
      
      const bankDetails = {
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signUrl || currentCompany?.details?.signUrl
      };
      
      console.log('Preloading images for PDF share...');
      await preloadBillImages(companyDetails, bankDetails);
      
      // Check if html2pdf is available
      let html2pdf = window.html2pdf;
      console.log('PDF Share: html2pdf available:', !!html2pdf);
      
      if (!html2pdf) {
        console.log('PDF Share: html2pdf not found, attempting to load...');
        try {
          // Try to load html2pdf
          const module = await import('html2pdf.js');
          html2pdf = module.default || module;
          window.html2pdf = html2pdf; // Store for future use
          console.log('PDF Share: html2pdf loaded from module');
        } catch (importError) {
          console.log('PDF Share: Module import failed, trying CDN...');
          // Try to load from CDN as fallback
          try {
            await new Promise((resolve, reject) => {
              const script = document.createElement('script');
              script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
              script.onload = () => {
                html2pdf = window.html2pdf;
                console.log('PDF Share: html2pdf loaded from CDN');
                resolve();
              };
              script.onerror = reject;
              document.head.appendChild(script);
            });
          } catch (cdnError) {
            console.error('PDF Share: CDN loading failed:', cdnError);
            error('PDF generation library not available. Please try again.', { title: 'PDF Error' });
            return;
          }
        }
      }
      
      // Test if html2pdf is working
      if (html2pdf && typeof html2pdf !== 'function') {
        console.error('PDF Share: html2pdf is not a function');
        error('PDF generation library not available. Please try again.', { title: 'PDF Error' });
        return;
      }
      
      console.log('PDF Share: html2pdf is ready, creating container...');
      
      // Create a temporary container and clone the existing content; attach offscreen for proper layout and image loading
      tempContainer = document.createElement('div');
      tempContainer.innerHTML = invoiceRef.current.innerHTML;
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.backgroundColor = '#ffffff';
      tempContainer.style.width = '210mm';
      document.body.appendChild(tempContainer);
      
      if (tempContainer.innerHTML.length < 100) {
        console.error('PDF Share: Cloned content is empty');
        warning('Document content is empty. Please try again.', { title: 'Document Error' });
        return;
      }
      
      console.log('PDF Share: Content cloned successfully, processing images...');
      
      // Hide print:hidden elements
      const hiddenElements = tempContainer.querySelectorAll('.print\\:hidden, [class*="print:hidden"]');
      hiddenElements.forEach(el => el.style.display = 'none');

      // Fast image processing for PDF generation
      const imgs = Array.from(tempContainer.querySelectorAll('img'));
      console.log('PDF Share: Found', imgs.length, 'images to process');
      
      const imagePromises = imgs.map(async (img) => {
        const src = img.getAttribute('src');
        if (!src) return;
        
        try {
          const imageData = await convertImageToDataUrl(src);
          if (imageData) {
            img.setAttribute('src', imageData);
            img.removeAttribute('crossorigin');
          } else if (window.imageManager) {
            const imageRecord = await window.imageManager.getImage(src);
            if (imageRecord?.data) {
              img.setAttribute('src', imageRecord.data);
              img.removeAttribute('crossorigin');
            }
          }
        } catch (error) {
          console.warn('PDF Share: Image processing failed for', src, error);
          img.style.display = 'none';
        }
      });
      
      // Process all images in parallel
      await Promise.all(imagePromises);
      console.log('PDF Share: Images processed, generating PDF...');
      
      // Generate filename
      const fmt = (d) => {
        try {
          const x = new Date(d);
          const day = String(x.getDate());
          const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][x.getMonth()];
          const yr = x.getFullYear();
          return `${day}${mon}${yr}`;
        } catch {
          return d;
        }
      };
      
      const filename = `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document').toUpperCase()}_${invoiceBill?.number || 'Bill'}_${fmt(invoiceBill?.billDate || invoiceBill?.invoiceDate || invoiceBill?.date)}.pdf`;
      console.log('PDF Share: Filename generated:', filename);
      
      // Generate PDF with optimized settings
      const worker = html2pdf().from(tempContainer).set({
        margin: 0.5,
        filename: filename,
        html2canvas: { 
          scale: 2, 
          useCORS: true, 
          allowTaint: true,
          backgroundColor: '#ffffff',
          imageTimeout: 10000, // Increased timeout for mobile
          removeContainer: true,
          logging: false, // Disable logging for better performance
          foreignObjectRendering: true,
          scrollX: 0,
          scrollY: 0,
          windowWidth: tempContainer.scrollWidth,
          windowHeight: tempContainer.scrollHeight
        },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
      });
      
      console.log('PDF Share: PDF worker created, generating blob...');
      const pdfBlob = await worker.outputPdf('blob');
      console.log('PDF Share: PDF blob generated, size:', pdfBlob.size);
      
      // Share PDF via system share or download
      try {
        if (navigator.canShare && navigator.canShare({ files: [new File([pdfBlob], filename, { type: 'application/pdf' })] })) {
          console.log('PDF Share: Attempting system share...');
          await navigator.share({
            title: `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill?.number || 'Bill'}`,
            text: `Sharing ${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill?.number || 'Bill'}`,
            files: [new File([pdfBlob], filename, { type: 'application/pdf' })]
          });
          console.log('PDF Share: System share successful');
        } else {
          console.log('PDF Share: System share not available, downloading...');
          // Download fallback
          const url = URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(url);
          console.log('PDF Share: Download successful');
        }
      } catch (shareError) {
        console.warn('PDF Share: System share failed, downloading instead:', shareError);
        // Download fallback if sharing fails
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
        console.log('PDF Share: Download fallback successful');
      }
      
      console.log('PDF Share: PDF generation completed successfully');
    } catch (err) {
      console.error('PDF Share: Error generating PDF:', err);
      const errorMessage = `Error generating PDF: ${err.message || err.toString()}. Please try again.`;
      error(errorMessage, { title: 'PDF Generation Failed', duration: 7000 });
    } finally {
      try { if (tempContainer && tempContainer.parentNode) document.body.removeChild(tempContainer); } catch {}
      // Restore button state
      if (shareButton) {
        shareButton.textContent = originalText;
        shareButton.disabled = false;
        console.log('PDF Share: Button state restored');
      }
    }
  };

  // Optimized Image Share Handler - generates image as blob and shares via system
  const handleInvoiceShareImage = async () => {
    console.log('Image Share: Starting image generation...');
    
    if (!invoiceRef.current || !invoiceBill) {
      console.error('Image Share: Missing invoiceRef or invoiceBill');
      warning('Document not ready. Please wait for the document to load.', { title: 'Document Not Ready' });
      return;
    }
    
    // Check if we have the required content
    if (!invoiceRef.current.innerHTML || invoiceRef.current.innerHTML.length < 100) {
      console.error('Image Share: Document content not ready');
      warning('Document content is not ready. Please wait for the document to load completely.', { title: 'Document Not Ready' });
      return;
    }
    
    console.log('Image Share: Document content ready, proceeding...');
    
    // Show loading state
    const shareButton = document.querySelector('button[onClick="handleInvoiceShareImage"]') ||
                       document.querySelector('button[onclick="handleInvoiceShareImage"]') ||
                       document.querySelector('[onClick="handleInvoiceShareImage"]') ||
                       document.querySelector('[onclick="handleInvoiceShareImage"]') ||
                       document.querySelector('.PurchaseShareButton button') ||
                       Array.from(document.querySelectorAll('button')).find(btn => btn.textContent.includes('Share'));
    
    let originalText = 'Share as Image';
    if (shareButton) {
      originalText = shareButton.textContent || 'Share as Image';
      shareButton.textContent = 'Generating Image...';
      shareButton.disabled = true;
      console.log('Image Share: Button state updated');
    } else {
      console.warn('Image Share: Could not find share button to update');
    }
    
    let tempContainer;
    try {
      // Preload images before image generation
      const companyDetails = {
        logoUrl: currentCompany?.logoUrl || currentCompany?.details?.logoUrl,
        upiQrUrl: currentCompany?.upiQrUrl || currentCompany?.details?.upiQrUrl,
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signUrl || currentCompany?.details?.signUrl
      };
      
      const bankDetails = {
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signUrl || currentCompany?.details?.signUrl
      };
      
      console.log('Preloading images for image share...');
      await preloadBillImages(companyDetails, bankDetails);
      
      // Check if html2canvas is available
      let html2canvas = window.html2canvas;
      console.log('Image Share: html2canvas available:', !!html2canvas);
      
      if (!html2canvas) {
        console.log('Image Share: html2canvas not found, attempting to load...');
        try {
          // Try to load html2canvas
          const module = await import('html2canvas');
          html2canvas = module.default || module;
          window.html2canvas = html2canvas; // Store for future use
          console.log('Image Share: html2canvas loaded from module');
        } catch (importError) {
          console.log('Image Share: Module import failed, trying CDN...');
          // Try to load from CDN as fallback
          try {
            await new Promise((resolve, reject) => {
              const script = document.createElement('script');
              script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
              script.onload = () => {
                html2canvas = window.html2canvas;
                console.log('Image Share: html2canvas loaded from CDN');
                resolve();
              };
              script.onerror = reject;
              document.head.appendChild(script);
            });
          } catch (cdnError) {
            console.error('Image Share: CDN loading failed:', cdnError);
            error('Image generation library not available. Please try again.', { title: 'Image Error' });
            return;
          }
        }
      }
      
      // Test if html2canvas is working
      if (html2canvas && typeof html2canvas !== 'function') {
        console.error('Image Share: html2canvas is not a function');
        error('Image generation library not available. Please try again.', { title: 'Image Error' });
        return;
      }
      
      console.log('Image Share: html2canvas is ready, creating container...');
      
      // Create a temporary container and clone the existing content; attach offscreen for accurate layout
      tempContainer = document.createElement('div');
      tempContainer.innerHTML = invoiceRef.current.innerHTML;
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.backgroundColor = '#ffffff';
      tempContainer.style.width = '210mm';
      document.body.appendChild(tempContainer);
      
      if (tempContainer.innerHTML.length < 100) {
        console.error('Image Share: Cloned content is empty');
        error('Template rendering failed. Please try again.', { title: 'Rendering Error' });
        return;
      }
      
      console.log('Image Share: Content cloned successfully, generating image...');
      
      // Generate image with optimized settings for mobile
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        imageTimeout: 10000, // Increased timeout for mobile
        logging: false, // Disable logging for better performance
        foreignObjectRendering: true,
        scrollX: 0,
        scrollY: 0,
        windowWidth: tempContainer.scrollWidth,
        windowHeight: tempContainer.scrollHeight
      });
      
      console.log('Image Share: Canvas generated, converting to blob...');
      
      canvas.toBlob(async (blob) => {
        const filename = `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')}_${invoiceBill?.number || 'Bill'}.png`;
        console.log('Image Share: Blob created, filename:', filename);
        
        try {
          // Try to share via system share
          if (navigator.canShare && navigator.canShare({ files: [new File([blob], filename, { type: 'image/png' })] })) {
            console.log('Image Share: Attempting system share...');
            await navigator.share({
              title: `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill?.number || 'Bill'}`,
              text: `Sharing ${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill?.number || 'Bill'}`,
              files: [new File([blob], filename, { type: 'image/png' })]
            });
            console.log('Image Share: System share successful');
          } else {
            console.log('Image Share: System share not available, downloading...');
            // Download fallback
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.click();
            URL.revokeObjectURL(url);
            console.log('Image Share: Download successful');
          }
        } catch (shareError) {
          console.warn('Image Share: System share failed, downloading instead:', shareError);
          // Download fallback if sharing fails
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(url);
          console.log('Image Share: Download fallback successful');
        }
        
        console.log('Image Share: Image generation completed successfully');
      }, 'image/png');
    } catch (err) {
      console.error('Image Share: Error generating image:', err);
      const errorMessage = `Error generating image: ${err.message || err.toString()}. Please try again.`;
      console.error('Image Share: Full error details:', errorMessage);
      error(errorMessage, { title: 'Image Generation Failed', duration: 7000 });
    } finally {
      try { if (tempContainer && tempContainer.parentNode) document.body.removeChild(tempContainer); } catch {}
      // Restore button state
      if (shareButton) {
        shareButton.textContent = originalText;
        shareButton.disabled = false;
        console.log('Image Share: Button state restored');
      }
    }
  };

  // Detailed Purchase Export Function
  const handleDetailedExport = async () => {
    try {
      const billsToExport = getFilteredBills(bills);
      if (!billsToExport || billsToExport.length === 0) {
        warning('No purchase bills to export', { title: 'Export Error' });
        return;
      }
      const exportData = [];
      const formatDate = (dateValue) => {
        if (!dateValue) return '';
        try {
          if (dateValue.toDate) {
            const d = dateValue.toDate();
            return d.toISOString().split('T')[0];
          } else if (typeof dateValue === 'string') {
            return dateValue.split('T')[0];
          } else if (dateValue.seconds) {
            const d = new Date(dateValue.seconds * 1000);
            return d.toISOString().split('T')[0];
          }
          return new Date(dateValue).toISOString().split('T')[0];
        } catch {
          return String(dateValue).split('T')[0];
        }
      };
      billsToExport.forEach((bill) => {
        const billRows = bill.rows || bill.items || [];
        const partyName = bill.partyName || getPartyName(bill.party) || '';
        const billDate = bill.billDate || bill.date || '';
        const dueDate = bill.dueDate || '';
        if (billRows.length > 0) {
          billRows.forEach((row, rowIndex) => {
            let itemName = row.itemName || row.description || '';
            if (row.item && items.length > 0) {
              const itemObj = items.find(it => it.id === row.item || it.itemId === row.item);
              if (itemObj) {
                itemName = itemObj.name || itemObj.itemName || itemName;
              }
            }
            const nos = parseFloat(row.nos || 1);
            const length = parseFloat(row.length || 0);
            const height = parseFloat(row.height || 0);
            const qty = parseFloat(row.qty || row.quantity || 0);
            const qtyExpr = row.qtyExpr || row.qtyDisplay || '';
            const rate = parseFloat(row.rate || 0);
            const amount = parseFloat(row.amount || 0);
            const lineDiscount = parseFloat(row.lineDiscountValue || 0);
            const sgst = parseFloat(row.sgst || 0);
            const cgst = parseFloat(row.cgst || 0);
            const igst = parseFloat(row.igst || 0);
            const total = parseFloat(row.total || amount + sgst + cgst + igst);
            exportData.push({
              'Bill Number': bill.number || bill.billNumber || bill.invoiceNumber || '',
              'Bill Date': formatDate(billDate),
              'Due Date': formatDate(dueDate),
              'Supplier Name': partyName,
              'Supplier ID': bill.party || bill.supplierId || '',
              'Document Type': docTypeOptions.find(opt => opt.value === bill.docType || docType)?.label || 'Purchase Bill',
              'Item Name': itemName,
              'Item ID': row.item || '',
              'HSN Code': row.hsn || row.hsnCode || '',
              'Nos': nos,
              'Length (M1)': length,
              'Height (M2)': height,
              'Quantity': qty,
              'Quantity Expression': qtyExpr,
              'Unit': row.unit || '',
              'Rate': rate,
              'Amount': amount,
              'Line Discount Type': row.lineDiscountType || '',
              'Line Discount Value': lineDiscount,
              'SGST %': row.sgstPercent || (sgst > 0 && amount > 0 ? ((sgst / amount) * 100).toFixed(2) : 0),
              'SGST Amount': sgst,
              'CGST %': row.cgstPercent || (cgst > 0 && amount > 0 ? ((cgst / amount) * 100).toFixed(2) : 0),
              'CGST Amount': cgst,
              'IGST %': row.igstPercent || (igst > 0 && amount > 0 ? ((igst / amount) * 100).toFixed(2) : 0),
              'IGST Amount': igst,
              'Line Total': total,
              'Bill Subtotal': bill.subtotal || bill.amount || 0,
              'Bill Total GST': (bill.sgstAmount || 0) + (bill.cgstAmount || 0) + (bill.igstAmount || 0),
              'Bill Total Amount': bill.amount || 0,
              'Bill Discount': bill.discount || 0,
              'Payment Status': bill.paymentStatus || 'Pending',
              'Paid Amount': bill.paid || 0,
              'Outstanding Amount': bill.outstanding || 0,
              'Notes': bill.notes || '',
              'E-way Bill No': bill.ewayBillNo || bill.customFields?.ewayBillNo || '',
              'E-way Bill Date': bill.ewayDate || bill.customFields?.ewayDate || '',
              'E-way QR': bill.ewayQr || bill.customFields?.ewayQr || '',
              'Created At': bill.createdAt ? formatDate(bill.createdAt) : '',
              'Updated At': bill.updatedAt ? formatDate(bill.updatedAt) : '',
              'Row Index': rowIndex + 1,
              'Total Items in Bill': billRows.length
            });
          });
        } else {
          exportData.push({
            'Bill Number': bill.number || bill.billNumber || bill.invoiceNumber || '',
            'Bill Date': formatDate(billDate),
            'Due Date': formatDate(dueDate),
            'Supplier Name': partyName,
            'Supplier ID': bill.party || bill.supplierId || '',
            'Document Type': docTypeOptions.find(opt => opt.value === bill.docType || docType)?.label || 'Purchase Bill',
            'Item Name': '', 'Item ID': '', 'HSN Code': '', 'Nos': '', 'Length (M1)': '', 'Height (M2)': '',
            'Quantity': '', 'Quantity Expression': '', 'Unit': '', 'Rate': '', 'Amount': '',
            'Line Discount Type': '', 'Line Discount Value': '', 'SGST %': '', 'SGST Amount': '',
            'CGST %': '', 'CGST Amount': '', 'IGST %': '', 'IGST Amount': '', 'Line Total': '',
            'Bill Subtotal': bill.subtotal || bill.amount || 0,
            'Bill Total GST': (bill.sgstAmount || 0) + (bill.cgstAmount || 0) + (bill.igstAmount || 0),
            'Bill Total Amount': bill.amount || 0,
            'Bill Discount': bill.discount || 0,
            'Payment Status': bill.paymentStatus || 'Pending',
            'Paid Amount': bill.paid || 0,
            'Outstanding Amount': bill.outstanding || 0,
            'Notes': bill.notes || '',
            'E-way Bill No': bill.ewayBillNo || bill.customFields?.ewayBillNo || '',
            'E-way Bill Date': bill.ewayDate || bill.customFields?.ewayDate || '',
            'E-way QR': bill.ewayQr || bill.customFields?.ewayQr || '',
            'Created At': bill.createdAt ? formatDate(bill.createdAt) : '',
            'Updated At': bill.updatedAt ? formatDate(bill.updatedAt) : '',
            'Row Index': 0,
            'Total Items in Bill': 0
          });
        }
      });
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [
        { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 15 },
        { wch: 30 }, { wch: 15 }, { wch: 12 }, { wch: 8 }, { wch: 10 }, { wch: 10 },
        { wch: 10 }, { wch: 15 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 15 },
        { wch: 15 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 10 },
        { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
        { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 30 }, { wch: 15 }, { wch: 12 },
        { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 15 }
      ];
      XLSX.utils.book_append_sheet(wb, ws, 'Purchase Bills');
      const dateStr = filterDate ? `_${filterDate.replace(/\//g, '-')}` : '';
      const filename = `Purchase_Bills_Export${dateStr}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, filename);
      success(`Successfully exported ${exportData.length} purchase bill records to ${filename}`, {
        title: 'Export Completed',
        details: `${exportData.length} records • ${filename}`,
        duration: 6000
      });
    } catch (err) {
      console.error('Export error:', err);
      error(`Error exporting purchase bills: ${err.message}`, { title: 'Export Failed', duration: 7000 });
    }
  };






  // UI rendering (similar to Sales)
    return (
    <div className="min-h-screen bg-gray-100 flex flex-col py-6 sm:py-10 max-w-full overflow-x-hidden">
      <style>
        {`
          /* Fix datalist dropdown z-index issues */
          input[list]::-webkit-calendar-picker-indicator {
            z-index: 9999 !important;
          }
          
          /* Ensure datalist dropdown appears above other elements */
          input[list] + datalist {
            z-index: 9999 !important;
            position: relative !important;
          }
          
          /* Fix table cell positioning context for dropdowns */
          table td {
            position: relative;
          }
          
          /* Ensure dropdowns appear above other content */
          select {
            z-index: 10 !important;
            position: relative;
          }
          
          /* Fix pagination dropdown overlap */
          .pagination-select {
            z-index: 20 !important;
          }
          
          /* Custom dropdown styling */
          select[style*="backgroundImage"] {
            -webkit-appearance: none;
            -moz-appearance: none;
            appearance: none;
          }
          
          select[style*="backgroundImage"]::-ms-expand {
            display: none;
          }
          
          table td .relative {
            z-index: auto;
          }
          
          /* Ensure dropdowns in table cells appear above */
          table td input[list] {
            position: relative;
            z-index: 10;
          }
        `}
      </style>
      {errorMessage && (
        <div className="fixed top-6 left-1/2 transform -translate-x-1/2 bg-red-100 border border-red-400 text-red-700 px-6 py-3 rounded shadow-lg z-50 flex items-center gap-4">
          <span>{errorMessage}</span>
          <button onClick={() => setErrorMessage('')} className="ml-4 text-red-700 font-bold text-lg">&times;</button>
        </div>
      )}
      {/* Tabs Section - Full width */}
      <div className="w-full mb-4 sm:mb-6 px-4 sm:px-0">
        <div className="w-full">
          {/* Desktop: Full-width Tabs, Mobile: Dropdown */}
          <div className="mb-2">
            {/* Desktop View - Full-width Tabs */}
            <div className="hidden sm:block">
            <div className="flex w-full bg-gradient-to-r from-blue-50 to-orange-50 border border-blue-100 rounded-xl p-1.5 justify-between shadow-sm gap-1">
                {docTypeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setDocType(opt.value)}
                  className={`flex-1 min-w-0 flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium rounded-md transition-all duration-200 ${
                      docType === opt.value
                        ? 'bg-white text-blue-700 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/70'
                    }`}
                  >
                    {/* Icon for each document type */}
                    {opt.value === 'purchaseBill' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    )}
                    {opt.value === 'purchaseOrder' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    )}
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile View - Dropdown */}
            <div className="sm:hidden flex items-center justify-center">
              <div className="relative">
                <button
                  onClick={() => setShowDocTypeDropdown(!showDocTypeDropdown)}
                  className="bg-white border border-blue-100 rounded-xl shadow-sm px-4 py-2.5 flex items-center gap-2 text-sm font-semibold text-slate-700 hover:bg-blue-50 transition-all duration-200 min-w-[120px] sm:min-w-[140px] justify-center"
                >
                  {/* Icon for current document type */}
                  {docType === 'purchaseBill' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )}
                  {docType === 'purchaseOrder' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  )}
                  <span>{docTypeOptions.find(opt => opt.value === docType)?.label || 'PURCHASE BILL'}</span>
                  <svg className={`w-4 h-4 transition-transform duration-200 ${showDocTypeDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showDocTypeDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    {docTypeOptions.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setDocType(opt.value);
                          setShowDocTypeDropdown(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm font-medium transition-all duration-200 flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg ${
                          docType === opt.value
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {/* Icon for each document type */}
                        {opt.value === 'purchaseBill' && (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        )}
                        {opt.value === 'purchaseOrder' && (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        )}
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
        {/* Quick Items Section - 20-60-20 Layout - Full Width */}
        <div className="mb-8 w-full px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-12 gap-4 items-stretch w-full">
            {/* Left Panel - Most Used Items (20%) */}
            <div className="hidden lg:block col-span-12 lg:col-span-2 bg-gradient-to-br from-blue-50 via-indigo-50 to-orange-50 border border-blue-100 rounded-2xl p-4 flex flex-col shadow-md">
              <h3 className="text-lg font-bold text-blue-800 mb-4 text-center bg-white/80 rounded-xl py-2 px-3 shadow-sm">Most Used Items</h3>

              <div className={`grid grid-cols-${getResponsiveColumns()} gap-2 flex-1 overflow-y-auto`}>
                {mostUsedItems.map((itemData) => {
                  const item = items.find(it => it.id === itemData.id);
                  if (!item) return null;
                  
                  return (
                    <button
                      key={itemData.itemId || itemData.id}
                      onClick={() => handleQuickItemClick(itemData)}
                      className="bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border border-blue-200 rounded-md p-1.5 text-left transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98]"
                      title={`${itemData.itemName || itemData.name || 'Unknown Item'} - Used ${itemData.count} times`}
                    >
                      <div className="font-semibold text-sm text-blue-900 truncate">
                        {itemData.itemName || itemData.name || 'Unknown Item'}
                      </div>
                      <div className="text-xs text-blue-700 font-medium">
                        {formatCurrency(itemData.lastRate || 0, companyCurrency)} × {itemData.lastQty || 1}
                      </div>
                      <div className="text-xs text-blue-600">
                        Used {itemData.count} times
                      </div>
                    </button>
                  );
                })}
              </div>
              {mostUsedItems.length === 0 && (
                <div className="text-center text-gray-500 text-sm py-4">
                  {t('noItemsUsedYet')}
                </div>
              )}
            </div>

            {/* Center Panel - Purchase Entry Form (60%) */}
            <div className="col-span-12 lg:col-span-8 bg-gradient-to-b from-white to-slate-50 border border-slate-200 rounded-2xl shadow-lg p-4 sm:p-6">
        {/* Fixed Header - Mobile & Desktop */}
        <div className="bg-gradient-to-r from-blue-50 to-orange-50 border border-blue-100 rounded-xl p-3 mb-4 shadow-sm">
          <div className="flex justify-between items-center">
            <div className="flex-1">
              <div className="text-xs text-slate-500 mb-1">{docTypeOptions.find(opt => opt.value === docType)?.numberLabel || 'Purchase Bill Number'}</div>
              <div className="text-sm font-semibold text-slate-800">{billNumber || 'Auto'}</div>
            </div>
            <div className="flex-1 ml-4">
              <div className="text-xs text-slate-500 mb-1">Date</div>
              <div className="text-sm font-semibold text-slate-800">{billDate}</div>
            </div>
            <button
              onClick={() => setShowMobileEditModal(true)}
              className="ml-2 p-1 text-gray-600 hover:text-blue-600"
              title="Edit Details"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Party Selection - Mobile & Desktop */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-slate-700 mb-2">{t('partySeller')}</label>
          <div className="relative party-dropdown-container">
            <input
              type="text"
              value={partySearchText}
              onChange={(e) => {
                setPartySearchText(e.target.value);
                if (e.target.value === '') {
                  setParty('');
                  setSelectedPartyForQuickItems('');
                }
              }}
              onFocus={() => setShowPartyDropdown(true)}
              className="w-full border border-slate-300 rounded-xl shadow-sm p-2.5 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
              placeholder="Search or type new party name..."
            />
            {showPartyDropdown && (
              <div className="absolute z-10 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                {filteredParties.map(p => (
                  <button
                    key={p.id}
                    onClick={() => {
                      setParty(p.id);
                      setSelectedPartyForQuickItems(p.id);
                      setPartySearchText(p.firmName);
                      setShowPartyDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                  >
                    {p.firmName}
                  </button>
                ))}
                {partySearchText && !filteredParties.some(p => p.firmName.toLowerCase() === partySearchText.toLowerCase()) && (
                  <button
                    onClick={() => {
                      setNewPartyName(partySearchText);
                      setShowAddPartyModal(true);
                      setShowPartyDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm text-blue-600 border-t border-gray-200"
                  >
                    + Add "{partySearchText}" as new party
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {docType === 'purchaseBill' && (
          <div className="mb-4">
            <WorkOrderLinkSelect
              db={db}
              companyCtx={companyCtx}
              workOrderId={workOrderLink.workOrderId}
              phaseId={workOrderLink.phaseId}
              costCategory={workOrderLink.costCategory}
              onChange={setWorkOrderLink}
            />
          </div>
        )}

        {/* Original Bill Reference and Reason Fields (for Returns and Notes) */}
        {(docType === 'purchaseReturn' || docType === 'debitNote' || docType === 'creditNote') && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h4 className="text-sm font-semibold text-yellow-800 mb-3">
              {docType === 'purchaseReturn' ? 'Return Details' : 'Note Details'}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Original {docType === 'purchaseReturn' ? 'Purchase Bill' : 'Bill'} Number
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={originalBillNumber}
                    onChange={(e) => handleOriginalBillNumberChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm pr-8"
                    placeholder="Enter or search original bill number"
                  />
                  {originalBillNumber && (
                    <button
                      type="button"
                      onClick={() => {
                        setOriginalBillNumber('');
                        setOriginalBillId('');
                        setRows(Array.from({ length: 5 }, () => ({ ...initialItemRow })));
                        setParty('');
                      }}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Clear"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {originalBillId && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Original bill loaded. Items and party have been auto-populated.
                  </p>
                )}
              </div>
              {docType === 'purchaseReturn' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Return Reason
                  </label>
                  <input
                    type="text"
                    value={returnReason}
                    onChange={(e) => setReturnReason(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                    placeholder="e.g., Defective goods, Wrong item"
                  />
                </div>
              )}
            </div>
          </div>
        )}
        
        
        {/* GST Type Info */}
        {party && (
          <>
            {/* Desktop View - Full Box */}
            <div className="hidden md:block mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium text-blue-800">
                GST Type: <span className="font-bold">{getPartyGstType(party)}</span>
                {getPartyGstType(party) === 'Regular' ? ' - GST fields are available for calculation' : ' - GST fields are hidden (no GST calculation)'}
              </span>
            </div>
            {getPartyGstType(party) === 'Regular' && (
              <div className="mt-2 text-xs text-blue-700">
                <p>• For Regular parties, GST calculation is applicable</p>
                <p>• GST fields are available for calculation</p>
                <p>• Regular GST template will be used for printing</p>
              </div>
            )}
            {getPartyGstType(party) !== 'Regular' && (
              <div className="mt-2 text-xs text-blue-700">
                <p>• For {getPartyGstType(party)} parties, GST calculation is not applicable</p>
                <p>• Bill amount will be entered as received from supplier</p>
                <p>• Total amount will be the same as subtotal (no GST added)</p>
                <p>• Non-GST template will be used for printing</p>
              </div>
            )}
            </div>

            {/* Mobile View - Icon Dropdown */}
            <div className="md:hidden mb-4 relative">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold text-slate-900 tracking-tight">Items</h3>
                  <div className="relative">
                    <button
                      onClick={() => setShowGstInfoDropdown(!showGstInfoDropdown)}
                      className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors"
                      title="GST Information"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                      
                    {/* Dropdown Content */}
                    {showGstInfoDropdown && (
                      <div className="absolute left-0 top-full mt-2 w-[min(20rem,calc(100vw-2rem))] sm:w-80 bg-white border border-blue-200 rounded-lg shadow-lg z-50">
                        <div className="p-3 bg-blue-50 border-b border-blue-200 rounded-t-lg">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm font-medium text-blue-800">
                              GST Type: <span className="font-bold">{getPartyGstType(party)}</span>
                              {getPartyGstType(party) === 'Regular' ? ' - GST fields are available for calculation' : ' - GST fields are hidden (no GST calculation)'}
                            </span>
                          </div>
                        </div>
                        <div className="p-3">
                          {getPartyGstType(party) === 'Regular' && (
                            <div className="text-xs text-blue-700 space-y-1">
                              <p>• For Regular parties, GST calculation is applicable</p>
                              <p>• GST fields are available for calculation</p>
                              <p>• Regular GST template will be used for printing</p>
          </div>
                          )}
                          {getPartyGstType(party) !== 'Regular' && (
                            <div className="text-xs text-blue-700 space-y-1">
                              <p>• For {getPartyGstType(party)} parties, GST calculation is not applicable</p>
                              <p>• Bill amount will be entered as received from supplier</p>
                              <p>• Total amount will be the same as subtotal (no GST added)</p>
                              <p>• Non-GST template will be used for printing</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Quick Item Button */}
                <button
                  ref={quickItemButtonRef}
                  onClick={(e) => {
                    const position = calculateModalPosition(e.currentTarget);
                    setQuickItemModalPosition(position);
                    setShowQuickItemsModal(true);
                  }}
                  className="bg-gradient-to-r from-blue-600 to-orange-500 hover:from-blue-700 hover:to-orange-600 text-white font-semibold py-1.5 px-3.5 rounded-lg text-sm flex items-center gap-1.5 shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Quick Item
                </button>
              </div>
            </div>
          </>
        )}
        
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto overflow-y-auto rounded-lg border border-gray-200 mb-4 max-h-[600px] custom-scrollbar touch-scrollbar">
          <table ref={tableRef} className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                <th className="px-2 py-1">ITEM</th>
                <th className="px-2 py-1">MEASUREMENT / NUMBERS</th>
                <th className="px-2 py-1">QTY</th>
                <th className="px-2 py-1">RATE</th>
                <th className="px-2 py-1">DISCOUNT</th>
                <th className="px-2 py-1">AMOUNT</th>
                {getPartyGstType(party) === 'Regular' && (
                  <>
                    <th className="px-2 py-1">SGST</th>
                    <th className="px-2 py-1">CGST</th>
                    <th className="px-2 py-1">IGST</th>
                  </>
                )}
                <th className="px-2 py-1">TOTAL</th>
                <th className="px-2 py-1">REMOVE</th>
                                    </tr>
                                </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx}>
                  <td className="px-2 py-1">
                      <div className="relative">
                        <input
                          type="text"
                          list={`p-items-list-${idx}`}
                          className="border border-gray-300 rounded-md p-1 w-48 relative"
                          style={{ zIndex: 1 }}
                          placeholder={t('selectItem')}
                          value={
                            itemSearchTextByRow[idx] !== undefined
                              ? itemSearchTextByRow[idx]
                              : (getItemDisplayName(findItemInMaster(row.item, items)) || row.itemName || '')
                          }
                          onChange={(e) => {
                            const text = e.target.value;
                            setItemSearchTextByRow(prev => ({ ...prev, [idx]: text }));
                            
                            // If the field is empty, clear the item selection
                            if (!text.trim()) {
                              handleRowChange(idx, 'item', '');
                              return;
                            }
                            
                            const match = matchItemBySearchText(text, items, 'exact');
                            if (match) {
                              handleRowChange(idx, 'item', match.id);
                              setItemSearchTextByRow(prev => ({ ...prev, [idx]: getItemDisplayName(match) }));
                            }
                          }}
                          onBlur={(e) => {
                            const text = e.target.value;
                            
                            // If the field is empty, clear the item selection
                            if (!text.trim()) {
                              handleRowChange(idx, 'item', '');
                              setItemSearchTextByRow(prev => ({ ...prev, [idx]: '' }));
                              return;
                            }
                            
                            const match = matchItemBySearchText(text, items, 'prefix');
                            if (match) {
                              handleRowChange(idx, 'item', match.id);
                              setItemSearchTextByRow(prev => ({ ...prev, [idx]: getItemDisplayName(match) }));
                            }
                          }}
                        />
                        <datalist id={`p-items-list-${idx}`}>
                          {filterItemsForPicker(items, itemSearchTextByRow[idx] || '', 500).map(it => (
                            <option key={it.id} value={getItemDisplayName(it)} />
                          ))}
                        </datalist>
                      </div>
                  </td>
                  <td className="px-2 py-1">
                    <div className="flex flex-col">
                      <input
                        type="text"
                        value={row.qtyExpr}
                        onChange={e => handleRowChange(idx, 'qtyExpr', e.target.value)}
                        placeholder="e.g. 5x3x2 or 10*2"
                        className="border border-gray-300 rounded-md p-1 w-40"
                      />
                      {row.qtyDisplay && (
                        <span className="text-xs text-gray-500 mt-1">= {row.qty} ({row.qtyDisplay})</span>
                      )}
                      {/* Show original quantity for returns/notes */}
                      {(docType === 'purchaseReturn' || docType === 'debitNote' || docType === 'creditNote') && row.originalQty && (
                        <span className="text-xs text-blue-600 mt-1 font-medium">
                          Original: {row.originalQtyDisplay || row.originalQtyExpr || row.originalQty} {row.unit ? `(${row.unit})` : ''}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1">
                    <div className="flex flex-col">
                      <input type="number" value={row.qty} min={0} readOnly
                        className="border border-gray-300 rounded-md p-1 w-16 bg-gray-100" />
                      {/* Show original quantity badge for partial returns */}
                      {(docType === 'purchaseReturn' || docType === 'debitNote' || docType === 'creditNote') && row.originalQty && parseFloat(row.qty || 0) < parseFloat(row.originalQty || 0) && (
                        <span className="text-xs text-orange-600 mt-1 font-semibold" title="Partial Return">
                          ⚠ Partial
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" value={row.rate} min={0} onChange={e => handleRowChange(idx, 'rate', e.target.value)}
                      className="border border-gray-300 rounded-md p-1 w-16" />
                  </td>
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min={0}
                        value={row.lineDiscountValue}
                        onChange={e => handleRowChange(idx, 'lineDiscountValue', e.target.value)}
                        className="border border-gray-300 rounded-md p-1 w-20"
                        placeholder={row.lineDiscountType === 'percent' ? 'Percent' : 'Amount'}
                      />
                      <select
                        value={row.lineDiscountType || 'amount'}
                        onChange={e => handleRowChange(idx, 'lineDiscountType', e.target.value)}
                        className="border border-gray-300 rounded-md p-1 relative z-10 w-12 text-center appearance-none cursor-pointer bg-white"
                        style={{
                          backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 4px center',
                          backgroundSize: '12px',
                          paddingRight: '20px'
                        }}
                      >
                        <option value="amount">{getCurrencySymbol(companyCurrency)}</option>
                        <option value="percent">%</option>
                      </select>
                    </div>
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" value={row.amount} min={0} readOnly
                      className="border border-gray-300 rounded-md p-1 w-20 bg-gray-100" />
                  </td>
                  {getPartyGstType(party) === 'Regular' && (
                    <>
                      <td className="px-2 py-1">
                        <input type="number" value={row.sgst} min={0} onChange={e => handleRowChange(idx, 'sgst', e.target.value)}
                          className="border border-gray-300 rounded-md p-1 w-14" />
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" value={row.cgst} min={0} onChange={e => handleRowChange(idx, 'cgst', e.target.value)}
                          className="border border-gray-300 rounded-md p-1 w-14" />
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" value={row.igst} min={0} onChange={e => handleRowChange(idx, 'igst', e.target.value)}
                          className="border border-gray-300 rounded-md p-1 w-14" />
                      </td>
                    </>
                  )}
                  <td className="px-2 py-1">
                    <input type="number" value={row.total} min={0} readOnly
                      className="border border-gray-300 rounded-md p-1 w-20 bg-green-50 font-semibold" />
                  </td>
                  <td className="px-2 py-1">
                    <button type="button" onClick={() => removeRow(idx)} className="text-red-600 font-bold px-2 py-1 rounded hover:bg-red-100">X</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
          </table>
        </div>
        
        {/* Mobile Card View - Carousel Style */}
        <div className="md:hidden mb-5">
          {/* Carousel Container */}
          <div
            className="relative bg-gradient-to-b from-blue-50/40 to-white border border-blue-100 rounded-xl p-4 shadow-sm transition-all duration-200 ease-in-out"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Swipe Instructions */}
            <div className="text-center text-[11px] text-slate-500 mb-3">
              Swipe left/right to navigate between items
            </div>
            
            {/* Navigation Header */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={goToPreviousItem}
                  disabled={currentMobileItemIndex === 0}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm touch-manipulation"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h4 className="text-sm font-semibold text-slate-800 bg-white px-2.5 py-1 rounded-full border border-slate-200">Item #{currentMobileItemIndex + 1}</h4>
                <button
                  onClick={goToNextItem}
                  disabled={currentMobileItemIndex === rows.length - 1}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm touch-manipulation"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              <button
                onClick={() => removeRow(currentMobileItemIndex)}
                className="text-rose-600 hover:text-rose-700 text-lg bg-rose-50 hover:bg-rose-100 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors touch-manipulation"
                aria-label="Remove item"
              >
                🗑️
              </button>
            </div>

            {/* Current Item Card */}
            {rows[currentMobileItemIndex] && (() => {
              const row = rows[currentMobileItemIndex];
              const index = currentMobileItemIndex;
              return (
              <div key={currentMobileItemIndex}>
              
              {/* Item Selection — custom picker (avoids native dark datalist on mobile) */}
              <div className="mb-3 relative z-30" ref={mobileItemPickerRef}>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">Item</label>
                  {(() => {
                  const searchQ = itemSearchTextByRow[index] || '';
                  const mobileFilteredItems = filterItemsForPicker(items, searchQ, 120).slice(0, 120);
                  return (
                    <>
                      <div className="relative">
                        <input
                          type="text"
                          autoComplete="off"
                          value={itemSearchTextByRow[index] || ''}
                          onChange={(e) => {
                            const text = e.target.value;
                            setItemSearchTextByRow((prev) => ({ ...prev, [index]: text }));
                            setMobileItemPickerOpen(true);
                            if (!text.trim()) {
                              handleRowChange(currentMobileItemIndex, 'item', '');
                              return;
                            }
                            const match = matchItemBySearchText(text, items, 'exact');
                            if (match) {
                              handleRowChange(currentMobileItemIndex, 'item', match.id);
                              setItemSearchTextByRow((prev) => ({
                                ...prev,
                                [currentMobileItemIndex]: getItemDisplayName(match),
                              }));
                            }
                          }}
                          onFocus={() => setMobileItemPickerOpen(true)}
                          onBlur={(e) => {
                            const text = e.target.value;
                            if (!text.trim()) {
                              handleRowChange(currentMobileItemIndex, 'item', '');
                              setItemSearchTextByRow((prev) => ({ ...prev, [currentMobileItemIndex]: '' }));
                              return;
                            }
                            const match = matchItemBySearchText(text, items, 'prefix');
                            if (match) {
                              handleRowChange(currentMobileItemIndex, 'item', match.id);
                              setItemSearchTextByRow((prev) => ({
                                ...prev,
                                [currentMobileItemIndex]: getItemDisplayName(match),
                              }));
                            }
                          }}
                          className="w-full p-2.5 pr-12 border border-slate-300 rounded-xl text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                          placeholder="Search item..."
                        />
                        <button
                          type="button"
                          tabIndex={-1}
                          onClick={() => setMobileItemPickerOpen((o) => !o)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg text-slate-500 hover:bg-sky-50 hover:text-sky-700 transition-colors"
                          aria-label={mobileItemPickerOpen ? 'Close item list' : 'Open item list'}
                        >
                          <ChevronDown
                            className={`w-4 h-4 transition-transform duration-200 ${mobileItemPickerOpen ? 'rotate-180' : ''}`}
                            aria-hidden
                          />
                        </button>
                      </div>
                      {mobileItemPickerOpen && (
                        <div className="absolute left-0 right-0 mt-1.5 max-h-60 overflow-y-auto rounded-xl border border-slate-200/90 bg-white shadow-xl shadow-slate-300/40 ring-1 ring-slate-100 py-1 overscroll-contain">
                          {mobileFilteredItems.length === 0 ? (
                            <div className="px-3 py-4 text-center text-sm text-slate-500">No matching items</div>
                          ) : (
                            mobileFilteredItems.map((it) => {
                              const displayName = it.itemName || it.name || 'Item';
                              return (
                                <button
                                  key={it.id}
                                  type="button"
                                  onMouseDown={(ev) => ev.preventDefault()}
                                  onClick={() => {
                                    handleRowChange(currentMobileItemIndex, 'item', it.id);
                                    setItemSearchTextByRow((prev) => ({
                                      ...prev,
                                      [currentMobileItemIndex]: displayName,
                                    }));
                                    setMobileItemPickerOpen(false);
                                  }}
                                  className="w-full text-left px-3 py-2.5 hover:bg-sky-50 active:bg-sky-100/80 border-b border-slate-100 last:border-0 transition-colors"
                                >
                                  <span className="block font-semibold text-slate-900 text-sm leading-snug truncate">
                                    {displayName}
                                  </span>
                                </button>
                              );
                            })
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              
              {/* Measurement / Numbers Row */}
              <div className="mb-3">
                <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">Measurement / Numbers</label>
                <div className="flex flex-col">
                  <input
                    type="text"
                    value={row.qtyExpr || ''}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'qtyExpr', e.target.value)}
                    className="w-full p-2.5 border border-slate-300 rounded-lg text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                    placeholder="e.g. 5x3x3, 4x5, 50"
                  />
                  {row.qtyDisplay && (
                    <span className="text-xs text-gray-500 mt-1">= {row.qty} ({row.qtyDisplay})</span>
                  )}
                </div>
              </div>
              
              {/* Quantity and Rate Row */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">Quantity</label>
                  <input
                    type="number"
                    value={row.qty || ''}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'qty', e.target.value)}
                    className="w-full p-2.5 border border-slate-200 rounded-lg text-sm bg-slate-100"
                    placeholder="0"
                    min="0"
                    step="0.01"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">Rate</label>
                  <input
                    type="number"
                    value={row.rate || ''}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'rate', e.target.value)}
                    className="w-full p-2.5 border border-slate-300 rounded-lg text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              
              {/* Discount Row */}
              <div className="mb-3">
                <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">Discount</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    value={row.lineDiscountValue || ''}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'lineDiscountValue', e.target.value)}
                    className="flex-1 p-2.5 border border-slate-300 rounded-lg text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                    placeholder={row.lineDiscountType === 'percent' ? 'Percent' : 'Amount'}
                  />
                  <select
                    value={row.lineDiscountType || 'amount'}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'lineDiscountType', e.target.value)}
                    className="border border-slate-300 rounded-lg text-sm p-2.5 w-20 relative z-10 appearance-none cursor-pointer bg-white text-center shadow-sm"
                    style={{
                      backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 6px center',
                      backgroundSize: '14px',
                      paddingRight: '24px'
                    }}
                  >
                    <option value="amount">{getCurrencySymbol(companyCurrency)}</option>
                    <option value="percent">%</option>
                  </select>
                </div>
              </div>
              
              {/* Amount Row */}
              <div className="mb-3">
                <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">Amount</label>
                <div className="w-full p-2.5 bg-slate-100 border border-slate-200 rounded-lg text-sm font-semibold text-blue-700">
                  {formatCurrency(parseFloat(row.amount) || 0, companyCurrency)}
                </div>
              </div>
              
              {/* GST Row (if applicable) */}
              {getPartyGstType(party) === 'Regular' && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">SGST</label>
                    <div className="w-full p-2 bg-gray-100 border border-gray-300 rounded text-xs text-center">
                      {formatCurrency(parseFloat(row.sgst) || 0, companyCurrency)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">CGST</label>
                    <div className="w-full p-2 bg-gray-100 border border-gray-300 rounded text-xs text-center">
                      {formatCurrency(parseFloat(row.cgst) || 0, companyCurrency)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">IGST</label>
                    <div className="w-full p-2 bg-gray-100 border border-gray-300 rounded text-xs text-center">
                      {formatCurrency(parseFloat(row.igst) || 0, companyCurrency)}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Total Row */}
              <div className="bg-gradient-to-r from-blue-50 to-orange-50 p-3 rounded-lg border border-blue-100 shadow-sm">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-slate-700">Total:</span>
                  <span className="text-lg font-bold text-blue-700">
                    {formatCurrency(parseFloat(row.total) || parseFloat(row.amount) || 0, companyCurrency)}
                  </span>
                </div>
              </div>
            </div>
              );
            })()}

            {/* Item Numbers Pagination */}
            <div className="mt-4 flex justify-center">
              <div className="flex gap-2">
                {rows.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToItem(index)}
                  className={`w-8 h-8 rounded-full text-sm font-semibold transition-colors ${
                      index === currentMobileItemIndex
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center mb-6">
          <div className="flex flex-col md:flex-row gap-2 mb-4 md:mb-0">
            <button onClick={addRow} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-lg shadow-sm">Add Item Row</button>
            <button onClick={() => setShowAddItemModal(true)} className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2.5 px-4 rounded-lg shadow-sm">Add New Item</button>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 w-full md:w-80 max-w-full">
            <div className="flex justify-between mb-1"><span>Subtotal (Excl. GST):</span><span>{formatCurrency(subtotal, companyCurrency)}</span></div>
            {getPartyGstType(party) === 'Regular' && (
              <>
                <div className="flex justify-between mb-1"><span>Total SGST:</span><span>{formatCurrency(totalSGST, companyCurrency)}</span></div>
                <div className="flex justify-between mb-1"><span>Total CGST:</span><span>{formatCurrency(totalCGST, companyCurrency)}</span></div>
                <div className="flex justify-between mb-1"><span>Total IGST:</span><span>{formatCurrency(totalIGST, companyCurrency)}</span></div>
              </>
            )}
            <div className="flex justify-between mt-2 font-bold text-lg">
              <span>Grand Total {getPartyGstType(party) === 'Regular' ? '(Incl. GST)' : '(No GST)'}:</span>
              <span className="text-blue-700">{formatCurrency(grandTotal, companyCurrency)}</span>
            </div>
          </div>
        </div>

        {/* Add Note and E-way Bill Buttons */}
        <div className="mb-6">
          <div className="flex gap-3 flex-wrap">
            {/* Add Note Button */}
            <button
              type="button"
              onClick={() => setShowNotesModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Note
            </button>
            
            {/* Add E-way Bill Button */}
            <button
              type="button"
              onClick={() => setShowEwayBillModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-600 bg-green-50 hover:bg-green-100 border border-green-200 rounded-md transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add E-way Bill
            </button>
          </div>
        </div>

                <div className="flex flex-col gap-2 sm:gap-3 mt-4 md:mt-4 sticky md:static bottom-[calc(var(--navigation-bar-height,env(safe-area-inset-bottom,0px))+8px)] z-30 bg-white/95 backdrop-blur-sm border-t border-gray-200 md:border-0 rounded-t-xl md:rounded-none p-3 md:p-0 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] md:shadow-none md:bg-transparent md:backdrop-blur-0">
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                      <button id="add-purchase-button"
                        onClick={handleSaveBill}
                        disabled={isSavingBill}
                        className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-xl shadow-md transition-all duration-300 active:scale-[0.98] touch-manipulation min-h-[44px]"
                      >
                        {isSavingBill
                          ? 'Saving…'
                          : editingBillId
                            ? 'Update Bill'
                            : 'Save Complete Bill'}
                      </button>
                      <button
                        onClick={clearBillForm}
                        className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold py-3 px-4 rounded-xl shadow-md transition-all duration-300 active:scale-[0.98] touch-manipulation min-h-[44px]"
                      >
                        Clear Form
                      </button>
                    </div>
                    <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 sm:gap-3">
                      <button
                        type="button"
                        onClick={handleOpenFIFOPayment}
                        className="flex-1 bg-gradient-to-r from-blue-600 to-orange-500 hover:from-blue-700 hover:to-orange-600 text-white font-semibold py-3 px-3 sm:px-4 rounded-xl shadow-md transition-all active:scale-[0.98] print:hidden touch-manipulation min-h-[44px] text-sm sm:text-base"
                      >
                        FIFO Payment
                      </button>
                      {editingBillId ? (
                        <>
                          <button
                            onClick={() => {
                              setEditingBillId(null);
                              setBillNumber('');
                              setBillDate(new Date().toISOString().split('T')[0]);
                              setParty('');
                              setNotes('');
                              setRows([{ ...initialItemRow }]);
                              setCustomFields({ ewayBillNo: '', ewayQr: '', ewayDate: '' });
                              setItemSearchTextByRow({});
                              setCurrentMobileItemIndex(0);
                            }}
                            className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-3 sm:px-4 rounded-xl shadow-md transition-all active:scale-[0.98] touch-manipulation min-h-[44px] text-sm sm:text-base"
                          >
                            Cancel Edit
                          </button>
                          <button
                            onClick={() => handleAddPayment(bills.find(b => b.id === editingBillId))}
                            className="col-span-2 sm:col-span-1 sm:flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-xl shadow-md transition-all active:scale-[0.98] touch-manipulation min-h-[44px]"
                          >
                            Add Payment
                          </button>
                        </>
                      ) : (
                        <div className="hidden sm:block sm:flex-1" aria-hidden="true" />
                      )}
                    </div>
                </div>

                {/* Success Modal - Mobile View (Inline after buttons) */}
                {showSuccessModal && (
                  <>
                    {/* Mobile View - Inline */}
                    <div className="mt-6 w-full mx-auto lg:hidden">
                      <div ref={successModalRef} className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 max-w-md w-full mx-4 sm:mx-auto relative">
                        <div className="absolute top-2 right-2 flex items-center space-x-2">
                          <span className="text-xs text-gray-500">Press ESC to close</span>
                          <button
                            onClick={() => setShowSuccessModal(false)}
                            className="text-gray-500 hover:text-gray-800 text-xl font-bold w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100"
                          >
                            ×
                          </button>
                        </div>
                        
                        <div className="text-center mb-4">
                          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">
                            {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'} Saved Successfully!
                          </h3>
                          <p className="text-gray-600">What would you like to do next?</p>
                        </div>
                        
                        <div className="flex flex-col gap-3">
                          <button
                            onClick={() => {
                              handleSuccessModalPrint();
                            }}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
                            </svg>
                            Print {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                          </button>
                          
                          <button
                            onClick={() => {
                              setShowSuccessModal(false);
                              resetFormToInitialState();
                            }}
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                            </svg>
                            Create New {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                          </button>
                          
                          <button
                            onClick={() => {
                              const bill = bills.find(b => b.id === savedBillId);
                              if (bill) {
                                handleAddPayment(bill);
                              }
                              setShowSuccessModal(false);
                            }}
                            className="w-full bg-green-100 hover:bg-green-200 text-green-700 font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                            </svg>
                            Add Payment
                          </button>
                          
                          <button
                            onClick={() => {
                              const bill = bills.find(b => b.id === savedBillId);
                              if (bill) {
                                handleEditBill(bill);
                              }
                              setShowSuccessModal(false);
                            }}
                            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-md"
                          >
                            Edit This {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Desktop View - Overlay */}
                    <div className="hidden lg:fixed lg:inset-0 lg:flex lg:items-center lg:justify-center lg:z-50">
                      <div ref={successModalRef} className="bg-white rounded-lg shadow-2xl border-2 border-gray-200 p-6 max-w-md w-full mx-auto relative">
                        <div className="absolute top-2 right-2 flex items-center space-x-2">
                          <span className="text-xs text-gray-500">Press ESC to close</span>
                          <button 
                            onClick={() => setShowSuccessModal(false)} 
                            className="text-gray-500 hover:text-gray-800 text-xl font-bold w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100"
                          >
                            ×
                          </button>
                        </div>
                        
                        <div className="text-center mb-4">
                          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">
                            {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'} Saved Successfully!
                          </h3>
                          <p className="text-gray-600">What would you like to do next?</p>
                        </div>
                        
                        <div className="flex flex-col gap-3">
                          <button
                            onClick={() => {
                              handleSuccessModalPrint();
                            }}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
                            </svg>
                            Print {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                          </button>
                          
                          <button
                            onClick={() => {
                              setShowSuccessModal(false);
                              resetFormToInitialState();
                            }}
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                            </svg>
                            Create New {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                          </button>
                          
                          <button
                            onClick={() => {
                              const bill = bills.find(b => b.id === savedBillId);
                              if (bill) {
                                handleAddPayment(bill);
                              }
                              setShowSuccessModal(false);
                            }}
                            className="w-full bg-green-100 hover:bg-green-200 text-green-700 font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                            </svg>
                            Add Payment
                          </button>
                          
                          <button
                            onClick={() => {
                              const bill = bills.find(b => b.id === savedBillId);
                              if (bill) {
                                handleEditBill(bill);
                              }
                              setShowSuccessModal(false);
                            }}
                            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-md"
                          >
                            Edit This {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
            </div> {/* Close Center Panel */}
            
            {/* Right Panel - Party-wise Items (20%) */}
            <div className="hidden lg:block col-span-12 lg:col-span-2 bg-gradient-to-br from-green-50 via-green-50 to-green-100 border border-green-200 rounded-lg p-4 flex flex-col shadow-sm">
              <h3 className="text-lg font-bold text-green-800 mb-4 text-center bg-white bg-opacity-70 rounded-lg py-2 px-3 shadow-sm">Party Items</h3>

              {!selectedPartyForQuickItems ? (
                <div className="text-center text-gray-500 text-sm py-4">
                  Select a party to see their items
                </div>
              ) : (
                <>
                  <div className="text-xs text-gray-600 mb-3 text-center">
                    {parties.find(p => p.id === selectedPartyForQuickItems)?.firmName}
                  </div>
                  <div className={`grid grid-cols-${getResponsiveColumns()} gap-2 flex-1 overflow-y-auto`}>
                    {partyWiseItems.map((itemData) => {
                      const item = items.find(it => it.id === itemData.itemId);
                      if (!item) return null;
                      
                      return (
                        <button
                          key={itemData.itemId || itemData.id}
                          onClick={() => handleQuickItemClick(itemData, true)}
                          className="bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border border-green-200 rounded-md p-1.5 text-left transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98]"
                          title={`${itemData.itemName || itemData.name || 'Unknown Item'} - Used ${itemData.count} times with this party`}
                        >
                          <div className="font-semibold text-sm text-green-900 truncate">
                            {itemData.itemName || itemData.name || 'Unknown Item'}
                          </div>
                          <div className="text-xs text-green-700 font-medium">
                            ₹{itemData.lastRate || 0} × {itemData.lastQty || 0}
                          </div>
                          <div className="text-xs text-green-600">
                            Used {itemData.count} times
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {partyWiseItems.length === 0 && (
                    <div className="text-center text-gray-500 text-sm py-4">
                      No items used with this party
                    </div>
                  )}
                </>
              )}
            </div>
          </div> {/* Close Quick Items Grid */}
        </div> {/* Close Quick Items Section */}
        
        {/* Quick Items Modal - Compact */}
        {showQuickItemsModal && createPortal(
          <div
            className="fixed inset-0 z-[10000] bg-slate-900/35 backdrop-blur-[1px] flex items-center justify-center p-3 sm:p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowQuickItemsModal(false);
              }
            }}
          >
            <div
              className="relative bg-white rounded-2xl border border-blue-100 shadow-2xl w-full max-w-3xl max-h-[88vh] overflow-hidden"
              style={quickItemModalPosition}
            >
              <div className="flex items-center justify-between p-3 border-b border-gray-200">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">Quick Items</h3>
                <button
                  onClick={() => setShowQuickItemsModal(false)}
                  className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-3 overflow-y-auto max-h-[calc(85vh-60px)]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Most Used Items */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 pb-1 border-b border-gray-200">
                      Most Used ({mostUsedItems.length})
                    </h4>
                    <div className="grid gap-1.5 max-h-80 overflow-y-auto" style={{
                      gridTemplateColumns: 
                        window.innerWidth >= 360 ? 'repeat(3, 1fr)' : 
                        window.innerWidth >= 144 ? 'repeat(2, 1fr)' : 
                        '1fr'
                    }}>
                      {mostUsedItems.map((itemData) => {
                        const item = items.find(it => it.id === itemData.id);
                        if (!item) return null;
                        
                        return (
                          <button
                            key={itemData.itemId || itemData.id}
                            onClick={() => {
                              handleQuickItemClick(itemData);
                              setShowQuickItemsModal(false);
                            }}
                            className="bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border border-blue-200 rounded p-1.5 text-left transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98]"
                            title={`${itemData.itemName || itemData.name || 'Unknown Item'} - Used ${itemData.count} times`}
                          >
                            <div className="font-medium text-xs text-blue-900 leading-tight break-words">
                              {itemData.itemName || itemData.name || 'Unknown Item'}
                            </div>
                            <div className="text-xs text-blue-700 font-medium mt-0.5">
                              {formatCurrency(itemData.lastRate || 0, companyCurrency)}×{itemData.lastQty || 1}
                            </div>
                            <div className="text-xs text-blue-600">
                              {itemData.count}×
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {mostUsedItems.length === 0 && (
                      <div className="text-center text-gray-500 text-xs py-3">
                        No items used yet
                      </div>
                    )}
                  </div>

                  {/* Party-wise Items */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 pb-1 border-b border-gray-200">
                      Party Items ({partyWiseItems.length})
                    </h4>
                    <AppSelect
                      value={selectedPartyForQuickItems}
                      onChange={(e) => setSelectedPartyForQuickItems(e.target.value)}
                      placeholder="Select Party"
                      aria-label="Party for quick items"
                      sheetTitle="Select party"
                      options={[
                        { value: '', label: 'Select Party' },
                        ...Array.from(new Set(bills.map((bill) => bill.party).filter(Boolean))).map((partyId) => {
                          const partyName = parties.find((p) => p.id === partyId)?.firmName || partyId;
                          return { value: partyId, label: partyName };
                        }),
                      ]}
                      triggerClassName={`${APP_SELECT_FILTER_TRIGGER} text-xs mb-2 rounded p-1.5`}
                    />
                    
                    {!selectedPartyForQuickItems ? (
                      <div className="text-center text-gray-500 text-xs py-3">
                        Select a party to see their items
                      </div>
                    ) : (
                      <div className="grid gap-1.5 max-h-80 overflow-y-auto" style={{
                        gridTemplateColumns: 
                          window.innerWidth >= 360 ? 'repeat(3, 1fr)' : 
                          window.innerWidth >= 144 ? 'repeat(2, 1fr)' : 
                          '1fr'
                      }}>
                        {partyWiseItems.map((itemData) => {
                          const item = items.find(it => it.id === itemData.itemId);
                          if (!item) return null;
                          
                          return (
                            <button
                              key={itemData.itemId || itemData.id}
                              onClick={() => {
                                handleQuickItemClick(itemData);
                                setShowQuickItemsModal(false);
                              }}
                              className="bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border border-green-200 rounded p-1.5 text-left transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98]"
                              title={`${itemData.itemName || itemData.name || 'Unknown Item'} - Used ${itemData.count} times`}
                            >
                              <div className="font-medium text-xs text-green-900 leading-tight break-words">
                                {itemData.itemName || itemData.name || 'Unknown Item'}
                              </div>
                              <div className="text-xs text-green-700 font-medium mt-0.5">
                                {formatCurrency(itemData.lastRate || 0, companyCurrency)}×{itemData.lastQty || 1}
                              </div>
                              <div className="text-xs text-green-600">
                                {itemData.count}×
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {partyWiseItems.length === 0 && selectedPartyForQuickItems && (
                      <div className="text-center text-gray-500 text-xs py-3">
                        No items found for this party
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
        
        {/* Mobile Edit Modal */}
        {showMobileEditModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
            <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-md w-full max-h-[90dvh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                <h3 className="text-lg font-semibold text-gray-800">Edit Details</h3>
                <button
                  onClick={() => setShowMobileEditModal(false)}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-gray-200 rounded-full transition-colors touch-manipulation"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{docTypeOptions.find(opt => opt.value === docType)?.numberLabel || 'Purchase Bill Number'}</label>
                  <input 
                    type="text" 
                    value={billNumber} 
                    onChange={e => setBillNumber(e.target.value)}
                    readOnly={!!editingBillId}
                    className={`w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm ${editingBillId ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    title={editingBillId ? 'Bill number cannot be changed when editing' : ''}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input 
                    type="date" 
                    value={billDate} 
                    onChange={e => setBillDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('partySeller')}</label>
                  <div className="relative party-dropdown-container">
                    <input
                      type="text"
                      value={partySearchText}
                      onChange={(e) => {
                        setPartySearchText(e.target.value);
                        if (e.target.value === '') {
                          setParty('');
                          setSelectedPartyForQuickItems('');
                        }
                      }}
                      onFocus={() => setShowPartyDropdown(true)}
                      className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                      placeholder="Search or type new party name..."
                    />
                    {showPartyDropdown && (
                      <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-32 overflow-y-auto">
                        {filteredParties.map(p => (
                          <button
                            key={p.id}
                            onClick={() => {
                              setParty(p.id);
                              setSelectedPartyForQuickItems(p.id);
                              setPartySearchText(p.firmName);
                              setShowPartyDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                          >
                            {p.firmName}
                          </button>
                        ))}
                        {partySearchText && !filteredParties.some(p => p.firmName.toLowerCase() === partySearchText.toLowerCase()) && (
                          <button
                            onClick={() => {
                              setNewPartyName(partySearchText);
                              setShowAddPartyModal(true);
                              setShowPartyDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm text-blue-600 border-t border-gray-200"
                          >
                            + Add "{partySearchText}" as new party
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => setShowMobileEditModal(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setShowMobileEditModal(false)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md text-sm"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Bills List Section */}
        <div className="mt-12 w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-6">
            <h3 id="purchases-list-title" className="text-xl font-bold text-gray-800">Recent Bills</h3>
            <div className="flex gap-2">
              <button
                onClick={handleDetailedExport}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out transform hover:scale-105 flex items-center gap-2"
                title="Export all purchase bills with detailed item information to Excel"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export to Excel
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out transform hover:scale-105"
              >
                {showFilters ? 'Hide Table & Filters' : 'Show Table & Filters'}
              </button>
            </div>
          </div>
          
          {/* Filter Section and Bill List - Both controlled by showFilters */}
          {showFilters ? (
            <>
              <CompactFilterBar
                filters={[
                  {
                    key: 'search',
                    label: 'Search Bills & Parties',
                    type: 'text',
                    value: filterSearch,
                    placeholder: 'Search by bill number or party name...',
                    fullWidth: true
                  },
                  {
                    key: 'date',
                    label: 'Date',
                    type: 'text',
                    value: filterDate,
                    placeholder: 'DD/MM/YYYY or partial'
                  },
                  {
                    key: 'amount',
                    label: 'Amount',
                    type: 'text',
                    value: filterAmount,
                    placeholder: 'Filter by amount'
                  },
                  {
                    key: 'billNo',
                    label: 'Bill No.',
                    type: 'text',
                    value: filterBillNo,
                    placeholder: 'Filter by bill number'
                  },
                  {
                    key: 'party',
                    label: 'Party',
                    type: 'text',
                    value: filterParty,
                    placeholder: 'Filter by party name'
                  },
                  {
                    key: 'fromDate',
                    label: 'From Date',
                    type: 'date',
                    value: filterFromDate,
                    placeholder: 'Start date'
                  },
                  {
                    key: 'toDate',
                    label: 'To Date',
                    type: 'date',
                    value: filterToDate,
                    placeholder: 'End date'
                  }
                ]}
                onFilterChange={handleFilterChange}
                onClearFilters={clearFilters}
                showClearButton={true}
                inlineSearch={true}
                className="bg-gray-50"
              />

              {bills.length === 0 ? (
                <div className="text-center text-gray-500 py-8 text-lg">No bills yet. Create your first bill above!</div>
              ) : (
                <div id="purchases-list" className="rounded-xl border border-gray-200 mb-6 shadow-sm bg-white overflow-hidden">
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                {/* Header Row */}
                <div className="bg-slate-50 border-b border-gray-200 rounded-t-xl p-3 lg:p-4 sticky top-0 z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 grid grid-cols-6 gap-3 lg:gap-5 items-center justify-items-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                      <SortableHeader
                        columnKey="number"
                        label="Bill No."
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center truncate cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="billDate"
                        label={t('date')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="partyName"
                        label={t('party')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="amount"
                        label={t('amount')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="paid"
                        label={t('paid')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="outstanding"
                        label={t('outstanding')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                    </div>
                    <div className="ml-2 lg:ml-4 w-20 lg:w-24 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                      Actions
                    </div>
                  </div>
                </div>
                
                {/* Dropdown Form Rows */}
                <div className="max-h-96 overflow-y-auto custom-scrollbar">
                  {pagination.currentData.map((bill, idx) => (
                    <DropdownFormRow
                      key={idx}
                      bill={bill}
                      docType={docType}
                      docTypeOptions={docTypeOptions}
                      companyCurrency={companyCurrency}
                      employees={employees}
                      onQuickEmployeeCashFlow={handleQuickEmployeeCashFlow}
                      onView={handleViewBill}
                      onEdit={handleEditBill}
                      onDelete={handleDeleteBill}
                      onPrint={() => {
                                const enhancedBill = {
                                  ...bill,
                                  rows: (bill.rows || []).map(row => {
                                    if (row.item) {
                                      const itemObj = items.find(it => it.id === row.item);
                                      if (itemObj) {
                                        return {
                                          ...row,
                                          description: itemObj.itemName || row.description || '',
                                          unit: itemObj.quantityMeasurement || row.unit || '',
                                          quantityMeasurement: itemObj.quantityMeasurement || row.quantityMeasurement || ''
                                        };
                                      }
                                    }
                                    return row;
                                  }),
                                  companyDetails: company,
                                  partyDetails: parties.find(p => p.id === bill.party) || {},
                                  docType: docType
                                };
                                handleMobileInvoiceView(enhancedBill);
                      }}
                      onPayment={handleAddPayment}
                      onReceipts={handleViewPaymentReceipts}
                      receiptsCount={getBillPayments(bill)?.length || 0}
                      receiptNumbers={getBillPayments(bill)?.map(payment => payment.receiptNumber) || []}
                      items={items}
                      index={idx}
                      isExpanded={expandedRows[idx] || false}
                      onToggle={toggleRowExpansion}
                    />
                  ))}
                </div>
              </div>
              
              {/* Mobile Card View with Collapsible Dropdown */}
              <div className="lg:hidden">
                  {pagination.currentData.map((bill, idx) => (
                    <PurchaseCard
                        employees={employees}
                        onQuickEmployeeCashFlow={handleQuickEmployeeCashFlow} 
                        key={bill.id} 
                        bill={bill}
                        docType={docType}
                        docTypeOptions={docTypeOptions}
                        companyCurrency={companyCurrency}
                        items={items}
                        index={idx}
                        isExpanded={expandedRows[idx] || false}
                        onToggle={toggleRowExpansion}
                        onView={handleViewBill}
                        onEdit={handleEditBill}
                        onDelete={handleDeleteBill}
                        onPayment={handleAddPayment}
                        onReceipts={handleViewPaymentReceipts}
                        receiptsCount={getBillPayments(bill)?.length || 0}
                        currentCompany={currentCompany}
                        receiptNumbers={getBillPayments(bill)?.map(payment => payment.receiptNumber) || []}
                        onPrint={() => {
                            // Enhance bill data with proper measurement units
                            const enhancedBill = {
                                ...bill,
                                rows: (bill.rows || []).map(row => {
                                    if (row.item) {
                                        const itemObj = items.find(it => it.id === row.item);
                                        if (itemObj) {
                                            return {
                                                ...row,
                                                description: itemObj.itemName || row.description || '',
                                                unit: itemObj.quantityMeasurement || row.unit || '',
                                                quantityMeasurement: itemObj.quantityMeasurement || row.quantityMeasurement || ''
                                            };
                                        }
                                    }
                                    return row;
                                }),
                                companyDetails: company,
                                partyDetails: parties.find(p => p.id === bill.party) || {},
                                docType: docType
                            };
                            handleMobileInvoiceView(enhancedBill);
                        }}
                    />
                  ))}
              </div>
              
              {/* Pagination Controls */}
              <div className="mt-6">
              <PaginationControls {...pagination} />
              </div>
              </div>
              )}
            </>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <p className="text-lg">Table and filters are hidden</p>
              <p className="text-sm">Click "Show Table & Filters" to view purchase bills</p>
            </div>
          )}
        </div> {/* Close Bills List Section */}
                
                {/* Modals */}
                {showInvoiceModal && invoiceBill && (
                  <PreviewModal section={MODAL_SECTIONS.PURCHASES}
                    isOpen={showInvoiceModal && invoiceBill}
                    onClose={() => setShowInvoiceModal(false)}
                    title={`${docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'} Preview`}
                    maxWidth={BILL_PREVIEW_MODAL_MAX_WIDTH}
                    maxHeight="max-h-[85vh] sm:max-h-[90vh]"
                    showPrintButton={true}
                    onPrint={handleInvoicePrint}
                    showPdfButton={true}
                    onPdf={handleInvoiceDownload}
                    extraActions={
                      <>
                        <PurchaseShareButton
                          onExportPDF={handleInvoiceSharePdf}
                          onExportImage={handleInvoiceShareImage}
                          disabled={!invoiceBill}
                        />
                      </>
                    }
                    showZoomControls={true}
                    zoom={invoiceZoom}
                    onZoomIn={() => setInvoiceZoom(z => Math.min(3, z + 0.1))}
                    onZoomOut={() => setInvoiceZoom(z => Math.max(0.5, z - 0.1))}
                    onZoomReset={() => setInvoiceZoom(getBillPreviewInitialZoom())}
                    zIndex="z-50"
                  >
                    <div 
                      className="w-full min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-gray-50 print:bg-white relative" 
                      onWheel={handleInvoiceWheel}
                    >
                      <div className="absolute top-2 left-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-md z-10 hidden sm:block">
                        Hold Ctrl + Scroll to zoom
                      </div>
                      <div className={`${BILL_PREVIEW_INNER_CLASS} px-1 sm:px-2 py-2 w-full`}>
                          <div 
                            ref={invoiceRef}
                            data-acctoo-bill-preview="true"
                            style={BILL_PREVIEW_SHELL_STYLE}
                            className={BILL_PREVIEW_SHELL_CLASS}
                          >
                          {(() => {
                            if (docType === 'purchaseReturn') {
                              return (
                                <PurchaseReturnTemplate
                                  billData={invoiceBill}
                                  companyDetails={{ ...company, gstinType: company.gstinType || '', logoUrl: company.logoUrl || '' }}
                                  partyDetails={parties.find(p => p.id === invoiceBill.party) || {}}
                                  bankDetails={{}}
                                />
                              );
                            } else if (docType === 'debitNote') {
                              return (
                                <DebitNoteTemplate
                                  billData={invoiceBill}
                                  companyDetails={{ ...company, gstinType: company.gstinType || '', logoUrl: company.logoUrl || '' }}
                                  partyDetails={parties.find(p => p.id === invoiceBill.party) || {}}
                                  bankDetails={{}}
                                />
                              );
                            } else if (docType === 'creditNote') {
                              return (
                                <CreditNoteTemplate
                                  billData={invoiceBill}
                                  companyDetails={{ ...company, gstinType: company.gstinType || '', logoUrl: company.logoUrl || '' }}
                                  partyDetails={parties.find(p => p.id === invoiceBill.party) || {}}
                                  bankDetails={{}}
                                />
                              );
                            } else if (docType === 'purchaseBill') {
                              // For purchase bills, select template based on party GST type
                              return getPartyGstType(invoiceBill.party) === 'Regular' ? (
                                <PurchaseBillTemplate
                                  billData={invoiceBill}
                                  companyDetails={{ ...company, gstinType: company.gstinType || '', logoUrl: company.logoUrl || '' }}
                                  partyDetails={parties.find(p => p.id === invoiceBill.party) || {}}
                                  payments={invoiceBill.payments || []}
                                />
                              ) : (
                                <PurchaseBillNonGstTemplate
                                  billData={invoiceBill}
                                  companyDetails={{ ...company, gstinType: company.gstinType || '', logoUrl: company.logoUrl || '' }}
                                  partyDetails={parties.find(p => p.id === invoiceBill.party) || {}}
                                  payments={invoiceBill.payments || []}
                                />
                              );
                            } else if (docType === 'purchaseOrder') {
                              return (
                                <PurchaseOrderTemplate
                                  billData={invoiceBill}
                                  companyDetails={{ ...company, gstinType: company.gstinType || '', logoUrl: company.logoUrl || '' }}
                                  partyDetails={parties.find(p => p.id === invoiceBill.party) || {}}
                                  payments={invoiceBill.payments || []}
                                />
                              );
                            } else {
                              return <div>Template not available</div>;
                            }
                          })()}
                            <style>{`${BILL_PREVIEW_SCREEN_CSS}
                              @media print {
                                html, body, #root, .print\\:w-[210mm] { width: 210mm !important; }
                                .print\\:min-h-[297mm] { min-height: 297mm !important; }
                                .print\\:max-w-none { max-width: none !important; }
                                .print\\:p-0 { padding: 0 !important; }
                                .print\\:m-0 { margin: 0 !important; }
                              }
                            `}</style>
                          </div>
                        </div>
                    </div>
                  </PreviewModal>
                )}

                {showViewModal && viewBill && (
                  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                    <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-lg p-4 sm:p-6 max-w-md w-full relative max-h-[92dvh] overflow-y-auto">
                      <div className="absolute top-2 right-2 flex items-center space-x-2">
                        <span className="text-xs text-gray-500 hidden sm:inline">Press ESC to close</span>
                        <button onClick={() => setShowViewModal(false)} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-500 hover:text-gray-800 text-xl touch-manipulation" aria-label="Close">&times;</button>
                      </div>
                      <h3 className="text-xl font-bold mb-4 text-center">{docType === 'purchaseBill' ? 'Purchase Bill' : docType === 'purchaseOrder' ? 'Purchase Order' : 'Bill'} Summary</h3>
                      <div className="mb-2 flex flex-col gap-1">
                        <div><span className="font-semibold">Number:</span> {viewBill.number}</div>
                        <div><span className="font-semibold">Date:</span> {viewBill.billDate}</div>
                        <div><span className="font-semibold">Party:</span> {(parties.find(p => p.id === viewBill.party)?.firmName) || viewBill.party || 'Unknown'}</div>
                        <div><span className="font-semibold">GST Type:</span> {getPartyGstType(viewBill.party)}</div>
                        <div><span className="font-semibold">Amount:</span> ₹{viewBill.amount}</div>
                        {viewBill.notes && <div><span className="font-semibold">Notes:</span> {viewBill.notes}</div>}
                      </div>
                      <div className="mt-4">
                        <div className="font-semibold mb-1">Items:</div>
                        <ul className="list-disc list-inside text-sm">
                          {(viewBill.rows || []).map((row, i) => {
                            const itemObj = (items || []).find(it => it.id === row.item) || {};
                            const itemName = itemObj.itemName || row.item || '?';
                            return (
                              <li key={i}>{itemName} (Qty: {row.qty}, Rate: ₹{row.rate})</li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

      {/* Payment Modal */}
      <FormModal section={MODAL_SECTIONS.PURCHASES}
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedBillForPayment(null);
          setRecordInEmployeeCashFlow(false);
          setSelectedEmployeeForCashFlow('');
        }}
        title={t('addPayment')}
        size="md"
        className="!w-[90vw] sm:!w-[60vw] !max-w-2xl"
        onSubmit={(e) => {
          e.preventDefault();
          handleSavePayment();
        }}
        submitText={t('savePayment')}
        cancelText={t('cancel')}
        submitVariant="success"
      >
            {/* Bill Information */}
            {selectedBillForPayment && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3">
            <h4 className="font-semibold text-blue-800 mb-1 text-xs sm:text-sm">Bill Information</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 sm:gap-2 text-xs">
              <div><span className="font-medium">Bill:</span> {selectedBillForPayment.number}</div>
                  <div><span className="font-medium">Party:</span> {getPartyName(selectedBillForPayment.party)}</div>
              <div><span className="font-medium">Total:</span> {formatCurrency(selectedBillForPayment.amount, companyCurrency)}</div>
              <div><span className="font-medium">Outstanding:</span> {formatCurrency(getBillOutstanding(selectedBillForPayment), companyCurrency)}</div>
                </div>
              </div>
            )}
            
        {/* Advance Information - Only show if advance is available */}
        {selectedBillForPayment && getPartyAdvance(selectedBillForPayment.party, payments) > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3">
            <h4 className="font-semibold text-green-800 mb-1 text-xs sm:text-sm">Advance Information</h4>
            <div className="text-xs space-y-1">
              <div><span className="font-medium">Available Advance:</span> {formatCurrency(getPartyAdvance(selectedBillForPayment.party, payments), companyCurrency)}</div>
                  <div className="text-xs text-green-600">
                Note: Excess payment will be allocated to other bills using FIFO method.
                  </div>
                </div>
              </div>
            )}
            
        <div className="space-y-2 sm:space-y-3">
          {/* Receipt Number and Date - Inline Edit */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 mb-2">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-xs font-medium text-gray-700">Payment Details</h4>
              <button
                type="button"
                onClick={() => {
                  setReceiptNumber('');
                  setPaymentDate('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors text-xs"
                title="Clear and regenerate"
              >
                Regenerate
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Receipt Number</label>
                <input
                  type="text"
                  value={receiptNumber}
                  onChange={(e) => setReceiptNumber(e.target.value)}
                  readOnly={!!selectedBillForPayment}
                  className={`w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs ${selectedBillForPayment ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="Auto-generated"
                  title={selectedBillForPayment ? 'Receipt number cannot be changed when adding payment' : ''}
                />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Date</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
                  required
                />
              </div>
            </div>
              </div>
              
          {/* Amount and Payment Mode in one line */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Amount</label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
                  placeholder={t('enterPaymentAmount')}
                  step="0.01"
                  min="0"
                required
              />
              </div>
              
              <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Payment Mode</label>
                <AppSelect
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  aria-label="Payment mode"
                  sheetTitle="Payment mode"
                  options={[
                    { value: 'Cash', label: t('cash') },
                    { value: 'Cheque', label: t('cheque') },
                    { value: 'Bank Transfer', label: t('bankTransfer') },
                    { value: 'UPI', label: 'UPI' },
                    { value: 'Card', label: t('card') },
                  ]}
                  triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-md shadow-sm p-1.5 text-xs`}
                />
            </div>
              </div>
              
          {/* Payment calculation info */}
          {selectedBillForPayment && paymentAmount && (
            <div className="bg-gray-50 p-2 rounded text-xs text-gray-600 space-y-1">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <div>Outstanding: {formatCurrency(getBillOutstanding(selectedBillForPayment), companyCurrency)}</div>
                <div>Advance: {formatCurrency(getPartyAdvance(selectedBillForPayment.party, payments), companyCurrency)}</div>
                <div className="font-medium text-blue-600 col-span-2 sm:col-span-1">
                  Payment needed: {formatCurrency(Math.max(0, getBillOutstanding(selectedBillForPayment) - getPartyAdvance(selectedBillForPayment.party, payments)), companyCurrency)}
                </div>
              </div>
            </div>
          )}
          
          {/* Reference and Notes in one line */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Reference</label>
                <input
                  type="text"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
                  placeholder="Cheque number, UPI reference, etc."
                />
              </div>
              
              <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
                rows="2"
                  placeholder={t('additionalNotes')}
                />
              </div>
            </div>
            
          {/* Employee Cash Flow Recording Section */}
          <div className="border-t pt-3 mt-3">
            <div className="flex items-center mb-3">
              <input
                type="checkbox"
                id="recordInEmployeeCashFlow"
                checked={recordInEmployeeCashFlow}
                onChange={(e) => {
                  setRecordInEmployeeCashFlow(e.target.checked);
                  if (!e.target.checked) {
                    setSelectedEmployeeForCashFlow('');
                  }
                }}
                className="mr-2"
              />
              <label htmlFor="recordInEmployeeCashFlow" className="text-xs sm:text-sm font-medium text-gray-700 cursor-pointer">
                Also record in employee cash flow
              </label>
            </div>
            
            {recordInEmployeeCashFlow && (
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Select Employee *
                </label>
                <AppSelect
                  value={selectedEmployeeForCashFlow}
                  onChange={(e) => setSelectedEmployeeForCashFlow(e.target.value)}
                  placeholder="Select Employee"
                  aria-label="Select employee"
                  sheetTitle="Select employee"
                  options={[
                    { value: '', label: 'Select Employee' },
                    ...(employees?.filter((emp) => emp.status === 'active') || []).map((employee) => {
                      const balance = employee.cashBalance || { cash: 0, upi: 0, total: 0 };
                      return {
                        value: employee.id,
                        label: `${employee.name} (${employee.employeeId || 'N/A'}) — Cash: ₹${balance.cash?.toLocaleString('en-IN') || 0} | UPI: ₹${balance.upi?.toLocaleString('en-IN') || 0}`,
                      };
                    }),
                  ]}
                  triggerClassName={`${APP_SELECT_FILTER_TRIGGER} text-xs px-2 py-1.5`}
                />
                {selectedEmployeeForCashFlow && (() => {
                  const selectedEmp = employees?.find(emp => emp.id === selectedEmployeeForCashFlow);
                  const balance = selectedEmp?.cashBalance || { cash: 0, upi: 0, total: 0 };
                  return (
                    <div className="mt-2 text-xs text-gray-600">
                      <div className="font-medium text-gray-700 mb-1">
                        Selected: {selectedEmp?.name} ({selectedEmp?.employeeId || 'N/A'})
                      </div>
                      <div className="space-y-0.5">
                        <div>Current Cash Balance: ₹{balance.cash?.toLocaleString('en-IN') || 0}</div>
                        <div>Current UPI Balance: ₹{balance.upi?.toLocaleString('en-IN') || 0}</div>
                        <div className="font-medium text-blue-600">Total Balance: ₹{balance.total?.toLocaleString('en-IN') || 0}</div>
                      </div>
                      <div className="mt-2 text-orange-600 font-medium">
                        Note: This payment will be recorded as <strong>outflow</strong> (money paid out)
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
            </div>
      </FormModal>

      {/* FIFO Payment Modal */}
      <FormModal section={MODAL_SECTIONS.PURCHASES}
        isOpen={showFIFOPaymentModal}
        onClose={() => {
          setShowFIFOPaymentModal(false);
          setSelectedPartyForFIFO('');
          setFifoPaymentAmount('');
          setFifoRecordInEmployeeCashFlow(false);
          setFifoSelectedEmployeeForCashFlow('');
        }}
        title="Add FIFO Payment"
        size="lg"
        className="!w-[90vw] sm:!w-[70vw] !max-w-4xl"
        onSubmit={(e) => {
          e.preventDefault();
          handleFIFOPayment();
        }}
        submitText="Save FIFO Payment"
        cancelText="Cancel"
        submitVariant="success"
      >
        <div className="space-y-4">
          {/* Party Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Party *</label>
            <AppSelect
              value={selectedPartyForFIFO}
              onChange={(e) => setSelectedPartyForFIFO(e.target.value)}
              placeholder="-- Select Party --"
              aria-label="Select party"
              sheetTitle="Select party"
              options={[
                { value: '', label: '-- Select Party --' },
                ...Array.from(new Set(bills.map((bill) => bill.party || bill.partyId).filter(Boolean)))
                  .filter((partyId) => {
                    const partyBills = bills.filter(
                      (bill) =>
                        (bill.partyId === partyId || bill.party === partyId) && (bill.outstanding || 0) > 0
                    );
                    return partyBills.length > 0;
                  })
                  .map((partyId) => {
                    const party = parties.find((p) => p.id === partyId);
                    const partyBills = bills.filter(
                      (bill) =>
                        (bill.partyId === partyId || bill.party === partyId) && (bill.outstanding || 0) > 0
                    );
                    const totalOutstanding = partyBills.reduce((sum, bill) => sum + (bill.outstanding || 0), 0);
                    return {
                      value: partyId,
                      label: `${party ? party.firmName || party.name : 'Unknown Party'} — Outstanding: ${formatCurrency(totalOutstanding, companyCurrency)}`,
                    };
                  }),
              ]}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-md shadow-sm p-2 text-sm`}
            />
          </div>

          {/* Pending Bills List (FIFO Order) */}
          {selectedPartyForFIFO && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <h4 className="font-semibold text-gray-800 mb-2 text-sm">Pending Bills (FIFO Order)</h4>
              <div className="max-h-48 overflow-y-auto">
                {bills
                  .filter(bill => 
                    (bill.partyId === selectedPartyForFIFO || bill.party === selectedPartyForFIFO) && 
                    (bill.outstanding || 0) > 0
                  )
                  .sort((a, b) => {
                    const dateA = new Date(a.billDate || a.date || 0);
                    const dateB = new Date(b.billDate || b.date || 0);
                    return dateA - dateB;
                  })
                  .map((bill, index) => (
                    <div key={bill.id} className="flex justify-between items-center py-2 px-2 border-b border-gray-200 last:border-b-0">
                      <div className="flex-1">
                        <div className="text-xs font-medium text-gray-700">
                          {index + 1}. {bill.number || bill.billNumber} - {formatDateToCompanyFormat(bill.billDate || bill.date, currentCompany)}
                        </div>
                        <div className="text-xs text-gray-500">
                          Total: {formatCurrency(bill.totalAmount || bill.amount || 0, companyCurrency)} | 
                          Paid: {formatCurrency(bill.totalPaid || 0, companyCurrency)}
                        </div>
                      </div>
                      <div className="text-xs font-semibold text-red-600 ml-2">
                        Outstanding: {formatCurrency(bill.outstanding || 0, companyCurrency)}
                      </div>
                    </div>
                  ))}
              </div>
              <div className="mt-2 pt-2 border-t border-gray-300">
                <div className="flex justify-between items-center text-sm">
                  <span className="font-medium text-gray-700">Total Outstanding:</span>
                  <span className="font-bold text-red-600">
                    {formatCurrency(
                      bills
                        .filter(bill => 
                          (bill.partyId === selectedPartyForFIFO || bill.party === selectedPartyForFIFO) && 
                          (bill.outstanding || 0) > 0
                        )
                        .reduce((sum, bill) => sum + (bill.outstanding || 0), 0),
                      companyCurrency
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Payment Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Receipt Number *</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={fifoReceiptNumber}
                  onChange={(e) => setFifoReceiptNumber(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                  required
                />
                <button
                  type="button"
                  onClick={async () => {
                    const receiptNum = await generateReceiptNumber();
                    setFifoReceiptNumber(receiptNum);
                  }}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-xs"
                >
                  Regenerate
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
              <input
                type="date"
                value={fifoPaymentDate}
                onChange={(e) => setFifoPaymentDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
              <input
                type="number"
                value={fifoPaymentAmount}
                onChange={(e) => setFifoPaymentAmount(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                placeholder="Enter payment amount"
                min="0"
                step="0.01"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode *</label>
              <AppSelect
                value={fifoPaymentMode}
                onChange={(e) => setFifoPaymentMode(e.target.value)}
                aria-label="Payment mode"
                sheetTitle="Payment mode"
                options={[
                  { value: 'Cash', label: 'Cash' },
                  { value: 'Bank Transfer', label: 'Bank Transfer' },
                  { value: 'UPI', label: 'UPI' },
                  { value: 'Cheque', label: 'Cheque' },
                  { value: 'Credit Card', label: 'Credit Card' },
                  { value: 'Debit Card', label: 'Debit Card' },
                  { value: 'Other', label: 'Other' },
                ]}
                triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-md shadow-sm p-2 text-sm`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
              <input
                type="text"
                value={fifoPaymentReference}
                onChange={(e) => setFifoPaymentReference(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                placeholder="Cheque number, UPI reference, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={fifoPaymentNotes}
                onChange={(e) => setFifoPaymentNotes(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                rows="2"
                placeholder="Additional notes"
              />
            </div>
          </div>

          {selectedPartyForFIFO && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="text-xs text-blue-800">
                <strong>Note:</strong> This payment will be allocated to outstanding bills using FIFO (First In, First Out) method - oldest bills first.
              </div>
            </div>
          )}
          
          {/* Employee Cash Flow Recording Section */}
          <div className="border-t pt-3 mt-3">
            <div className="flex items-center mb-3">
              <input
                type="checkbox"
                id="fifoRecordInEmployeeCashFlow"
                checked={fifoRecordInEmployeeCashFlow}
                onChange={(e) => {
                  setFifoRecordInEmployeeCashFlow(e.target.checked);
                  if (!e.target.checked) {
                    setFifoSelectedEmployeeForCashFlow('');
                  }
                }}
                className="mr-2"
              />
              <label htmlFor="fifoRecordInEmployeeCashFlow" className="text-xs sm:text-sm font-medium text-gray-700 cursor-pointer">
                Also record in employee cash flow
              </label>
            </div>
            
            {fifoRecordInEmployeeCashFlow && (
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Select Employee *
                </label>
                <AppSelect
                  value={fifoSelectedEmployeeForCashFlow}
                  onChange={(e) => setFifoSelectedEmployeeForCashFlow(e.target.value)}
                  placeholder="Select Employee"
                  aria-label="Select employee"
                  sheetTitle="Select employee"
                  options={[
                    { value: '', label: 'Select Employee' },
                    ...(employees?.filter((emp) => emp.status === 'active') || []).map((employee) => {
                      const balance = employee.cashBalance || { cash: 0, upi: 0, total: 0 };
                      return {
                        value: employee.id,
                        label: `${employee.name} (${employee.employeeId || 'N/A'}) — Cash: ₹${balance.cash?.toLocaleString('en-IN') || 0} | UPI: ₹${balance.upi?.toLocaleString('en-IN') || 0}`,
                      };
                    }),
                  ]}
                  triggerClassName={`${APP_SELECT_FILTER_TRIGGER} text-xs px-2 py-1.5`}
                />
                {fifoSelectedEmployeeForCashFlow && (() => {
                  const selectedEmp = employees?.find(emp => emp.id === fifoSelectedEmployeeForCashFlow);
                  const balance = selectedEmp?.cashBalance || { cash: 0, upi: 0, total: 0 };
                  return (
                    <div className="mt-2 text-xs text-gray-600">
                      <div className="font-medium text-gray-700 mb-1">
                        Selected: {selectedEmp?.name} ({selectedEmp?.employeeId || 'N/A'})
                      </div>
                      <div className="space-y-0.5">
                        <div>Current Cash Balance: ₹{balance.cash?.toLocaleString('en-IN') || 0}</div>
                        <div>Current UPI Balance: ₹{balance.upi?.toLocaleString('en-IN') || 0}</div>
                        <div className="font-medium text-blue-600">Total Balance: ₹{balance.total?.toLocaleString('en-IN') || 0}</div>
                      </div>
                      <div className="mt-2 text-orange-600 font-medium">
                        Note: This payment will be recorded as <strong>outflow</strong> (money paid out)
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </FormModal>

      {/* Payment Details Modal */}
      {selectedBillForReceipts && (
        <StandardModal section={MODAL_SECTIONS.PURCHASES}
          isOpen={showPaymentDetailsModal}
          onClose={() => setShowPaymentDetailsModal(false)}
          title={t('paymentReceipts')}
          size="2xl"
          className="!w-[90vw] sm:!w-[60vw] !max-w-4xl"
        >
        <div className="mb-2">
          <p className="text-xs sm:text-sm text-gray-600 break-words">
            <span className="hidden sm:inline">{docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}: {selectedBillForReceipts?.number} | Party: {getPartyName(selectedBillForReceipts?.party)} | Total Amount: ₹{(selectedBillForReceipts?.amount || 0).toLocaleString('en-IN')}</span>
            <span className="sm:hidden">{selectedBillForReceipts?.number} | ₹{(selectedBillForReceipts?.amount || 0).toLocaleString('en-IN')}</span>
          </p>
            </div>
            
            {/* Payment Summary */}
        <div className="bg-gray-50 p-2 sm:p-3 rounded-lg mb-2 sm:mb-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 text-xs">
            <div className="text-center sm:text-left">
              <span className="font-medium text-gray-700">Total Bill:</span>
              <span className="ml-1 text-xs sm:text-sm font-semibold">₹{(selectedBillForReceipts?.amount || 0).toLocaleString('en-IN')}</span>
                </div>
            <div className="text-center sm:text-left">
              <span className="font-medium text-gray-700">Paid:</span>
              <span className="ml-1 text-xs sm:text-sm font-semibold text-green-600">₹{(selectedBillForReceipts?.paid || 0).toLocaleString('en-IN')}</span>
                </div>
            <div className="text-center sm:text-left">
                  <span className="font-medium text-gray-700">Outstanding:</span>
              <span className="ml-1 text-xs sm:text-sm font-semibold text-red-600">₹{(selectedBillForReceipts?.outstanding || 0).toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
            
        <div className="overflow-x-auto max-h-64">
          {/* Desktop Table View */}
          <div className="hidden sm:block">
            <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt No</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {(getBillPayments(selectedBillForReceipts) || []).map((payment) => {
                  const allocation = payment.allocations?.find(a => a.billId === selectedBillForReceipts?.id && a.billType === 'purchase');
                    return (
                      <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-2 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                          {payment.receiptNumber}
                        </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                          ₹{parseFloat(allocation?.allocatedAmount || 0).toLocaleString('en-IN')}
                        </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                          {payment.paymentDate}
                        </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                          {payment.paymentMode}
                        </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                        <StandardButton
                          variant="info"
                          size="sm"
                              onClick={() => {
                                setSelectedPaymentForReceipt(payment);
                                setShowReceiptModal(true);
                                setShowPaymentDetailsModal(false);
                              }}
                            >
                              Preview
                        </StandardButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          
          {/* Mobile Card View */}
          <div className="sm:hidden space-y-2">
            {(getBillPayments(selectedBillForReceipts) || []).map((payment) => {
              const allocation = payment.allocations?.find(a => a.billId === selectedBillForReceipts?.id && a.billType === 'purchase');
              return (
                <div key={payment.id} className="bg-white p-2 rounded border border-gray-200">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900 truncate">{payment.receiptNumber}</div>
                      <div className="text-xs text-gray-500">₹{parseFloat(allocation?.allocatedAmount || 0).toLocaleString('en-IN')}</div>
                      <div className="text-xs text-gray-500">{payment.paymentDate} • {payment.paymentMode}</div>
          </div>
                    <StandardButton
                      variant="info"
                      size="sm"
                      onClick={() => {
                        setSelectedPaymentForReceipt(payment);
                        setShowReceiptModal(true);
                        setShowPaymentDetailsModal(false);
                      }}
                    >
                      Preview
                    </StandardButton>
        </div>
                </div>
              );
            })}
          </div>
        </div>
        </StandardModal>
      )}

      {/* Payment Receipt Preview Modal */}
      <PreviewModal section={MODAL_SECTIONS.PURCHASES}
        isOpen={showReceiptModal && selectedPaymentForReceipt}
        onClose={() => setShowReceiptModal(false)}
        title={t('paymentReceiptPreview')}
        maxWidth="max-w-[90vw] sm:max-w-[60vw] lg:max-w-2xl"
        maxHeight="max-h-[70vh] sm:max-h-[80vh]"
        extraActions={
          <ShareButton
            onExportPDF={async () => {
              try {
                const jsPDFLocal = window.jsPDF || (window.jspdf && window.jspdf.jsPDF);
                if (!jsPDFLocal || !selectedPaymentForReceipt) return;
                const doc = new jsPDFLocal();
                let y = 15;
                doc.setFontSize(20); doc.setFont('helvetica', 'bold'); doc.setTextColor(31,41,55);
                doc.text('PAYMENT RECEIPT', 105, y, { align: 'center' }); y += 12;
                doc.setFontSize(12); doc.setTextColor(59,130,246); doc.setFont('helvetica', 'bold');
                doc.text(`Receipt No: ${selectedPaymentForReceipt.receiptNumber}`, 105, y, { align: 'center' }); y += 12;
                doc.setFontSize(14); doc.setTextColor(0,0,0); doc.setFont('helvetica', 'bold');
                doc.text(company?.firmName || company?.name || 'Company Name', 105, y, { align: 'center' }); y += 10;
                doc.setDrawColor(209,213,219); doc.line(25, y, 185, y); y += 10;
                doc.setFontSize(12); doc.setFont('helvetica', 'normal');
                const addKV = (k, v) => { doc.text(`${k}: ${v}`, 25, y); y += 8; };
                addKV('Date', selectedPaymentForReceipt.paymentDate || new Date().toISOString().split('T')[0]);
                addKV('Received From', selectedPaymentForReceipt.partyName || '');
                addKV('Amount', `₹${parseFloat(selectedPaymentForReceipt.totalAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
                addKV('Payment Mode', selectedPaymentForReceipt.paymentMode || 'Cash');
                addKV('Reference', selectedPaymentForReceipt.reference || '-');
                if (selectedPaymentForReceipt.notes) addKV('Notes', selectedPaymentForReceipt.notes);
                const blob = doc.output('blob');
                const file = new File([blob], `receipt-${selectedPaymentForReceipt.receiptNumber}.pdf`, { type: 'application/pdf' });
                if (navigator.canShare && navigator.canShare({ files: [file] })) {
                  await navigator.share({ files: [file], title: `Receipt ${selectedPaymentForReceipt.receiptNumber}` });
                } else {
                  doc.save(`receipt-${selectedPaymentForReceipt.receiptNumber}.pdf`);
                }
              } catch (e) { console.error('Share receipt PDF failed:', e); }
            }}
            onExportImage={async () => {
              try {
                const el = document.querySelector('.receipt-container');
                if (!el || !selectedPaymentForReceipt) return;
                const html2canvas = await import('html2canvas');
                const canvas = await html2canvas.default(el, { scale: 2, useCORS: true, allowTaint: false, backgroundColor: '#ffffff' });
                const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
                const file = new File([blob], `receipt-${selectedPaymentForReceipt.receiptNumber}.png`, { type: 'image/png' });
                if (navigator.canShare && navigator.canShare({ files: [file] })) {
                  await navigator.share({ files: [file], title: `Receipt ${selectedPaymentForReceipt.receiptNumber}` });
                } else {
                  const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `receipt-${selectedPaymentForReceipt.receiptNumber}.png`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
                }
              } catch (e) { console.error('Share receipt image failed:', e); }
            }}
          />
        }
        showBackButton={true}
        onBack={() => {
          setShowReceiptModal(false);
          setShowPaymentDetailsModal(true);
        }}
        showPrintButton={true}
        onPrint={printReceipt}
        showPdfButton={true}
        onPdf={generatePDFReceipt}
        zIndex="z-50"
      >
        <div ref={receiptRef} className="flex justify-center">
          <ReceiptTemplate
            receipt={{
              totalAmount: selectedPaymentForReceipt?.totalAmount,
              date: selectedPaymentForReceipt?.paymentDate,
              mode: selectedPaymentForReceipt?.paymentMode,
              reference: selectedPaymentForReceipt?.reference,
              notes: selectedPaymentForReceipt?.notes,
              billReference: selectedPaymentForReceipt?.billId,
              allocations: selectedPaymentForReceipt?.allocations,
              advanceAllocations: selectedPaymentForReceipt?.advanceAllocations,
              fifoAllocationUsed: selectedPaymentForReceipt?.fifoAllocationUsed,
              remainingAmount: selectedPaymentForReceipt?.remainingAmount
            }}
            bill={{
              number: selectedPaymentForReceipt?.billNumber,
              invoiceNumber: selectedPaymentForReceipt?.billNumber,
              billNumber: selectedPaymentForReceipt?.billNumber
            }}
            company={company}
            party={{ firmName: selectedPaymentForReceipt?.partyName }}
            receiptNumber={selectedPaymentForReceipt?.receiptNumber}
            fifoAllocation={selectedPaymentForReceipt?.allocations}
            isPurchase={true}
          />
        </div>
      </PreviewModal>


      {/* ADD ITEM Modal */}
      <FormModal section={MODAL_SECTIONS.PURCHASES}
        isOpen={showAddItemModal}
        onClose={() => { setShowAddItemModal(false); clearAddItemForm(); }}
        title={t('addNewItem')}
        size="2xl"
        onSubmit={(e) => {
          e.preventDefault();
          handleAddItem();
        }}
        submitText={t('addItem')}
        cancelText={t('cancel')}
        submitVariant="success"
      >
        {addItemMessage && (
          <div className={`mb-4 p-3 rounded ${addItemMessage.includes('successfully') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {addItemMessage}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('itemName')} *</label>
            <input
              type="text"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('enterItemName')}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('quantityMeasurement')}</label>
            <input
              type="text"
              value={newItemMeasurement}
              onChange={(e) => setNewItemMeasurement(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., kg, pcs, liters"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('salePricePerUnit')}</label>
            <input
              type="number"
              value={newItemRate}
              onChange={(e) => setNewItemRate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="0.00"
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('purchasePricePerUnit')}</label>
            <input
              type="number"
              value={newItemPurchasePrice}
              onChange={(e) => setNewItemPurchasePrice(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="0.00"
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">HSN Code</label>
            <input
              type="text"
              value={newItemHsnCode}
              onChange={(e) => setNewItemHsnCode(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., 48025510"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">GST %</label>
            <input
              type="number"
              value={newItemGstPercentage}
              onChange={(e) => setNewItemGstPercentage(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="0"
              min="0"
              max="100"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Opening Stock</label>
            <input
              type="number"
              value={newItemCurrentStock}
              onChange={(e) => setNewItemCurrentStock(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="0"
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Custom Unit</label>
            <input
              type="text"
              value={newItemCustomUnit}
              onChange={(e) => setNewItemCustomUnit(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., box, set, pair"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('itemDescription')}</label>
          <textarea
            value={newItemDescription}
            onChange={(e) => setNewItemDescription(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            rows="3"
            placeholder={t('enterItemDescription')}
          />
        </div>
      </FormModal>

      {/* ADD PARTY Modal */}
      <FormModal section={MODAL_SECTIONS.PURCHASES}
        isOpen={showAddPartyModal}
        onClose={() => { setShowAddPartyModal(false); clearAddPartyForm(); }}
        title="Add New Party"
        size="2xl"
        onSubmit={(e) => {
          e.preventDefault();
          handleAddParty();
        }}
        submitText="Add Party"
        cancelText="Cancel"
        submitVariant="success"
      >
        {addPartyMessage && (
          <div className={`mb-4 p-3 rounded ${addPartyMessage.includes('successfully') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {addPartyMessage}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Party Name *</label>
            <input
              type="text"
              value={newPartyName}
              onChange={(e) => setNewPartyName(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter party name"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">GST Type</label>
            <AppSelect
              value={newPartyGstType}
              onChange={(e) => setNewPartyGstType(e.target.value)}
              aria-label="GST type"
              sheetTitle="GST type"
              options={[
                { value: 'Regular', label: 'Regular' },
                { value: 'Composition', label: 'Composition' },
                { value: 'Unregistered', label: 'Unregistered' },
              ]}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-md p-2`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              GSTIN {newPartyGstType === 'Regular' && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={newPartyGstin}
              onChange={(e) => setNewPartyGstin(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder={newPartyGstType === 'Regular' ? 'Enter GSTIN (required for Regular)' : 'Enter GSTIN (if applicable)'}
              required={newPartyGstType === 'Regular'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="text"
              value={newPartyPhone}
              onChange={(e) => setNewPartyPhone(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter phone number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={newPartyEmail}
              onChange={(e) => setNewPartyEmail(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter email address"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <textarea
            value={newPartyAddress}
            onChange={(e) => setNewPartyAddress(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            rows="3"
            placeholder="Enter party address"
          />
        </div>
        
        {/* Advanced Fields Toggle */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowAdvancedFields(!showAdvancedFields)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm"
          >
            <svg className={`w-4 h-4 transition-transform ${showAdvancedFields ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {showAdvancedFields ? 'Hide' : 'Show'} Advanced Fields (Opening Balance, Credit Limit)
          </button>
        </div>
        
        {/* Advanced Fields */}
        {showAdvancedFields && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Advanced Settings (Optional)</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Opening Balance</label>
                <input
                  type="number"
                  value={newPartyOpeningBalance}
                  onChange={(e) => setNewPartyOpeningBalance(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
                <p className="text-xs text-gray-500 mt-1">+ve: Supplier owes you, -ve: You owe supplier</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credit Limit (₹)</label>
                <input
                  type="number"
                  value={newPartyCreditLimit}
                  onChange={(e) => setNewPartyCreditLimit(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum credit allowed</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credit Period (Days)</label>
                <input
                  type="number"
                  value={newPartyCreditPeriod}
                  onChange={(e) => setNewPartyCreditPeriod(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">Payment terms in days</p>
              </div>
            </div>
          </div>
        )}
      </FormModal>

      {/* Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[92dvh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Add Note</h3>
              <button
                onClick={() => setShowNotesModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-4">
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                className="w-full h-64 border border-gray-300 rounded-md shadow-sm p-3 resize-none"
                placeholder="Enter additional notes..."
                autoFocus
              />
              <div className="mt-2 text-right text-sm text-gray-500">
                {notes.length} characters
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                Save Note
              </button>
            </div>
          </div>
        </div>
      )}

      {/* E-way Bill Modal */}
      {showEwayBillModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[92dvh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Add E-way Bill Details</h3>
              <button
                onClick={() => setShowEwayBillModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E-way Bill No</label>
                  <input
                    type="text"
                    value={customFields.ewayBillNo}
                    onChange={e => setCustomFields(f => ({ ...f, ewayBillNo: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-3"
                    placeholder="Enter E-way Bill number"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E-way Bill Date</label>
                  <input
                    type="date"
                    value={customFields.ewayDate}
                    onChange={e => setCustomFields(f => ({ ...f, ewayDate: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-3"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">QR Code URL</label>
                  <input
                    type="text"
                    value={customFields.ewayQr}
                    onChange={e => setCustomFields(f => ({ ...f, ewayQr: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-3"
                    placeholder="Enter QR code URL"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setShowEwayBillModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowEwayBillModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
              >
                Save E-way Bill
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
    );
}

export default PurchasesDev; 